"use client";
import React, { useState, useEffect } from "react";
import { Button } from "./ui/Button";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import CalculatorModal from "./CalculatorModal";
import { getServiceCodes } from "../apis/getServiceCodes";
import { getSpendingPlans, getSpendingPlanLines } from "../apis/getSpendingPlans";
import addServiceTypeName from "../apis/addServiceTypeName";
import { toggleServiceEvv } from "../apis/toggleServiceEvv";
import { getExportableServiceIds } from "../apis/getExportableServiceIds";
import { getBusinessMultipliers } from "../apis/getBusinessMultipliers";
import { addSpendingPlanLine } from "../apis/addSpendingPlansLine";
import { editSpendingPlanLine } from "../apis/editSpendingPlanLine";
import { deleteSpendingPlanLine } from "../apis/deleteSpendingPlanLine";
import { addSpendingPlan } from "../apis/addSpendingPlan";
import { editSpendingPlan } from "../apis/editSpendingPlan";
import { getAuthorizedServices } from "../apis/getAuthorizedServices";
import { getPatientAssignedWorkers } from "../apis/getPatientAssignedWorkers";
import { getPayrollTransactionsByServiceOrder } from "../apis/getPayrollTransactionsByServiceOrder";
import getBusinessList from "../apis/getBusinessList";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import addPatient from "../apis/addPatient";
import login from "../apis/login";
import saveUser from "../apis/saveUser";
import savePatientContact from "../apis/savePatientContact";
import businessLogin from "../apis/businessLogin";
import { getConfigurableList } from "../apis/getConfigurableList";
import { getBusinessServiceTypes, getBurdenRates, addPos, addWorkerAssignment, describeFmsError } from "../apis/fmsPosApi";
import { isEmbeddedInAnkotaParent, isLocalhost } from "../apis/variables";
import { sessionIds } from "../apis/session";
import ProviderPicker, { loadAssignableWorkers } from "./ProviderPicker";
import {
    PROVIDER_TYPE_DSW,
    PROVIDER_TYPE_VENDOR,
    isSdpEntryEnabled,
    isVendorRow,
    getLocalProviderType,
    inferProviderTypeFromPayroll,
    markSdpEnteredLines,
    rememberLineProviderTypes,
    getRememberedLineProviderType,
    isSdpEnteredLine,
    parseCustomBurdenPercent,
    formatCustomBurden,
    buildActivationRequests,
    isRowEvv,
    isLiveIn,
    canRowBeLiveIn,
    isRowLinked,
    getRowTotal,
    sumRows,
    getDefaultPlanEndDate,
    getRowFieldOrder,
} from "./sdp";
import toast, { Toaster } from 'react-hot-toast';
//const NUMERIC_FIELDS = new Set(["basePay", "pricingUnit", "frequency", "annualCost"]);

// Predefined payment model options
const PAYMENT_MODEL_OPTIONS = [
    "Bill Payer",
    "Co-Employer",
    "Sole-Employer",
];

// Helper function to format date for API without timezone shift
// The New Plan form's Certified Budget field is hidden for now; set to true to bring it back
const SHOW_CERTIFIED_BUDGET_ON_NEW_PLAN = false;

const formatDateForApi = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function ServiceTableV2({ businessId, patientId }) {
   // const sections = initialSections;
   console.log('businessId in ServiceTable:', businessId);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [paymentModelMultipliers, setPaymentModelMultipliers] = useState({
        "Bill Payer": 1,
        "Co-Employer": 1.20,
        "Sole-Employer": 1.11,
    });
    // SDP: the company's burden rates (get-burden-rates), offered as a worker's Payment Model
    const [burdenRates, setBurdenRates] = useState([]);
    const [editingCell, setEditingCell] = useState({ sectionKey: null, rowId: null, field: null });
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("current"); // Add tab state
    // Add state for service codes
    const [serviceCodeOptions, setServiceCodeOptions] = useState([]);
    const [evvServiceIds, setEvvServiceIds] = useState([]); // List of EVV-enabled service IDs
    const [spendingPlans, setSpendingPlans] = useState([]);
   // const [planLines, setPlanLines] = useState({});
    const [apiSections, setApiSections] = useState({}); // State for interactive API data
    const [modifiedRows, setModifiedRows] = useState(new Set()); // Track modified rows
    const [savingRows, setSavingRows] = useState(new Set()); // Track rows being saved
    const [savingAuthWorkers, setSavingAuthWorkers] = useState(new Set()); // Guards the auth-worker ADD button against double-submission
    const [payrollRates, setPayrollRates] = useState({}); // Base pay rates from payroll, keyed by normalized worker name
    const [companyProviders, setCompanyProviders] = useState(null); // The company's workers/vendors, each with its profile's Worker/Vendor type
    const [payrollRatesLoadedForPlan, setPayrollRatesLoadedForPlan] = useState(null);
    const [approvedBudgets, setApprovedBudgets] = useState({}); // Track approved budgets per service code subtotal
    const [editingPlanDates, setEditingPlanDates] = useState(null); // Track which plan dates are being edited
    const [planDateForms, setPlanDateForms] = useState({}); // Track plan date form values
    const [editingPlanName, setEditingPlanName] = useState(null); // Track which plan name is being edited
    const [planNameForms, setPlanNameForms] = useState({}); // Store name form data for each plan
    const [savingPlanName, setSavingPlanName] = useState(null); // Track which plan name is being saved
    const [savingPlanDates, setSavingPlanDates] = useState(null); // Track which plan dates are being saved
    
    // Service type name management states
    const [customServiceTypes, setCustomServiceTypes] = useState({}); // Track custom service types with their IDs
    const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
    const [serviceTypeForm, setServiceTypeForm] = useState({
        originalName: '',
        newName: '',
        operation: 'duplicate', // 'duplicate' or 'edit'
        serviceId: null,
        serviceCode: null, // Store service code for API payload lookup
        planId: null,
        sectionKey: null,
        rowId: null,
        useAddAPI: false // Flag to determine if we should use add API even when UI shows edit
    });
    const [isSavingServiceType, setIsSavingServiceType] = useState(false);
    
    // Guest mode and authentication states
    const isGuestMode = !patientId || patientId === '0';
    const isEmbedded = window.self !== window.top;
    // V2 is reached via ?token=... or ?patientId=...&businessId=... (see App.js); "Add New Worker" isn't needed in that flow
    const hasUrlParams = (() => {
        if (sessionIds) return true;
        const p = new URLSearchParams(window.location.search);
        return !!((p.get('patientId') || p.get('PatientId') || p.get('patiendId')) &&
            (p.get('businessId') || p.get('businessdId') || p.get('departmentId') || p.get('departmentid')));
    })();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('register'); // 'login' or 'register' - default to register for new users
    const [authForm, setAuthForm] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '', // Add date of birth for prospect registration
        // Emergency contact fields to be sent to save-patient-contact
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactMobilePhone: '',
        contactAddress1: '',
        contactCity: '',
        contactState: '',
        contactPostalCode: '',
        contactRelationshipId: 11,
        contactNote: 'Primary emergency contact'
    });
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isBusinessLoginComplete, setIsBusinessLoginComplete] = useState(false); // Track business login completion
    
    // Plan locking removed — no longer used
    // const [hasBeenPrinted, setHasBeenPrinted] = useState(false);
    
    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState({
        title: '',
        message: '',
        onConfirm: () => {},
        confirmText: 'Confirm',
        cancelText: 'Cancel'
    });
    
    // Local storage keys for guest mode
    const GUEST_PLANS_KEY = 'guest_spending_plans';
    const GUEST_SECTIONS_KEY = 'guest_api_sections';
    const GUEST_BUDGETS_KEY = 'guest_approved_budgets';
    
    // Helper functions for storage. For guest keys we intentionally DO NOT persist to storage
    // so guest drafts remain only in-memory and are cleared on refresh/new tab.
    const saveToLocalStorage = (key, data) => {
        try {
            // Do not persist guest-scoped keys to any storage (ephemeral only)
            if (typeof key === 'string' && key.toLowerCase().includes('guest')) {
                return;
            }
            localStorage.setItem(key, JSON.stringify(data));
        } catch (error) {
            console.error('Error saving to storage:', error);
        }
    };

    const getFromLocalStorage = (key, defaultValue = []) => {
        try {
            // Guest keys should not be read from persistent storage
            if (typeof key === 'string' && key.toLowerCase().includes('guest')) {
                return defaultValue;
            }
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (error) {
            console.error('Error reading from storage:', error);
            return defaultValue;
        }
    };
    
    // Plan management states
    const [showAddPlanModal, setShowAddPlanModal] = useState(false);
    const [newPlanForm, setNewPlanForm] = useState({
        planName: '',
        startDate: '',
        endDate: '',
        totalBudget: '',
        description: ''
    });
    
    // Accordion states
    const [expandedPlans, setExpandedPlans] = useState(new Set([0])); // First plan expanded by default
    
    // Loading state for add plan
    const [isAddingPlan, setIsAddingPlan] = useState(false);
    
    // Loading state for data fetching
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Authorization data
    const [authorizedServices, setAuthorizedServices] = useState([]);
    const [assignedWorkers, setAssignedWorkers] = useState([]);
    const [authAllocations, setAuthAllocations] = useState({});
    const [editingAuthCell, setEditingAuthCell] = useState(null); // { serviceTypeId, assignmentId }
    const [editingAuthDraft, setEditingAuthDraft] = useState('');

    // SDP spending-plan entry (per-company flag): the plan is entered here as provider build-up lines
    // and Activate writes the POS authorizations + assignments. Off = the existing POS-first screen.
    const [sdpEntry, setSdpEntry] = useState(false);

    // SDP: load the company's burden rates once the flag is known
    useEffect(() => {
        if (!sdpEntry || isGuestMode) return;
        getBurdenRates().then(setBurdenRates).catch(error => console.error('Error loading burden rates:', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdpEntry]);

    const roundCents = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

    // Burden multiplier for a payment model: in SDP from the company's burden rates
    // (Rate = Base Pay x (1 + burden %), as the .NET FMS popup does); otherwise the business multipliers
    const getModelMultiplier = (model) => {
        if (sdpEntry) {
            const burden = burdenRates.find(b => String(b.Name || '').trim().toLowerCase() === String(model || '').trim().toLowerCase());
            if (burden) return 1 + Number(burden.Percent || 0) / 100;
            const customPercent = parseCustomBurdenPercent(model);
            if (customPercent !== null) return 1 + customPercent / 100;
        }
        return paymentModelMultipliers[model] || 1;
    };

    // SDP worker: is the payment model a real burden (from the list or a custom %)? Bill Payer
    // (the default for new lines) is for suppliers, so for a worker it means none chosen yet
    const isWorkerBurdenChosen = (model) =>
        burdenRates.some(b => String(b.Name || '').trim().toLowerCase() === String(model || '').trim().toLowerCase())
        || parseCustomBurdenPercent(model) !== null;
    // SDP: custom burden % being typed (applied on Enter / leaving the box)
    const [customBurdenDraft, setCustomBurdenDraft] = useState('');
    const [editingCertifiedBudget, setEditingCertifiedBudget] = useState(null); // planId being edited
    const [certifiedBudgetDraft, setCertifiedBudgetDraft] = useState('');
    // SDP: Total Annual Cost being typed (applied on Enter / leaving the cell)
    const [totalDraft, setTotalDraft] = useState('');
    const [savingCertifiedBudget, setSavingCertifiedBudget] = useState(null);
    const [activatingPlan, setActivatingPlan] = useState(null);
        // Helpers: EVV detection and Live-In check
        const isOriginalServiceEVV = (serviceCode) => {
            try {
                const scStr = String(serviceCode ?? '').trim();

                // 1) Direct id/code/name equality to find the service
                let matchOpt = serviceCodeOptions.find(o => {
                    const candidates = [
                        o?.ServiceTypeID, o?.ServiceTypeId, o?.ID, o?.Id,
                        o?.Name, o?.Code, o?.ServiceCode
                    ].filter(v => v !== undefined && v !== null);
                    return candidates.some(v => String(v).trim() === scStr);
                });

                // 2) Fallback: Name starts with the numeric code (e.g., "315 (EVV) - ...")
                if (!matchOpt) {
                    const escaped = scStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const startsWithCode = new RegExp('^\\s*' + escaped + '\\b');
                    matchOpt = serviceCodeOptions.find(o => typeof o?.Name === 'string' && startsWithCode.test(o.Name));
                }

                // 3) If still not found, try loose compare on ServiceTypeID
                if (!matchOpt) {
                    matchOpt = serviceCodeOptions.find(o => String(o?.ServiceTypeID) === scStr);
                }

                // Check if the service ID is in the EVV-enabled list from API
                if (matchOpt) {
                    const serviceId = matchOpt.ServiceID || matchOpt.ServiceId || matchOpt.ID || matchOpt.Id;
                    // evvServiceIds might be array of objects {serviceId: xxx} or just IDs
                    const isEvv = evvServiceIds.some(evvItem => {
                        const evvId = typeof evvItem === 'object' ? (evvItem.serviceId || evvItem.ServiceId) : evvItem;
                        return String(evvId) === String(serviceId) || evvId === serviceId;
                    });
                    console.log('EVV Check:', {
                        serviceCode: scStr,
                        serviceId,
                        evvServiceIds,
                        isEvv
                    });
                    return isEvv;
                }

                return false;
            } catch (_) { return false; }
        };

        const isLiveInUnchecked = (liveInVal) => {
            if (!liveInVal) return true;
            const s = String(liveInVal).toLowerCase();
            return s === 'false' || s === '0' || s === 'no' || s === 'n';
        };

    // --- Auth row editable data (mirrors apiSections row format) ---
    const [authRowData, setAuthRowData] = useState({});

    const DEFAULT_AUTH_ROW = { ippGoal: '', basePay: 0, paymentModel: 'Bill Payer', pricingUnit: 0, frequency: 1, liveIn: false, annualCost: 0 };

    function getAuthRowObj(rowKey) {
        return authRowData[rowKey] || { ...DEFAULT_AUTH_ROW };
    }

    function handleAuthRowChange(rowKey, field, value) {
        setAuthRowData(prev => {
            const current = prev[rowKey] || { ...DEFAULT_AUTH_ROW };
            const updatedRow = { ...current, [field]: value };
            if (field === 'paymentModel') {
                const multiplier = paymentModelMultipliers[value] || 1;
                updatedRow.pricingUnit = updatedRow.basePay * multiplier;
                updatedRow.annualCost = updatedRow.pricingUnit * (updatedRow.frequency || 0);
            }
            if (field === 'basePay') {
                const multiplier = paymentModelMultipliers[updatedRow.paymentModel || 'Bill Payer'] || 1;
                updatedRow.pricingUnit = Number(value) * multiplier;
                updatedRow.annualCost = updatedRow.pricingUnit * (updatedRow.frequency || 0);
            }
            if (field === 'frequency') {
                if (value === '') { updatedRow.frequency = ''; updatedRow.annualCost = 0; }
                else { const freq = Number(value) || 0; updatedRow.frequency = freq; updatedRow.annualCost = freq * (Number(updatedRow.pricingUnit) || 0); }
            }
            return { ...prev, [rowKey]: updatedRow };
        });
    }

    // --- Auth helpers ---
    const AUTH_SECTION_MAP = {
        'livingarrangements': [310, 330],
        'employmentandcommunityparticipation': [331, 355],
        'healthandsafety': [356, 399],
    };

    function getCodeFromServiceType(name) {
        const m = String(name || '').match(/^\d+/);
        return m ? parseInt(m[0]) : null;
    }

    // Payroll's WorkerName comes reversed from ours (e.g. "Daniel DSP" vs our "DSP, Daniel"),
    // so match by a sorted set of name words rather than exact string equality.
    function normalizeNameForMatch(name) {
        return (name || '')
            .split(/[,\s]+/)
            .map(s => s.trim().toLowerCase())
            .filter(Boolean)
            .sort()
            .join(' ');
    }

    // Match by leading numeric service code (310/320/331/...) rather than exact string —
    // payroll service-order names can be more specific than our authorization's name
    // (e.g. "331 (EVV) - Community Integration Supports" or "320 Live-in" vs our generic
    // "331 - Community Integration Supports"), so an exact-string match silently misses them.
    function getPayrollServiceKey(serviceName) {
        const code = getCodeFromServiceType(serviceName);
        return code !== null ? String(code) : (serviceName || '').trim().toLowerCase();
    }

    function getPayrollRateKey(provider, serviceName) {
        return `${normalizeNameForMatch(provider)}|${getPayrollServiceKey(serviceName)}`;
    }

    function getAuthsForSection(sectionKey) {
        const [lo, hi] = AUTH_SECTION_MAP[sectionKey] || [0, 0];
        return authorizedServices.filter(svc => {
            const code = getCodeFromServiceType(svc.ServiceTypeName);
            return code >= lo && code <= hi;
        });
    }

    function getAuthWorkerEntry(serviceTypeId, assignmentId) {
        return (authAllocations[serviceTypeId]?.workers || {})[assignmentId] || { amount: 0, mode: 'year' };
    }

    function setAuthWorkerEntry(serviceTypeId, assignmentId, patch) {
        setAuthAllocations(prev => {
            const updated = {
                ...prev,
                [serviceTypeId]: {
                    ...(prev[serviceTypeId] || { workers: {} }),
                    workers: {
                        ...(prev[serviceTypeId]?.workers || {}),
                        [assignmentId]: {
                            ...(prev[serviceTypeId]?.workers?.[assignmentId] || { amount: 0, mode: 'year' }),
                            ...patch,
                        },
                    },
                },
            };
            try { localStorage.setItem(`authAllocationsV2:${patientId}`, JSON.stringify(updated)); } catch { /* ignore */ }
            return updated;
        });
    }

    function usdAuth(val) {
        if (!val && val !== 0) return '—';
        return '$' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // Loading state per plan for lines lazy-loading
    const [planLinesLoading, setPlanLinesLoading] = useState({});
    // const [hasInitialized, setHasInitialized] = useState(false); // Track if data has been loaded once

    // Parse payment multipliers from query parameters or department mapping (runs once on mount)
    useEffect(() => {
        if (businessId === null || businessId === undefined) return;

        const fetchData = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            // With a token the business comes from it only; a businessId added to the URL is ignored
            const queryBusinessId = sessionIds ? null : (urlParams.get('departmentId') || urlParams.get('businessId'));
            const paymentMultipliersParam = urlParams.get('paymentMultipliers');
            const effectiveBusinessId = queryBusinessId || businessId;
            const businessesWithServiceCodes = [10935, 10937, 10942, 11004, 11173, 11215, 9335];
            const shouldPassBusinessId = effectiveBusinessId &&
                businessesWithServiceCodes.includes(parseInt(effectiveBusinessId));
            let loadedPlans = []; // shared between phases so Phase 4 can check if auto-create is needed
            let authorizedServicesForRange = []; // fetched early in Phase 2 so auto-created plans can use the current POS range; reused by Phase 4

            try {
                setIsLoadingData(true);

                // --- Phase 1: business login (must complete first) ---
                if (businessId && businessId !== 1 && businessId !== '1') {
                    setIsBusinessLoginComplete(false);
                    try {
                        const loginResponse = await businessLogin(businessId);
                        if (loginResponse.success) {
                            setIsBusinessLoginComplete(true);
                            if (isGuestMode) toast.success(`Authenticated as ${loginResponse.businessAlias}`);
                        } else {
                            setIsBusinessLoginComplete(true);
                            if (isGuestMode) toast.error('Business login failed');
                        }
                    } catch (error) {
                        console.error('Business login failed:', error);
                        setIsBusinessLoginComplete(true);
                        if (isGuestMode) toast.error('Failed to authenticate with business. Some features may not work.');
                    }
                } else {
                    localStorage.removeItem('businessAuthToken');
                    localStorage.removeItem('businessUserId');
                    localStorage.removeItem('businessId');
                    setIsBusinessLoginComplete(true);
                }

                // --- SDP entry flag (company config), needed before a plan can be auto-created ---
                let isSdp = false;
                if (effectiveBusinessId) {
                    const configList = await getConfigurableList(effectiveBusinessId).catch(() => ({ data: [] }));
                    isSdp = isSdpEntryEnabled(configList?.data);
                }
                // ?sdpEntry=1 / 0 override, honored only inside Ankota's iframe or on localhost —
                // a direct link or another site embedding this page gets the company flag only
                const sdpParam = urlParams.get('sdpEntry');
                if ((isEmbeddedInAnkotaParent || isLocalhost) && sdpParam !== null) {
                    isSdp = sdpParam === '1' || sdpParam.toLowerCase() === 'true';
                }
                setSdpEntry(isSdp);

                // --- Phase 2: fetch plans, auto-create if none cover today (spinner stays on) ---
                if (isGuestMode) {
                    const guestPlans = getFromLocalStorage(GUEST_PLANS_KEY, []);
                    const guestSections = getFromLocalStorage(GUEST_SECTIONS_KEY, {});
                    const guestBudgets = getFromLocalStorage(GUEST_BUDGETS_KEY, {});
                    setSpendingPlans(guestPlans);
                    setApiSections(guestSections);
                    setApprovedBudgets(guestBudgets);
                    setExpandedPlans(guestPlans.length > 0 ? new Set([0]) : new Set());
                } else if (patientId) {
                    const plansData = await getSpendingPlans(patientId).catch(() => ({ data: [] }));
                    loadedPlans = plansData?.data || [];

                    // Fetch authorizations (POS) early so an auto-created plan can use the current POS range
                    const authSvcsForRange = await getAuthorizedServices(patientId).catch(() => ({ data: [] }));
                    authorizedServicesForRange = authSvcsForRange?.data || [];
                    setAuthorizedServices(authorizedServicesForRange);

                    // Determine the currently active POS (authorization) date range, if any; if several
                    // POS ranges cover today, use the one that started most recently
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const currentPos = authorizedServicesForRange
                        .filter(svc => svc.StartDate && svc.EndDate)
                        .map(svc => {
                            const start = new Date(svc.StartDate); start.setHours(0, 0, 0, 0);
                            const end = new Date(svc.EndDate); end.setHours(23, 59, 59, 999);
                            return { start, end };
                        })
                        .filter(({ start, end }) => today >= start && today <= end)
                        .sort((a, b) => b.start - a.start)[0];

                    // Find the plan that covers today, if any
                    const currentPlan = loadedPlans.find(plan => {
                        const start = new Date(plan.start_date); start.setHours(0, 0, 0, 0);
                        const end = new Date(plan.end_date); end.setHours(23, 59, 59, 999);
                        return today >= start && today <= end;
                    });

                    if (!currentPlan && businessId) {
                        // No plan covers today - auto-create one, preferring the active POS range over the calendar year
                        try {
                            const year = new Date().getFullYear();
                            const payload = {
                                PatientId: patientId,
                                Name: `Spending Plan ${year}`,
                                Year: year,
                                // SDP plans stay a draft until Activate writes the authorizations
                                IsActive: isSdp ? 0 : 1,
                                StartDate: currentPos ? formatDateForApi(currentPos.start) : `${year}-01-01`,
                                EndDate: currentPos ? formatDateForApi(currentPos.end) : `${year}-12-31`,
                                BusinessId: businessId,
                                businessId: businessId,
                            };
                            try {
                                const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                                const createdBy = stored?.userId || window.__createdUserId || null;
                                if (createdBy) payload.CreatedByUserId = createdBy;
                            } catch (e) { /* ignore */ }

                            await addSpendingPlan(payload);
                            const refreshed = await getSpendingPlans(patientId).catch(() => ({ data: [] }));
                            loadedPlans = refreshed?.data || [];
                        } catch (autoErr) {
                            console.error('Auto-create plan failed:', autoErr);
                        }
                    } else if (currentPlan && currentPos && businessId && !isSdp) {
                        // A plan already covers today - snap its range to the active POS if it doesn't already match.
                        // Not for SDP: there the plan's dates drive the POS on Activate, not the other way round.
                        const posStart = formatDateForApi(currentPos.start);
                        const posEnd = formatDateForApi(currentPos.end);
                        if (formatDateForApi(currentPlan.start_date) !== posStart || formatDateForApi(currentPlan.end_date) !== posEnd) {
                            try {
                                const payload = {
                                    ...currentPlan,
                                    Name: currentPlan.Name || currentPlan.planName || '',
                                    start_date: posStart,
                                    end_date: posEnd,
                                    StartDate: posStart,
                                    EndDate: posEnd,
                                    BusinessId: businessId,
                                    businessId: businessId,
                                };
                                await editSpendingPlan(payload);
                                loadedPlans = loadedPlans.map(plan =>
                                    plan.PlanId === currentPlan.PlanId
                                        ? { ...plan, start_date: posStart, end_date: posEnd, StartDate: posStart, EndDate: posEnd }
                                        : plan
                                );
                            } catch (syncErr) {
                                console.error('Failed to sync plan dates to POS range:', syncErr);
                            }
                        }
                    }

                    setSpendingPlans(loadedPlans);
                    setApiSections({});
                    if (loadedPlans.length > 0) {
                        setExpandedPlans(new Set([0]));
                        // Preload lines for whichever plan actually covers today (matching what the
                        // "Current" tab will display) — not just raw array index 0, which can be an
                        // unrelated old/empty plan and leave the real displayed plan's lines unloaded
                        // until some save action happens to trigger a full refreshPlanData().
                        const planToLoad = loadedPlans.find(plan => {
                            const start = new Date(plan.start_date); start.setHours(0, 0, 0, 0);
                            const end = new Date(plan.end_date); end.setHours(23, 59, 59, 999);
                            return today >= start && today <= end;
                        }) || loadedPlans[0];
                        ensurePlanLinesLoaded(planToLoad.PlanId);
                        ensurePayrollRatesLoaded(planToLoad);
                    } else {
                        setExpandedPlans(new Set());
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoadingData(false);
                // --- Phase 4: load all non-critical data in background after UI is visible ---
                Promise.all([
                    getServiceCodes(shouldPassBusinessId ? effectiveBusinessId : null).catch(() => ({ data: [] })),
                    effectiveBusinessId
                        ? getExportableServiceIds(effectiveBusinessId).catch(() => ({ data: [] }))
                        : Promise.resolve({ data: [] }),
                    effectiveBusinessId
                        ? getBusinessMultipliers(effectiveBusinessId).catch(() => ({ data: [] }))
                        : Promise.resolve({ data: [] }),
                    // Already fetched in Phase 2 (needed there to pick the auto-created plan's date range)
                    Promise.resolve({ data: authorizedServicesForRange }),
                    patientId ? getPatientAssignedWorkers(patientId).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                ]).then(async ([serviceCodesData, evvData, multipliersData, authSvcsData, authWorkersData]) => {
                    setServiceCodeOptions(serviceCodesData?.data || []);
                    setEvvServiceIds(evvData?.data || []);
                    setAuthorizedServices(authSvcsData?.data || []);
                    setAssignedWorkers(authWorkersData?.data || []);
                    try {
                        const saved = JSON.parse(localStorage.getItem(`authAllocationsV2:${patientId}`) || '{}');
                        setAuthAllocations(saved);
                    } catch { /* ignore */ }

                    // Resolve multipliers: API → query params → defaults
                    let resolvedMultipliers = null;
                    if (multipliersData?.data?.length > 0) {
                        const multipliersString = multipliersData.data[0].Multipliers;
                        if (multipliersString) {
                            const vals = multipliersString.split(',').map(m => parseFloat(m.trim()));
                            if (vals.length >= PAYMENT_MODEL_OPTIONS.length) {
                                resolvedMultipliers = {};
                                PAYMENT_MODEL_OPTIONS.forEach((model, i) => { resolvedMultipliers[model] = vals[i] || 1; });
                            }
                        }
                    }
                    if (!resolvedMultipliers && paymentMultipliersParam) {
                        const vals = paymentMultipliersParam.split(',').map(m => parseFloat(m.trim()));
                        if (vals.length >= PAYMENT_MODEL_OPTIONS.length) {
                            resolvedMultipliers = {};
                            PAYMENT_MODEL_OPTIONS.forEach((model, i) => { resolvedMultipliers[model] = vals[i] || 1; });
                        }
                    }
                    if (resolvedMultipliers) setPaymentModelMultipliers(resolvedMultipliers);
                }).catch(err => console.error('Background data load error:', err));
            }
        };

        fetchData();
    }, [patientId, businessId, isGuestMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load custom service types from localStorage
    useEffect(() => {
        try {
            const stored = localStorage.getItem('customServiceTypes');
            if (stored) {
                setCustomServiceTypes(JSON.parse(stored));
            }
        } catch (error) {
            console.error('Error loading custom service types:', error);
        }
    }, []);

    // Save custom service types to localStorage whenever they change (but not for guest mode keys)
    useEffect(() => {
        try {
            if (Object.keys(customServiceTypes).length > 0) {
                localStorage.setItem('customServiceTypes', JSON.stringify(customServiceTypes));
            }
        } catch (error) {
            console.error('Error saving custom service types:', error);
        }
    }, [customServiceTypes]);

    /** ---- HELPERS ---- **/
    // const handleAddRow = (sectionKey) => {
    //     const newRow = {
    //         id: sections[sectionKey].length + 1,
    //         serviceCode: "",
    //         provider: "",
    //         description: "",
    //         ippGoal: "",
    //         basePay: "",
    //         baseBasePay: 0, // keep raw values
    //         paymentModel: "",
    //         pricingUnit: "",
    //         basePricingUnit: 0, // keep raw values
    //         frequency: "",
    //         annualCost: "",
    //         isNew: true,
    //     };
    //     setSections({ ...sections, [sectionKey]: [...sections[sectionKey], newRow] });
    // };

    // Add copy row handler
    // const handleCopyRow = (sectionKey, row) => {
    //     const newRow = {
    //         ...row,
    //         id: sections[sectionKey].length + 1, // new ID
    //         isNew: true, // mark as new until saved
    //         baseBasePay: row.baseBasePay ?? row.basePay, // keep original
    //         basePricingUnit: row.basePricingUnit ?? row.pricingUnit,
    //     };
    //     setSections((prev) => ({
    //         ...prev,
    //         [sectionKey]: [...prev[sectionKey], newRow],
    //     }));
    // };

    // const handleChange = (sectionKey, id, field, value) => {
    //     setSections((prev) => ({
    //         ...prev,
    //         [sectionKey]: prev[sectionKey].map((r) => {
    //             if (r.id !== id) return r;
    //             const updatedRow = { ...r };

    //             // Normalize numeric inputs
    //             const v =
    //                 NUMERIC_FIELDS.has(field) ? (value === "" ? "" : Number(value)) : value;

    //             // Update editable fields
    //             if (field === "basePay") {
    //                 updatedRow.basePay = v;
    //             } else if (field === "paymentModel") {
    //                 updatedRow.paymentModel = v;
    //             } else if (field === "frequency") {
    //                 updatedRow.frequency = v; // make sure freq is updated immediately
    //             } else {
    //                 updatedRow[field] = v;
    //             }

    //             // Multiplier mapping per dropdown
    //             let multiplier = 0;
    //             if (updatedRow.paymentModel === "Sole-Employer") multiplier = 11.5;
    //             if (updatedRow.paymentModel === "Co-Employer") multiplier = 20.5;
    //             if (updatedRow.paymentModel === "Bill Payer") multiplier = 0;

    //             const base = Number(updatedRow.basePay) || 0;

    //             // Pricing unit = basePay + (basePay * percentage/100)
    //             updatedRow.pricingUnit = base + (base * multiplier / 100);

    //             // --- recalculate frequency if pricingUnit changes and not editing frequency directly ---
    //             if ((field === "basePay" || field === "paymentModel") && updatedRow.pricingUnit) {
    //                 updatedRow.frequency = Number((updatedRow.pricingUnit * 12).toFixed(2));
    //             }

    //             const freq =
    //                 field === "frequency"
    //                     ? Number(v) || 0
    //                     : Number(updatedRow.frequency) || 0;
    //             const pu = Number(updatedRow.pricingUnit) || 0;

    //             // Annual cost = frequency × pricing unit
    //             updatedRow.annualCost = freq * pu;

    //             return updatedRow;
    //         }),
    //     }));
    // };

    // const handleSave = (sectionKey, id) => {
    //     setSections({
    //         ...sections,
    //         [sectionKey]: sections[sectionKey].map((r) =>
    //             r.id === id ? { ...r, isNew: false } : r
    //         ),
    //     });
    // };

    // const handleDelete = (sectionKey, id) => {
    //     setSections({
    //         ...sections,
    //         [sectionKey]: sections[sectionKey].filter((r) => r.id !== id),
    //     });
    // };

    // API data handlers
    const handleApiCopyRow = (planId, sectionKey, row) => {
        // Use functional updater so we derive a consistent new id from the latest state
        setApiSections(prev => {
            const currentRows = prev?.[planId]?.[sectionKey] || [];
            const newId = Math.max(...currentRows.map(r => r.id), 0) + 1;
            const newRow = { ...row, id: newId, isNew: true };

            const updated = {
                ...prev,
                [planId]: {
                    ...prev[planId],
                    [sectionKey]: [...currentRows, newRow]
                }
            };

            // Persist for guest users
            if (isGuestMode) saveToLocalStorage(GUEST_SECTIONS_KEY, updated);

            // Mark the new row as modified so the UI shows save state
            setModifiedRows(prevSet => {
                const s = new Set(prevSet);
                s.add(`${planId}-${sectionKey}-${newId}`);
                return s;
            });

            // Focus the new row's serviceCode field for immediate editing
            setEditingCell({ sectionKey: `${planId}-${sectionKey}`, rowId: newId, field: 'serviceCode' });

            return updated;
        });
    };

    const handleApiDeleteRow = async (planId, sectionKey, row) => {
        console.log('Deleting row:', { planId, sectionKey, row });
        
        // Plan lock removed — allow deletion in guest mode
        
        // Show confirmation modal before deleting
        setConfirmModalData({
            title: 'Delete Row',
            message: `Are you sure you want to delete this row?\n\nThis action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setShowConfirmModal(false);
                await performDeleteRow(planId, sectionKey, row);
            }
        });
        setShowConfirmModal(true);
    };

    const performDeleteRow = async (planId, sectionKey, row) => {
        try {
            if (isGuestMode) {
                // Guest mode - remove from localStorage
                const updatedSections = {
                    ...apiSections,
                    [planId]: {
                        ...apiSections[planId],
                        [sectionKey]: apiSections[planId][sectionKey].filter(r => r.id !== row.id)
                    }
                };
                setApiSections(updatedSections);
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                
                // Remove from modified rows if it was there
                setModifiedRows(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(`${planId}-${sectionKey}-${row.id}`);
                    return newSet;
                });
                
                return;
            }

            // Authenticated mode - delete from API
            if (!row.originalLine?.LineId && !row.isNew) {
                console.warn('No LineId found for deletion');
                return;
            }

            // Only call API if row exists in backend (has LineId)
            if (row.originalLine?.LineId) {
                await deleteSpendingPlanLine({ LineId: row.originalLine.LineId });
            }
            
            // Remove from local state
            setApiSections(prev => ({
                ...prev,
                [planId]: {
                    ...prev[planId],
                    [sectionKey]: prev[planId][sectionKey].filter(r => r.id !== row.id)
                }
            }));
            
            // Remove from modified rows if it was there
            setModifiedRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${planId}-${sectionKey}-${row.id}`);
                return newSet;
            });
            
            // Refresh data if it was an existing row
            if (row.originalLine?.LineId) {
                await refreshPlanData();
            }
        } catch (error) {
            console.error('Error deleting spending plan line:', error);
            toast.error('Failed to delete spending plan line. Please try again.');
        }
    };

    const handleApiAddRow = (planId, sectionKey) => {
        console.log('Adding new row for plan:', planId, 'section:', sectionKey);
        
        // Plan lock removed — allow adding rows in guest mode

        // Create row using functional updater to avoid id collisions when adding quickly
        setApiSections(prev => {
            const currentRows = prev?.[planId]?.[sectionKey] || [];
            const newId = Math.max(...currentRows.map(r => r.id), 0) + 1;

            const basePay = 0;
            const pricingUnit = basePay; // For Bill Payer, pricing unit equals base pay
            const frequency = 1; // Default frequency is 1 (yearly)
            const annualCost = pricingUnit * frequency; // Calculate annual cost

            const newRow = {
                id: newId,
                serviceCode: '',
                provider: '',
                liveIn: false,
                description: '',
                ippGoal: '',
                basePay: basePay,
                paymentModel: 'Bill Payer',
                pricingUnit: pricingUnit,
                frequency: frequency,
                annualCost: annualCost,
                isApi: true,
                planId: planId,
                isNew: true
            };

            const updatedSections = {
                ...prev,
                [planId]: {
                    ...prev[planId],
                    [sectionKey]: [...(prev[planId]?.[sectionKey] || []), newRow]
                }
            };

            // Save to localStorage in guest mode
            if (isGuestMode) {
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
            }

            // Mark as modified using the new id
            const rowKey = `${planId}-${sectionKey}-${newId}`;
            setModifiedRows(prevSet => {
                const s = new Set(prevSet);
                s.add(rowKey);
                return s;
            });

            // Auto-focus the serviceCode field for the new row (schedule after state update)
            setTimeout(() => {
                setEditingCell({ sectionKey: `${planId}-${sectionKey}`, rowId: newId, field: 'serviceCode' });
            }, 0);

            return updatedSections;
        });
    };

    const handleApiAddAuthRow = (planId, sectionKey, svcName) => {
        setApiSections(prev => {
            const currentRows = prev?.[planId]?.[sectionKey] || [];
            const newId = Math.max(...currentRows.map(r => r.id), 0) + 1;
            const newRow = {
                id: newId,
                serviceCode: svcName,
                provider: '',
                liveIn: false,
                description: svcName,
                ippGoal: '',
                basePay: 0,
                paymentModel: 'Bill Payer',
                pricingUnit: 0,
                frequency: 1,
                annualCost: 0,
                allocatedBudget: null,
                isApi: true,
                planId: planId,
                isNew: true
            };
            const updatedSections = {
                ...prev,
                [planId]: {
                    ...prev[planId],
                    [sectionKey]: [...(prev[planId]?.[sectionKey] || []), newRow]
                }
            };
            if (isGuestMode) {
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
            }
            const rowKey = `${planId}-${sectionKey}-${newId}`;
            setModifiedRows(prevSet => {
                const s = new Set(prevSet);
                s.add(rowKey);
                return s;
            });
            // Pre-filled service code — auto-focus provider field
            setTimeout(() => {
                setEditingCell({ sectionKey: `${planId}-${sectionKey}`, rowId: newId, field: 'provider' });
            }, 0);
            return updatedSections;
        });
    };

    const handleApiSave = async (planId, sectionKey, row) => {
        console.log('Saving row:', { planId, sectionKey, row });
        
        // Plan lock removed — allow saving in guest mode

        const rowKey = `${planId}-${sectionKey}-${row.id}`;
        setSavingRows(prev => new Set([...prev, rowKey]));
        
        try {
            if (isGuestMode) {
                // Guest mode - save to localStorage
                const updatedSections = {
                    ...apiSections,
                    [planId]: {
                        ...apiSections[planId],
                        [sectionKey]: apiSections[planId][sectionKey].map(r => 
                            r.id === row.id ? { ...row, isNew: false } : r
                        )
                    }
                };
                setApiSections(updatedSections);
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                
                // Remove from modified rows
                setModifiedRows(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(rowKey);
                    return newSet;
                });

                return true;
            }

            // Authenticated mode - save to API
            // Map section key to plan_type
            const planTypeMap = {
                'livingarrangements': 'Living Arrangements',
                'employmentandcommunityparticipation': 'Employment and Community Participation', 
                'healthandsafety': 'Health and Safety'
            };
            
            // Try to include any custom service id so backend can persist it in the line
            const resolveCustomServiceId = () => {
                // 1) From the row itself
                if (row.customServiceId) return row.customServiceId;
                // 2) From our in-memory mapping using this row key
                const key = `${planId}-${sectionKey}-${row.id}`;
                const mapped = customServiceTypes[key]?.serviceId;
                if (mapped) return mapped;
                // 3) Heuristic: find by same serviceCode and description within the same plan/section mapping
                const entries = Object.values(customServiceTypes || {});
                const match = entries.find(v => v && String(v.serviceCode) === String(row.serviceCode) && (v.customName === row.description || v.originalServiceDescription === row.description));
                return match?.serviceId || null;
            };
            const customServiceId = resolveCustomServiceId();

            if (sdpEntry && !row.serviceCode) {
                toast.error('Select a service code before saving this line.');
                return false;
            }

            // SDP rows carry the provider record picked in ProviderPicker; the by-name match is only
            // a fallback for the legacy screen (an unlinked SDP row stays unlinked until picked)
            const providerLower = (row.provider || '').trim().toLowerCase();
            const resolvedWorkerId = row.workerId
                || (sdpEntry ? null : assignedWorkers.find(w => (w.WorkerName || '').trim().toLowerCase() === providerLower)?.WorkerId)
                || null;
            const serviceCodeLower = (row.serviceCode || '').trim().toLowerCase();
            const resolvedServiceId = row.serviceId
                || authorizedServices.find(svc => (svc.ServiceTypeName || '').trim().toLowerCase() === serviceCodeLower)?.ServiceTypeId
                || null;

            const payload = {
                PlanId: planId,
                ServiceCode: row.serviceCode || '',
                Provider: row.provider || '',
                LiveIn: row.liveIn || false,
                ServiceDescription: row.description || row.serviceCode || '',
                IPPGoal: row.ippGoal || '',
                BasePay: Number(row.basePay) || 0,
                PricingUnit: Number(row.pricingUnit) || 0,
                AnnualFrequency: Number(row.frequency) || 0,
                TotalAnnualCost: Number(row.pricingUnit) * Number(row.frequency) || 0,
                AllocatedBudget: (row.allocatedBudget !== null && row.allocatedBudget !== undefined && row.allocatedBudget !== '') ? Number(row.allocatedBudget) : null,
                plan_type: planTypeMap[sectionKey] || 'Other',
                PaymentModel: row.paymentModel || 'Bill Payer',
                CustomServiceId: customServiceId,
                WorkerId: resolvedWorkerId,
                ServiceId: resolvedServiceId,
                BusinessId: businessId,
            };
            if (sdpEntry) {
                // Build-up budgeting: the line's own total is its budget, nothing is allocated down
                delete payload.AllocatedBudget;
                payload.ProviderType = isVendorRow(row) ? PROVIDER_TYPE_VENDOR : PROVIDER_TYPE_DSW;
                payload.IsEvv = isRowEvv(row) ? 1 : 0;
                payload.LiveIn = canRowBeLiveIn(row) ? isLiveIn(row.liveIn) : false;
            }
            // Attach CreatedByUserId when available from demo credentials or temporary window var
            try {
                const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                const createdBy = stored?.userId || window.__createdUserId || null;
                if (createdBy) payload.CreatedByUserId = createdBy;
            } catch (e) { /* ignore */ }
            
            if (row.isNew) {
                // Add new row
                const addResult = await addSpendingPlanLine(payload);
                if (sdpEntry) {
                    // Remember it as SDP-entered: use the returned LineId, or else the line that is
                    // in the plan now but wasn't before this save
                    const newLineId = addResult?.LineId ?? addResult?.data?.LineId ?? addResult?.lineId;
                    let newLineIds;
                    if (newLineId !== undefined && newLineId !== null) {
                        newLineIds = [newLineId];
                    } else {
                        const before = new Set(Object.values(apiSections?.[planId] || {}).flat().filter(r => !r.isNew).map(r => String(r.id)));
                        const after = await getSpendingPlanLines(planId).catch(() => ({ data: [] }));
                        newLineIds = (after?.data || []).map(l => l.LineId).filter(id => !before.has(String(id)));
                    }
                    markSdpEnteredLines(newLineIds);
                    // The line save doesn't store ProviderType yet, so keep it here for the reload
                    rememberLineProviderTypes(newLineIds, payload.ProviderType);
                }
            } else if (row.originalLine?.LineId) {
                // Edit existing row
                payload.LineId = row.originalLine.LineId;
                await editSpendingPlanLine(payload);
                if (sdpEntry) rememberLineProviderTypes([payload.LineId], payload.ProviderType);
            }
            
            // If original service is EVV and live-in is unchecked, toggle EVV on the overridden service
            try {
                const customKey = `${planId}-${sectionKey}-${row.id}`;
                // Only use serviceId from explicit user-created custom service type, never from row.customServiceId
                // which may have been polluted by old saves that stored the master ServiceId as CustomServiceId
                const newServiceId = customServiceTypes[customKey]?.serviceId || null;
                const isEvvLine = sdpEntry ? isRowEvv(row) : (isOriginalServiceEVV(row.serviceCode) && isLiveInUnchecked(row.liveIn));
                const shouldToggle = !isGuestMode && isEvvLine && newServiceId;
                console.log('EVV toggle check:', {
                    guestMode: isGuestMode,
                    originalServiceCode: row.serviceCode,
                    isEVV: isOriginalServiceEVV(row.serviceCode),
                    liveInUnchecked: isLiveInUnchecked(row.liveIn),
                    newServiceId
                });
                if (shouldToggle) {
                    const resp = await toggleServiceEvv(newServiceId, 1);
                    console.log('toggleServiceEvv response:', resp);
                }
            } catch (e) {
                console.warn('toggleServiceEvv call failed (non-blocking):', e);
            }

            // Remove from modified rows
            setModifiedRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(rowKey);
                return newSet;
            });
            
            // Refresh data to get updated information
            await refreshPlanData();
            return true;

        } catch (error) {
            console.error('Error saving spending plan line:', error);
            toast.error('Failed to save spending plan line. Please try again.');
            return false;
        } finally {
            setSavingRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(rowKey);
                return newSet;
            });
        }
    };

    // Function to handle saving plan date changes
    const handleSavePlanDates = async (planId) => {
        try {
            setSavingPlanDates(planId);
            const dateForm = planDateForms[planId];
            if (!dateForm) {
                setSavingPlanDates(null);
                return;
            }

            // Find the current plan to get all its properties
            const currentPlan = spendingPlans.find(plan => plan.PlanId === planId);
            if (!currentPlan) {
                toast.error('Plan not found');
                setSavingPlanDates(null);
                return;
            }

            // Format dates to avoid timezone shift
            const formattedStartDate = formatDateForApi(dateForm.startDate);
            const formattedEndDate = formatDateForApi(dateForm.endDate);

            // Send the full plan object with updated dates
            const payload = {
                ...currentPlan,
                Name: currentPlan.Name || currentPlan.planName || '',
                start_date: formattedStartDate,
                end_date: formattedEndDate,
                StartDate: formattedStartDate, // Include both formats in case API expects different property names
                EndDate: formattedEndDate,
                BusinessId: businessId,
                businessId: businessId
            };

            await editSpendingPlan(payload);

            // Update the local state with new dates
            setSpendingPlans(prev => prev.map(plan =>
                plan.PlanId === planId
                    ? { ...plan, start_date: formattedStartDate, end_date: formattedEndDate }
                    : plan
            ));

            // Clear editing state
            setEditingPlanDates(null);
            setPlanDateForms(prev => {
                const newForms = { ...prev };
                delete newForms[planId];
                return newForms;
            });

            toast.success('Plan dates updated successfully!');
        } catch (error) {
            console.error('Error updating plan dates:', error);
            toast.error('Failed to update plan dates. Please try again.');
        } finally {
            setSavingPlanDates(null);
        }
    };

    const handleSavePlanName = async (planId) => {
        try {
            setSavingPlanName(planId);
            const nameForm = planNameForms[planId];
            const currentPlan = spendingPlans.find(p => p.PlanId === planId);
            
            if (!nameForm || !currentPlan) {
                toast.error('Invalid plan or name data');
                setSavingPlanName(null);
                return;
            }

            // Send the plan object with updated name
            const payload = {
                ...currentPlan,
                Name: nameForm.planName,
                StartDate: currentPlan.start_date || currentPlan.StartDate,
                EndDate: currentPlan.end_date || currentPlan.EndDate,
                BusinessId: businessId,
                businessId: businessId
            };

            await editSpendingPlan(payload);
            
            // Update the local state with new name
            setSpendingPlans(prev => prev.map(plan => 
                plan.PlanId === planId 
                    ? { ...plan, Name: nameForm.planName }
                    : plan
            ));

            // Clear editing state
            setEditingPlanName(null);
            setPlanNameForms(prev => {
                const newForms = { ...prev };
                delete newForms[planId];
                return newForms;
            });

            toast.success('Plan name updated successfully!');
        } catch (error) {
            console.error('Error updating plan name:', error);
            toast.error('Failed to update plan name. Please try again.');
        } finally {
            setSavingPlanName(null);
        }
    };

    const refreshPlanData = async () => {
        // Only refresh in authenticated mode
        if (isGuestMode) {
            console.log('Skipping refresh in guest mode');
            return;
        }

        try {
            console.log('Refreshing plan data for authenticated user...');
            const plans = spendingPlans;
            const linesPromises = plans.map(async (plan) => {
                const linesData = await getSpendingPlanLines(plan.PlanId);
                return { planId: plan.PlanId, lines: linesData?.data || [] };
            });

            const allLines = await Promise.all(linesPromises);
            const linesMap = {};
            const apiSectionsMap = {};
            
            allLines.forEach(({ planId, lines }) => {
                linesMap[planId] = lines;
                
                const groupedByPlanType = lines.reduce((acc, line) => {
                    const planType = line.plan_type || 'Other';
                    const sectionKey = planType.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');
                    
                    if (!acc[sectionKey]) acc[sectionKey] = [];
                    
                    // Calculate correct pricing unit based on payment model
                    const basePay = parseFloat(line.BasePay) || 0;
                    const paymentModel = line.PaymentModel || 'Bill Payer';
                    // Vendor (SDP) lines have no base pay/burden; their invoice amount is the pricing unit
                    const lineProviderType = line.ProviderType || getRememberedLineProviderType(line.LineId) || getLocalProviderType(line.Provider || line.ProviderName);
                    const correctPricingUnit = lineProviderType === PROVIDER_TYPE_VENDOR
                        ? (parseFloat(line.PricingUnit) || 0)
                        : basePay * (paymentModelMultipliers[paymentModel] || 1);

                    const allocatedBudgetValue = (line.AllocatedBudget !== null && line.AllocatedBudget !== undefined) ? parseFloat(line.AllocatedBudget) : null;

                    acc[sectionKey].push({
                        id: line.LineId,
                        serviceCode: line.ServiceCode || '',
                        provider: line.Provider || line.ProviderName || '',
                        workerId: line.WorkerId || null,
                        providerType: lineProviderType,
                        sdpEntered: isSdpEnteredLine(line),
                        serviceId: line.ServiceId || null,
                        liveIn: line.LiveIn || false,
                        description: line.ServiceDescription || line.Description || '',
                        ippGoal: line.IPPGoal || '',
                        basePay: basePay,
                        paymentModel: paymentModel,
                        pricingUnit: correctPricingUnit,
                        frequency: parseFloat(line.AnnualFrequency) || 0,
                        annualCost: parseFloat(line.TotalAnnualCost) || 0,
                        allocatedBudget: allocatedBudgetValue,
                        customServiceId: line.CustomServiceId || null,
                        isApi: true,
                        planId: planId,
                        originalLine: line,
                        isNew: false
                    });
                    
                    return acc;
                }, {});
                
                apiSectionsMap[planId] = groupedByPlanType;
            });
            
           // setPlanLines(linesMap);
            setApiSections(apiSectionsMap);
        } catch (error) {
            console.error('Error refreshing plan data:', error);
        }
    };

    const handleApiChange = (planId, sectionKey, rowId, field, value) => {
        // Plan lock removed — allow changes in guest mode

        const rowKey = `${planId}-${sectionKey}-${rowId}`;
        
        const updatedSections = {
            ...apiSections,
            [planId]: {
                ...apiSections[planId],
                [sectionKey]: apiSections[planId][sectionKey].map(row => {
                    if (row.id === rowId) {
                        const updatedRow = { ...row, [field]: value };
                        
                        // Recalculate pricing unit based on payment model (same logic as original)
                        if (field === "paymentModel") {
                            const multiplier = getModelMultiplier(value);
                            updatedRow.pricingUnit = roundCents(updatedRow.basePay * multiplier);
                            // Keep existing frequency, just update annual cost
                            updatedRow.annualCost = updatedRow.pricingUnit * (updatedRow.frequency || 0);
                        }
                        
                        // Recalculate pricing unit when base pay changes (for current payment model)
                        if (field === "basePay") {
                            const currentPaymentModel = updatedRow.paymentModel || "Bill Payer";
                            const multiplier = getModelMultiplier(currentPaymentModel);
                            updatedRow.pricingUnit = roundCents(Number(value) * multiplier);
                            // Keep existing frequency, just update annual cost
                            updatedRow.annualCost = updatedRow.pricingUnit * (updatedRow.frequency || 0);
                        }
                        
                        // When frequency changes, update annual cost
                        if (field === "frequency") {
                            // Allow empty string while editing so backspace can clear the field
                            if (value === '') {
                                updatedRow.frequency = '';
                                updatedRow.annualCost = (Number(updatedRow.pricingUnit) || 0) * 0;
                            } else {
                                const freq = Number(value) || 0;
                                const pu = Number(updatedRow.pricingUnit) || 0;
                                updatedRow.frequency = freq;
                                // Annual cost is automatically calculated as pricing unit × frequency
                                updatedRow.annualCost = freq * pu;
                            }
                        }

                        // Vendor (SDP) rows enter the invoice amount directly as the pricing unit
                        if (field === "pricingUnit") {
                            updatedRow.annualCost = (Number(value) || 0) * (Number(updatedRow.frequency) || 0);
                        }

                        return updatedRow;
                    }
                    return row;
                })
            }
        };

        setApiSections(updatedSections);
        
        // Save to localStorage in guest mode
        if (isGuestMode) {
            saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
        }
        
        // Mark row as modified
        console.log('Marking row as modified:', rowKey);
        setModifiedRows(prev => {
            const newSet = new Set([...prev, rowKey]);
            console.log('Modified rows after change:', Array.from(newSet));
            return newSet;
        });
        
        // Track when description is manually changed to mark as custom
        if (field === 'description') {
            const customKey = `${planId}-${sectionKey}-${rowId}`;
            // Find original service description to compare
            const row = apiSections[planId]?.[sectionKey]?.find(r => r.id === rowId);
            if (row?.serviceCode) {
                const serviceCodeOption = serviceCodeOptions.find(opt => 
                    (opt.ServiceTypeID === row.serviceCode || opt.Name === row.serviceCode)
                );
                const originalServiceDescription = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || row.serviceCode || '';
                
                // If description is different from original, mark as manually customized
                if (value && value !== originalServiceDescription) {
                    const existingCustomType = customServiceTypes[customKey];
                    setCustomServiceTypes(prev => {
                        const updated = {
                            ...prev,
                            [customKey]: {
                                serviceId: existingCustomType?.serviceId || null, // Preserve existing ServiceID if any
                                originalServiceDescription: originalServiceDescription,
                                customName: value,
                                serviceCode: row.serviceCode,
                                isCustomized: true,
                                manuallyEdited: true
                            }
                        };
                        // Immediately save to localStorage
                        try {
                            localStorage.setItem('customServiceTypes', JSON.stringify(updated));
                        } catch (error) {
                            console.error('Error saving to localStorage:', error);
                        }
                        return updated;
                    });
                } else if (value === originalServiceDescription && customServiceTypes[customKey]) {
                    // If description is changed back to original, remove the customization
                    setCustomServiceTypes(prev => {
                        const updated = { ...prev };
                        delete updated[customKey];
                        // Immediately save to localStorage
                        try {
                            localStorage.setItem('customServiceTypes', JSON.stringify(updated));
                        } catch (error) {
                            console.error('Error saving to localStorage:', error);
                        }
                        return updated;
                    });
                }
            }
        }
    };

    // --- SDP entry: provider picker, keyboard entry, certified budget, Activate ---

    // Row shape follows the provider type: a vendor is a single amount x count with no pay/burden
    // and never live-in; switching back to a DSW recomputes the rate from base pay + burden
    const applyProviderToRow = (row, { workerId, name, providerType }) => {
        const wasVendor = isVendorRow(row);
        const updatedRow = { ...row, provider: name, workerId: workerId || null, providerType, providerTypeSource: null };
        if (providerType === PROVIDER_TYPE_VENDOR) {
            updatedRow.basePay = 0;
            updatedRow.paymentModel = 'Bill Payer';
            updatedRow.liveIn = false;
            if (!wasVendor) {
                updatedRow.pricingUnit = 0;
                updatedRow.frequency = 1;
            }
        } else if (wasVendor) {
            updatedRow.pricingUnit = roundCents(Number(updatedRow.basePay || 0) * getModelMultiplier(updatedRow.paymentModel || 'Bill Payer'));
        }
        updatedRow.annualCost = getRowTotal(updatedRow);
        return updatedRow;
    };

    const handleSdpProviderSelect = (planId, sectionKey, rowId, selection) => {
        setApiSections(prev => ({
            ...prev,
            [planId]: {
                ...prev[planId],
                [sectionKey]: (prev[planId]?.[sectionKey] || []).map(r => r.id === rowId ? applyProviderToRow(r, selection) : r),
            },
        }));
        setModifiedRows(prev => new Set([...prev, `${planId}-${sectionKey}-${rowId}`]));
    };

    const closeEditingCell = () => setEditingCell({ sectionKey: null, rowId: null, field: null });
    // Blur only closes the cell it belongs to, so a late blur after Tab can't close the next cell
    const closeCellIf = (cellSectionKey, rowId, field) => setEditingCell(prev =>
        (prev.sectionKey === cellSectionKey && prev.rowId === rowId && prev.field === field)
            ? { sectionKey: null, rowId: null, field: null }
            : prev);

    // New line for keyboard entry, pre-filled with the previous line's code so several providers
    // under one code are quick to key; focus starts on the code so it's one Tab to move past it
    const addSdpRow = (planId, sectionKey, serviceCode = '', description = '') => {
        setApiSections(prev => {
            const currentRows = prev?.[planId]?.[sectionKey] || [];
            const newId = Math.max(...currentRows.map(r => (typeof r.id === 'number' ? r.id : 0)), 0) + 1;
            const newRow = {
                id: newId,
                serviceCode,
                provider: '',
                workerId: null,
                providerType: null,
                liveIn: false,
                description: description || serviceCode,
                ippGoal: '',
                basePay: 0,
                paymentModel: 'Bill Payer',
                pricingUnit: 0,
                frequency: 1,
                annualCost: 0,
                isApi: true,
                planId,
                isNew: true,
            };
            setModifiedRows(prevSet => new Set([...prevSet, `${planId}-${sectionKey}-${newId}`]));
            setTimeout(() => setEditingCell({ sectionKey: `${planId}-${sectionKey}`, rowId: newId, field: 'serviceCode' }), 0);
            const updated = { ...prev, [planId]: { ...prev[planId], [sectionKey]: [...currentRows, newRow] } };
            if (isGuestMode) saveToLocalStorage(GUEST_SECTIONS_KEY, updated);
            return updated;
        });
    };

    // Tab / Shift+Tab move through the row's cells in entry order; Enter commits the row and,
    // for a newly keyed line, starts the next one
    const handleSdpCellKey = async (planId, sectionKey, row, field, key) => {
        if (key === 'Escape') { closeEditingCell(); return; }
        if (key === 'Tab' || key === 'ShiftTab') {
            const order = getRowFieldOrder(row);
            const next = order[order.indexOf(field) + (key === 'Tab' ? 1 : -1)];
            if (next) setEditingCell({ sectionKey: `${planId}-${sectionKey}`, rowId: row.id, field: next });
            else closeEditingCell();
            return;
        }
        if (key === 'Enter') {
            closeEditingCell();
            const wasNew = row.isNew;
            const saved = await handleApiSave(planId, sectionKey, row);
            if (saved && wasNew) addSdpRow(planId, sectionKey, row.serviceCode, row.description);
        }
    };

    // Shared key handler for plain inputs in SDP rows (non-SDP keeps Enter/Escape = close)
    const sdpInputKeyDown = (planId, sectionKey, row, field) => (e) => {
        if (!sdpEntry) {
            if (e.key === "Enter" || e.key === "Escape") closeEditingCell();
            return;
        }
        if (e.key === 'Tab' || e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault();
            handleSdpCellKey(planId, sectionKey, row, field, e.key === 'Tab' && e.shiftKey ? 'ShiftTab' : e.key);
        }
    };

    // SDP: a typed Total Annual Cost is worked back into the row, since Total = Rate/Amount x
    // Hours/Count. Supplier: the amount (total / count). Worker: the hours (total / rate), because
    // the rate comes from base pay + burden.
    const applySdpTotal = (planId, sectionKey, row, rawValue) => {
        const total = parseFloat(String(rawValue).replace(/[^0-9.]/g, ''));
        if (isNaN(total) || total < 0) return;
        if (isVendorRow(row)) {
            const count = Number(row.frequency) || 1;
            handleApiChange(planId, sectionKey, row.id, 'pricingUnit', roundCents(total / count));
            return;
        }
        const rate = Number(row.pricingUnit) || 0;
        if (!(rate > 0)) {
            toast.error('Enter the base pay first, so the hours can be worked out from the total.');
            return;
        }
        handleApiChange(planId, sectionKey, row.id, 'frequency', roundCents(total / rate));
    };

    const getPlanRows = (planId) => Object.values(apiSections[planId] || {}).flat();

    const usdSigned = (n) => (n < 0 ? '-' : '') + usdAuth(Math.abs(n));

    const getSdpBudgetSummary = (plan, planApiSections) => {
        const planTotal = sumDisplayRows(Object.values(planApiSections || {}).flat());
        const certified = plan.CertifiedBudget;
        const hasCertified = certified !== null && certified !== undefined && certified !== '';
        return `Certified: ${hasCertified ? usdAuth(Number(certified)) : 'not set'} | Planned: ${usdAuth(planTotal)} | Unallocated: ${hasCertified ? usdSigned(Number(certified) - planTotal) : '—'}`;
    };

    // Providers already on this page (the patient's assigned workers), offered instantly in the
    // picker while the server search runs
    const sdpLocalProviders = (() => {
        const seen = new Set();
        return assignedWorkers
            .filter(w => w.WorkerId && !seen.has(String(w.WorkerId)) && seen.add(String(w.WorkerId)))
            .map(w => ({
                workerId: w.WorkerId,
                name: w.WorkerName || '',
                providerType: (w.ProviderType || w.WorkerType) === PROVIDER_TYPE_VENDOR ? PROVIDER_TYPE_VENDOR : PROVIDER_TYPE_DSW,
            }));
    })();

    const handleSaveCertifiedBudget = async (plan) => {
        const amount = certifiedBudgetDraft === '' ? null : Number(String(certifiedBudgetDraft).replace(/[^0-9.]/g, ''));
        if (amount !== null && isNaN(amount)) {
            toast.error('Enter a dollar amount');
            return;
        }
        setSavingCertifiedBudget(plan.PlanId);
        try {
            if (!isGuestMode) {
                await editSpendingPlan({
                    ...plan,
                    Name: plan.Name || plan.planName || '',
                    StartDate: plan.start_date || plan.StartDate,
                    EndDate: plan.end_date || plan.EndDate,
                    CertifiedBudget: amount,
                    BusinessId: businessId,
                    businessId: businessId,
                });
            }
            const updatedPlans = spendingPlans.map(p => p.PlanId === plan.PlanId ? { ...p, CertifiedBudget: amount } : p);
            setSpendingPlans(updatedPlans);
            if (isGuestMode) saveToLocalStorage(GUEST_PLANS_KEY, updatedPlans);
            setEditingCertifiedBudget(null);
        } catch (error) {
            console.error('Error saving certified budget:', error);
            toast.error('Failed to save certified budget. Please try again.');
        } finally {
            setSavingCertifiedBudget(null);
        }
    };

    // Same checks the backend enforces (it is the authority); caught here first so staff see every
    // problem at once instead of one 400 at a time
    // Checks on the plan itself; what to write is worked out against the server in handleActivatePlan
    const getActivationProblems = (plan) => {
        const problems = [];
        const planSections = apiSections[plan.PlanId] || {};
        const hasUnsaved = Object.entries(planSections).some(([sectionKey, sectionRows]) =>
            (sectionRows || []).some(r => r.isNew || modifiedRows.has(`${plan.PlanId}-${sectionKey}-${r.id}`)));
        if (hasUnsaved) problems.push('Save every line before activating.');
        if (getPlanRows(plan.PlanId).length === 0) problems.push('The plan has no lines.');
        const certified = plan.CertifiedBudget;
        const planTotal = sumRows(getPlanRows(plan.PlanId));
        // Certified budget is optional for now; when one is set, the plan still can't exceed it
        const hasCertified = certified !== null && certified !== undefined && certified !== '';
        if (hasCertified && planTotal > Number(certified)) {
            problems.push(`The plan is ${usdAuth(planTotal - Number(certified))} over the certified budget of ${usdAuth(Number(certified))}.`);
        }
        return problems;
    };

    // Activate: load the participant's current POS and assignments, work out what's missing for the
    // plan's lines, show that, and write only it on confirm. Nothing is remembered in the browser, so
    // it works the same for anyone, any time: what already exists in .NET is never written again.
    const handleActivatePlan = async (plan) => {
        const problems = getActivationProblems(plan);
        if (problems.length > 0) {
            showActivationResult('Cannot activate yet', problems.map(p => `• ${p}`));
            return;
        }
        setActivatingPlan(plan.PlanId);
        let built;
        try {
            const [serviceTypes, burdenRateList, posData, assignedData] = await Promise.all([
                getBusinessServiceTypes(),
                getBurdenRates(),
                getAuthorizedServices(patientId),
                getPatientAssignedWorkers(patientId),
            ]);
            built = buildActivationRequests({
                rows: getPlanRows(plan.PlanId),
                serviceTypes,
                burdenRates: burdenRateList,
                multipliers: paymentModelMultipliers,
                existingPos: posData?.data || [],
                existingAssignments: assignedData?.data || [],
                patientId,
                startDate: plan.start_date,
                endDate: plan.end_date,
            });
        } catch (error) {
            showActivationResult('Activation failed', [`Could not load the current POS / assignments: ${describeFmsError(error)}`, 'Nothing was written.']);
            return;
        } finally {
            setActivatingPlan(null);
        }

        const { problems: buildProblems, posItems, assignmentItems, skippedAssignments } = built;
        if (buildProblems.length > 0) {
            showActivationResult('Cannot activate yet', [...buildProblems.map(p => `• ${p}`), '', 'Nothing was written.']);
            return;
        }
        const posToAdd = posItems.filter(p => !p.existing);
        const alreadyThere = [
            ...posItems.filter(p => p.existing).map(p => `• POS ${p.label} (${usdAuth(Number(p.existing.AuthorizedUnits) || 0)})`),
            ...skippedAssignments.map(s => `• Assignment ${s.label}`),
        ];
        if (posToAdd.length === 0 && assignmentItems.length === 0) {
            showActivationResult('Nothing to activate', ['Every POS and assignment on this plan already exists:', ...alreadyThere]);
            return;
        }
        // One preview row: a coloured POS / Assignment tag, the service, and the amount or provider
        const previewRow = (key, kind, service, detail, muted) => (
            <li key={key} className={`flex items-start gap-2 py-1.5 ${muted ? 'text-gray-500' : 'text-gray-800'}`}>
                <span className={`mt-0.5 shrink-0 w-24 text-center text-[11px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${
                    kind === 'pos'
                        ? (muted ? 'bg-blue-50 text-blue-400' : 'bg-blue-100 text-blue-800')
                        : (muted ? 'bg-purple-50 text-purple-400' : 'bg-purple-100 text-purple-800')
                }`}>
                    {kind === 'pos' ? 'POS' : 'Assignment'}
                </span>
                <span className="flex-1 text-sm leading-snug">{service}</span>
                <span className={`shrink-0 text-sm text-right ${muted ? '' : 'font-semibold'}`}>{detail}</span>
            </li>
        );
        const existingPos = posItems.filter(p => p.existing);
        setConfirmModalData({
            title: plan.IsActive ? 'Activate plan changes' : 'Activate spending plan',
            content: (
                <div className="text-left space-y-4">
                    <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                        <div className="text-sm font-semibold text-green-800 mb-1">
                            Will be added · {formatDateUS(plan.start_date)} – {formatDateUS(plan.end_date)}
                        </div>
                        <ul className="divide-y divide-green-100">
                            {posToAdd.map(p => previewRow(`pos-${p.label}`, 'pos', p.label, usdAuth(p.amount)))}
                            {assignmentItems.map((a, i) => previewRow(`asg-${i}`, 'assignment', a.serviceName, a.provider))}
                        </ul>
                    </div>
                    {(existingPos.length > 0 || skippedAssignments.length > 0) && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="text-sm font-semibold text-gray-600 mb-1">Already in place (left as is)</div>
                            <ul className="divide-y divide-gray-200">
                                {existingPos.map(p => previewRow(`epos-${p.label}`, 'pos', p.label, usdAuth(Number(p.existing.AuthorizedUnits) || 0), true))}
                                {skippedAssignments.map((s, i) => previewRow(`easg-${i}`, 'assignment', s.serviceName, s.provider, true))}
                            </ul>
                            {existingPos.length > 0 && (
                                <div className="text-xs text-gray-500 mt-1">Existing POS amounts aren't changed from here; update them in .NET if needed.</div>
                            )}
                        </div>
                    )}
                </div>
            ),
            variant: 'primary',
            confirmText: 'Activate',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setShowConfirmModal(false);
                await performActivatePlan(plan, posToAdd, assignmentItems);
            },
        });
        setShowConfirmModal(true);
    };

    const showActivationResult = (title, lines) => {
        setConfirmModalData({
            title,
            message: lines.join('\n'),
            confirmText: 'OK',
            cancelText: 'Close',
            onConfirm: () => setShowConfirmModal(false),
        });
        setShowConfirmModal(true);
    };

    // Writes what handleActivatePlan worked out was missing (front end, until the backend
    // activate-spending-plan endpoint exists): the POS first via add-pos, then the assignments via
    // add-worker-assignment. The calls are separate, so it stops at the first failure and reports
    // what was already written; activating again then picks up only what is still missing.
    const performActivatePlan = async (plan, posToAdd, assignmentItems) => {
        setActivatingPlan(plan.PlanId);
        const written = [];
        try {
            for (const item of posToAdd) {
                try {
                    const result = await addPos(item.request);
                    written.push(`• POS ${item.label}: ${usdAuth(item.amount)} added (#${result?.AuthorizeServiceID ?? '?'})`);
                } catch (error) {
                    showActivationResult('Activation stopped', [`POS ${item.label} failed: ${describeFmsError(error)}`, '', 'Already written:', ...(written.length ? written : ['• nothing'])]);
                    return;
                }
            }
            for (const item of assignmentItems) {
                try {
                    const result = await addWorkerAssignment(item.request);
                    written.push(`• Assignment ${item.label}: ${result?.alreadyExisted ? 'already assigned' : 'added'}`);
                } catch (error) {
                    showActivationResult('Activation stopped', [`Assignment ${item.label} failed: ${describeFmsError(error)}`, '', 'Already written:', ...written]);
                    return;
                }
            }

            // Everything written: mark the plan active
            try {
                await editSpendingPlan({
                    ...plan,
                    Name: plan.Name || plan.planName || '',
                    StartDate: plan.start_date || plan.StartDate,
                    EndDate: plan.end_date || plan.EndDate,
                    IsActive: 1,
                    BusinessId: businessId,
                    businessId: businessId,
                });
            } catch (error) {
                console.error('Error marking plan active:', error);
                written.push('• The plan itself could not be marked active; try Activate again.');
            }
            toast.success('Spending plan activated.');
            showActivationResult('Spending plan activated', written);
            const [plansData, authSvcs, workers] = await Promise.all([
                getSpendingPlans(patientId).catch(() => ({ data: [] })),
                getAuthorizedServices(patientId).catch(() => ({ data: [] })),
                getPatientAssignedWorkers(patientId).catch(() => ({ data: [] })),
            ]);
            if (plansData?.data?.length) setSpendingPlans(plansData.data);
            setAuthorizedServices(authSvcs?.data || []);
            setAssignedWorkers(workers?.data || []);
        } catch (error) {
            console.error('Error activating spending plan:', error);
            showActivationResult('Activation failed', [String(error?.message || error), '', 'Already written:', ...(written.length ? written : ['• nothing'])]);
        } finally {
            setActivatingPlan(null);
        }
    };

    // Service type name management functions
    const handleAddServiceType =(planId, sectionKey, rowId, originalServiceCode, currentDescription) => {
        // Get the original service description from service codes
        const serviceCodeOption = serviceCodeOptions.find(opt => 
            (opt.ServiceTypeID === originalServiceCode || opt.Name === originalServiceCode)
        );
        const originalServiceDescription = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || originalServiceCode || '';
        
        setServiceTypeForm({
            originalName: originalServiceDescription, // Show original service description in UI
            newName: currentDescription || originalServiceDescription, // Pre-fill with current or original
            operation: 'duplicate',
            serviceId: null,
            serviceCode: originalServiceCode, // Store service code for payload lookup
            planId: planId,
            sectionKey: sectionKey,
            rowId: rowId
        });
        setShowServiceTypeModal(true);
    };

    const handleEditServiceType = (planId, sectionKey, rowId, originalServiceCode, currentDescription) => {
        // Check if we have a stored ServiceID for this custom service type
        const customKey = `${planId}-${sectionKey}-${rowId}`;
        
        // ALWAYS check the row itself first for customServiceId (most reliable source)
        const row = apiSections?.[planId]?.[sectionKey]?.find(r => r.id === rowId);
        let customType = customServiceTypes[customKey];
        let hasStoredServiceId = false;
        let serviceId = null;

        // Priority 1: Check row's customServiceId field (persisted from database)
        if (row?.customServiceId) {
            serviceId = row.customServiceId;
            hasStoredServiceId = true;
            customType = { ...(customType || {}), serviceId: row.customServiceId };
        }
        
        // Priority 2: Check in-memory customServiceTypes mapping
        if (!hasStoredServiceId && customType?.serviceId) {
            serviceId = customType.serviceId;
            hasStoredServiceId = true;
        }

        // Priority 3: Search by serviceCode and current description within same plan/section
        if (!hasStoredServiceId) {
            const entries = Object.entries(customServiceTypes || {});
            for (const [, val] of entries) {
                if (!val) continue;
                const sameCode = String(val.serviceCode) === String(originalServiceCode);
                const nameMatches = val.customName === currentDescription || val.originalServiceDescription === currentDescription;
                if (sameCode && nameMatches) {
                    customType = val;
                    serviceId = val.serviceId;
                    hasStoredServiceId = Boolean(val.serviceId);
                    break;
                }
            }
        }
        
        console.log('Edit Service Type Debug:', {
            customKey,
            rowCustomServiceId: row?.customServiceId,
            customType,
            hasStoredServiceId,
            serviceId,
            willUseEditAPI: hasStoredServiceId
        });
        
        // For edit operations, show the current row description as the "original" that's being edited
        // and allow them to enter a new value
        const currentRowDescription = currentDescription || originalServiceCode || '';
        
        setServiceTypeForm({
            originalName: currentRowDescription, // Show current row description as what's being edited FROM
            newName: currentRowDescription, // Pre-fill with current value, user will change this TO new value
            operation: 'edit', // Always show "Edit" in UI when coming from edit icon
            serviceId: serviceId,
            serviceCode: originalServiceCode, // Store service code for API payload lookup
            planId: planId,
            sectionKey: sectionKey,
            rowId: rowId,
            useAddAPI: !hasStoredServiceId // Flag to determine API operation - false means use edit API
        });
        setShowServiceTypeModal(true);
    };

    const handleSaveServiceType = async () => {
        if (isSavingServiceType) return;
        
        try {
            setIsSavingServiceType(true);
            
            // Get the original service description from service codes
            const serviceCodeOption = serviceCodeOptions.find(opt => 
                (opt.ServiceTypeID === serviceTypeForm.serviceCode || opt.Name === serviceTypeForm.serviceCode)
            );
            const originalServiceDescription = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || serviceTypeForm.serviceCode || '';
            
            // Determine operation: use 'edit' if we have a serviceId, otherwise 'duplicate'
            const hasServiceId = Boolean(serviceTypeForm.serviceId);
            const operation = hasServiceId ? 'edit' : 'duplicate';
            
            const payload = {
                OriginalName: hasServiceId ? (serviceTypeForm.originalName || originalServiceDescription) : originalServiceDescription,
                NewName: serviceTypeForm.newName,
                Operation: operation
            };
            
            // Add ServiceId for edit operations
            if (hasServiceId) {
                payload.ServiceId = serviceTypeForm.serviceId;
                console.log('Using EDIT operation with ServiceId:', serviceTypeForm.serviceId);
            } else {
                console.log('Using DUPLICATE operation (no ServiceId available)');
            }
            
            // Call the API; log payload and response
            console.log('Saving service type with payload:', payload);
            const response = await addServiceTypeName(payload);
            console.log('Service type save response:', response);
            
            if (response?.success) {
                const customKey = `${serviceTypeForm.planId}-${serviceTypeForm.sectionKey}-${serviceTypeForm.rowId}`;
                
                // Store or update the custom service type
                if (!serviceTypeForm.serviceId && response.ServiceID) {
                    // Add operation - store the new ServiceID from add operation
                    console.log('Storing new ServiceID:', response.ServiceID, 'for key:', customKey);
                    setCustomServiceTypes(prev => {
                        const updated = {
                            ...prev,
                            [customKey]: {
                                serviceId: response.ServiceID,
                                originalServiceDescription: originalServiceDescription,
                                customName: serviceTypeForm.newName,
                                serviceCode: serviceTypeForm.serviceCode,
                                isCustomized: true
                            }
                        };
                        console.log('Updated customServiceTypes:', updated);
                        // Immediately save to localStorage
                        try {
                            localStorage.setItem('customServiceTypes', JSON.stringify(updated));
                        } catch (error) {
                            console.error('Error saving to localStorage:', error);
                        }
                        return updated;
                    });

                    // Also stamp the ServiceID onto the row so we can retrieve it later
                    setApiSections(prev => {
                        const rows = prev?.[serviceTypeForm.planId]?.[serviceTypeForm.sectionKey] || [];
                        const patched = rows.map(r => r.id === serviceTypeForm.rowId ? { ...r, customServiceId: response.ServiceID } : r);
                        return {
                            ...prev,
                            [serviceTypeForm.planId]: {
                                ...prev[serviceTypeForm.planId],
                                [serviceTypeForm.sectionKey]: patched
                            }
                        };
                    });

                    // If original service is EVV and row live-in is unchecked, toggle EVV immediately
                    try {
                        if (!isGuestMode && (sdpEntry || isOriginalServiceEVV(serviceTypeForm.serviceCode))) {
                            const rowRef = apiSections?.[serviceTypeForm.planId]?.[serviceTypeForm.sectionKey]?.find(r => r.id === serviceTypeForm.rowId);
                            if (rowRef && (sdpEntry ? isRowEvv(rowRef) : isLiveInUnchecked(rowRef.liveIn))) {
                                const sid = response.ServiceID;
                                if (sid) {
                                    const resp = await toggleServiceEvv(sid, 1);
                                    console.log('Immediate toggleServiceEvv response:', resp);
                                }
                            }
                        }
                    } catch (e) {
                        console.warn('Immediate toggleServiceEvv failed (non-blocking):', e);
                    }

                    // If this line already exists in the backend, persist the CustomServiceId silently
                    try {
                        const rowRef = apiSections?.[serviceTypeForm.planId]?.[serviceTypeForm.sectionKey]?.find(r => r.id === serviceTypeForm.rowId);
                        const existingLineId = rowRef?.originalLine?.LineId;
                        if (existingLineId) {
                            const persistPayload = {
                                LineId: existingLineId,
                                PlanId: serviceTypeForm.planId,
                                CustomServiceId: response.ServiceID
                            };
                            console.log('Persisting CustomServiceId to plan line:', persistPayload);
                            await editSpendingPlanLine(persistPayload);
                        }
                    } catch (e) {
                        console.warn('Failed to persist CustomServiceId on line (will remain in memory until next save):', e);
                    }
                } else if (serviceTypeForm.serviceId) {
                    // Edit operation - update existing custom service type
                    console.log('Updating existing custom service type for key:', customKey);
                    setCustomServiceTypes(prev => {
                        const updated = {
                            ...prev,
                            [customKey]: {
                                ...prev[customKey],
                                customName: serviceTypeForm.newName,
                                isCustomized: true
                            }
                        };
                        // Immediately save to localStorage
                        try {
                            localStorage.setItem('customServiceTypes', JSON.stringify(updated));
                        } catch (error) {
                            console.error('Error saving to localStorage:', error);
                        }
                        return updated;
                    });

                    // Ensure row retains the service id
                    if (serviceTypeForm.serviceId) {
                        setApiSections(prev => {
                            const rows = prev?.[serviceTypeForm.planId]?.[serviceTypeForm.sectionKey] || [];
                            const patched = rows.map(r => r.id === serviceTypeForm.rowId ? { ...r, customServiceId: serviceTypeForm.serviceId } : r);
                            return {
                                ...prev,
                                [serviceTypeForm.planId]: {
                                    ...prev[serviceTypeForm.planId],
                                    [serviceTypeForm.sectionKey]: patched
                                }
                            };
                        });
                    }
                }
                
                // Update the row's description with the new name
                handleApiChange(
                    serviceTypeForm.planId, 
                    serviceTypeForm.sectionKey, 
                    serviceTypeForm.rowId, 
                    'description', 
                    serviceTypeForm.newName
                );
                
                toast.success(`Service type ${serviceTypeForm.serviceId ? 'updated' : 'added'} successfully!`);
                setShowServiceTypeModal(false);
                
                // Auto-save the row to persist the changes to the database
                if (!isGuestMode) {
                    // Find the updated row and save it
                    const updatedRow = apiSections[serviceTypeForm.planId]?.[serviceTypeForm.sectionKey]?.find(r => r.id === serviceTypeForm.rowId);
                    if (updatedRow) {
                        // Wait a moment for state to update, then save
                        setTimeout(() => {
                            const chosenServiceId = serviceTypeForm.serviceId || response.ServiceID || updatedRow.customServiceId || null;
                            handleApiSave(serviceTypeForm.planId, serviceTypeForm.sectionKey, {
                                ...updatedRow,
                                description: serviceTypeForm.newName,
                                customServiceId: chosenServiceId
                            });
                        }, 100);
                    }
                }
            } else {
                toast.error('Failed to save service type. Please try again.');
            }
        } catch (error) {
            console.error('Error saving service type:', error);
            toast.error('Failed to save service type. Please try again.');
        } finally {
            setIsSavingServiceType(false);
        }
    };

    // const calcSubTotal = (rows) =>
    //     rows.reduce(
    //         (sum, r) => sum + (Number(r.frequency || 0) * Number(r.pricingUnit || 0)),
    //         0
    //     );

    // const calcGrandTotal = () =>
    //     calcSubTotal(sections.livingArrangements) + calcSubTotal(sections.healthAndSafety);

    // Get plans based on date ranges
    const getCurrentPlans = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day
        const matches = spendingPlans.filter(plan => {
            const startDate = new Date(plan.start_date);
            startDate.setHours(0, 0, 0, 0); // Set to start of day
            const endDate = new Date(plan.end_date);
            endDate.setHours(23, 59, 59, 999); // Set to end of day
            return today >= startDate && today <= endDate;
        });

        // Only one plan should ever represent "current" — if duplicate/overlapping plans
        // both cover today (e.g. leftover data), prefer the one matching the active
        // authorization (POS) range, falling back to the most recently started plan.
        if (matches.length <= 1) return matches;

        const currentPos = authorizedServices
            .filter(svc => svc.StartDate && svc.EndDate)
            .map(svc => {
                const start = new Date(svc.StartDate); start.setHours(0, 0, 0, 0);
                const end = new Date(svc.EndDate); end.setHours(23, 59, 59, 999);
                return { start, end };
            })
            .filter(({ start, end }) => today >= start && today <= end)
            .sort((a, b) => b.start - a.start)[0];

        if (currentPos) {
            const posStart = formatDateForApi(currentPos.start);
            const posEnd = formatDateForApi(currentPos.end);
            const posMatch = matches.find(plan => formatDateForApi(plan.start_date) === posStart && formatDateForApi(plan.end_date) === posEnd);
            if (posMatch) return [posMatch];
        }

        return [matches.reduce((best, plan) => (new Date(plan.start_date) > new Date(best.start_date) ? plan : best))];
    };

    const getPreviousPlans = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day
        return spendingPlans.filter(plan => {
            const endDate = new Date(plan.end_date);
            endDate.setHours(23, 59, 59, 999); // Set to end of day
            return endDate < today;
        });
    };

    // Parse date string to proper Date object, handling various formats
    const parseDate = (dateInput) => {
        if (!dateInput) return null;
        if (dateInput instanceof Date) return dateInput;
        
        // Handle ISO format (YYYY-MM-DD)
        if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}/)) {
            return new Date(dateInput);
        }
        
        // Handle DD/MM/YYYY format
        if (typeof dateInput === 'string' && dateInput.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
            const [day, month, year] = dateInput.split('/');
            return new Date(year, month - 1, day); // month is 0-indexed
        }
        
        // Handle MM/DD/YYYY format  
        if (typeof dateInput === 'string' && dateInput.includes('/')) {
            // Try parsing as MM/DD/YYYY first, then DD/MM/YYYY
            const parts = dateInput.split('/');
            if (parts.length === 3) {
                const [first, second, year] = parts;
                // If first part > 12, assume DD/MM/YYYY, otherwise MM/DD/YYYY
                if (parseInt(first) > 12) {
                    return new Date(year, second - 1, first); // DD/MM/YYYY
                } else {
                    return new Date(year, first - 1, second); // MM/DD/YYYY
                }
            }
        }
        
        // Fallback to standard Date parsing
        return new Date(dateInput);
    };

    // Convert date to ISO format (YYYY-MM-DD) for HTML date inputs
    // const dateToISO = (dateInput) => {
    //     if (!dateInput) return '';
    //     const date = parseDate(dateInput);
    //     if (!date || isNaN(date)) return '';
    //     return date.toISOString().split('T')[0];
    // };

    // Format a date for US display (MM/DD/YYYY)
    const formatDateUS = (dateInput) => {
        if (!dateInput) return '';
        const d = parseDate(dateInput);
        if (!d || isNaN(d)) return '';
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
    };

    // Parse a US date string (MM/DD/YYYY or M/D/YYYY) into ISO (YYYY-MM-DD) for storage
    // const parseDateUSToISO = (str) => {
    //     if (!str) return '';
    //     // Accept separators slash or hyphen
    //     const s = String(str).trim();
    //     const parts = s.includes('/') ? s.split('/') : s.split('-');
    //     if (parts.length !== 3) return '';
    //     let [m, d, y] = parts.map(p => p.trim());
    //     // If year is two digits, try to expand (assume 20xx)
    //     if (y.length === 2) y = `20${y}`;
    //     const mm = m.padStart(2, '0');
    //     const dd = d.padStart(2, '0');
    //     const iso = `${y}-${mm}-${dd}`;
    //     const dt = new Date(iso);
    //     if (isNaN(dt)) return '';
    //     return iso;
    // };

    // Use native date inputs (ISO strings) for calendar picker while formatting for display elsewhere

    const getFuturePlans = () => {
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of day
        return spendingPlans.filter(plan => {
            const startDate = new Date(plan.start_date);
            startDate.setHours(0, 0, 0, 0); // Set to start of day
            return startDate > today;
        });
    };

    // Plan management functions
    const handleAddPlan = async () => {
        if (isAddingPlan) return; // Prevent multiple calls
        
        try {
            setIsAddingPlan(true); // Set loading state
            
            if (isGuestMode) {
                // Guest mode - save to localStorage
                const newPlan = {
                    PlanId: Date.now(), // Use timestamp as unique ID for guest plans
                    Name: newPlanForm.planName || '',
                    //planName: newPlanForm.planName || '',
                    Year: new Date(newPlanForm.startDate).getFullYear() || new Date().getFullYear(),
                    IsActive: sdpEntry ? 0 : 1,
                    start_date: formatDateForApi(newPlanForm.startDate),
                    end_date: formatDateForApi(newPlanForm.endDate),
                    created_at: new Date().toISOString(),
                    isGuestPlan: true
                };
                if (sdpEntry && newPlanForm.totalBudget !== '') newPlan.CertifiedBudget = Number(newPlanForm.totalBudget) || 0;
                
                console.log('Adding new guest plan:', newPlan);
                
                // Update local state and localStorage
                const updatedPlans = [...spendingPlans, newPlan];
                setSpendingPlans(updatedPlans);
                // Open the newly added guest plan by default (add its index to expandedPlans)
                setExpandedPlans(prev => {
                    const s = new Set(prev);
                    s.add(updatedPlans.length - 1);
                    return s;
                });
                saveToLocalStorage(GUEST_PLANS_KEY, updatedPlans);
                
                // Initialize empty sections for the new plan
                const updatedSections = { 
                    ...apiSections, 
                    [newPlan.PlanId]: {
                        'livingarrangements': [],
                        'employmentandcommunityparticipation': [],
                        'healthandsafety': []
                    }
                };
                setApiSections(updatedSections);
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                
                toast.success('Spending plan created successfully in guest mode!');
            } else {
                // Authenticated mode - use API
                const payload = {
                    PatientId: patientId,
                    Name: newPlanForm.planName || '',
                    Year: new Date(newPlanForm.startDate).getFullYear() || new Date().getFullYear(),
                    // SDP plans stay a draft until Activate writes the authorizations
                    IsActive: sdpEntry ? 0 : 1,
                    StartDate: formatDateForApi(newPlanForm.startDate),
                    EndDate: formatDateForApi(newPlanForm.endDate),
                    BusinessId: businessId,
                    businessId: businessId
                };
                if (sdpEntry && newPlanForm.totalBudget !== '') payload.CertifiedBudget = Number(newPlanForm.totalBudget) || 0;

                // Attach CreatedByUserId when available from demo credentials or temporary window var
                try {
                    const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                    const createdBy = stored?.userId || window.__createdUserId || null;
                    if (createdBy) payload.CreatedByUserId = createdBy;
                } catch (e) {
                    // ignore
                }
                
                console.log('Adding new plan with payload:', payload);
                
                // Call add API endpoint
                const result = await addSpendingPlan(payload);
                console.log('Plan added successfully:', result);
                
                // Refresh spending plans after successful addition
                const plansData = await getSpendingPlans(patientId);
                const updatedPlans = plansData?.data || [];
                setSpendingPlans(updatedPlans);
                
                // Expand the newly added plan (last plan in the list)
                if (updatedPlans.length > 0) {
                    setExpandedPlans(prev => {
                        const s = new Set(prev);
                        s.add(updatedPlans.length - 1);
                        return s;
                    });
                }
                
                toast.success('Spending plan added successfully!');
            }
            
            // Close modal and reset form
            setShowAddPlanModal(false);
            setNewPlanForm({
                planName: '',
                startDate: '',
                endDate: '',
                totalBudget: '',
                description: ''
            });
            
        } catch (error) {
            console.error('Error adding plan:', error);
            toast.error('Failed to add spending plan. Please try again.');
        } finally {
            setIsAddingPlan(false); // Reset loading state
        }
    };

    // Authentication functions
    const handleAuth = async (e) => {
        e.preventDefault();
        if (isAuthenticating) return;
        
        setIsAuthenticating(true);
        
        try {
            if (authMode === 'register') {
                // Validate passwords match
                if (authForm.password !== authForm.confirmPassword) {
                    toast.error('Passwords do not match');
                    return;
                }
                
                // Validate date of birth (must be at least 18 years old)
                if (authForm.dateOfBirth) {
                    const birthDate = new Date(authForm.dateOfBirth);
                    const today = new Date();
                    const age = today.getFullYear() - birthDate.getFullYear();
                    // const monthDiff = today.getMonth() - birthDate.getMonth();
                    
                    // if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    //     age--;
                    // }
                    
                    if (age < 18) {
                        toast.error('You must be at least 18 years old to register');
                        return;
                    }
                }
                
                // Prospect registration: call getBusinessList and addPatient
                let createdPatientId = null;
                try {
                    console.log('Registering prospect user (local) for addPatient:', {
                        email: authForm.email,
                        firstName: authForm.firstName,
                        lastName: authForm.lastName,
                        dateOfBirth: authForm.dateOfBirth,
                        ankotaStatus: 'prospect'
                    });

                    // Fetch the business list data to include in payload
                    let businessListData = [];
                    try {
                        businessListData = await getBusinessList(businessId);
                        console.log('getBusinessList returned:', businessListData);
                    } catch (err) {
                        console.warn('getBusinessList failed, proceeding with empty list:', err);
                        businessListData = [];
                    }

                    // Build payload per sample. businessId must be 1 in all cases.
                    // Ensure patientStatus is passed as 4 (Prospect) when not provided
                    const patientStatusValue = (authForm.patientStatus !== undefined && authForm.patientStatus !== null && authForm.patientStatus !== '')
                        ? Number(authForm.patientStatus)
                        : 4;

                    // Ensure birthDate is passed in YYYY-MM-DD format as expected by the backend
                    const birthDateValue = authForm.dateOfBirth
                        ? new Date(authForm.dateOfBirth).toISOString().slice(0, 10)
                        : '';

                    const patientPayload = {
                        businessList: typeof businessListData === 'string' ? businessListData : JSON.stringify(businessListData),
                        businessId: businessId,
                        firstName: authForm.firstName || '',
                        lastName: authForm.lastName || '',
                        homePhone: authForm.homePhone || '555-123-5555',
                        address1: authForm.address1 || '',
                        ssn: authForm.ssn || '',
                        birthDate: birthDateValue,
                        patientStatus: patientStatusValue
                    };

                    console.log('Calling addPatient with payload:', patientPayload);
                    const addPatientResult = await addPatient(patientPayload);
                    console.log('addPatient result:', addPatientResult);

                    // Try to extract the newly created PatientID from the response
                    if (addPatientResult) {
                        createdPatientId = addPatientResult?.PatientID ?? addPatientResult?.PatientId ?? addPatientResult?.PatientID ?? addPatientResult?.data?.PatientID ?? null;
                        if (!createdPatientId) {
                            // Fallback: try to parse any numeric ID in the returned message
                            try {
                                const text = JSON.stringify(addPatientResult);
                                const m = text.match(/\b(\d{4,})\b/);
                                if (m) createdPatientId = Number(m[1]);
                            } catch (e) { /* ignore */ }
                        }
                    }

                    // If we have an ID, call the save-user and save-patient-contact endpoints
                    if (createdPatientId) {
                        try {
                            const userPayload = {
                                UserID: 0,
                                UserName: authForm.email || authForm.contactEmail || '',
                                Password: authForm.password || '',
                                MemberId: Number(createdPatientId),
                                // Include BusinessID for proper association when backend supports it
                                BusinessID: businessId ? Number(businessId) : undefined,
                                businessId: businessId ? Number(businessId) : undefined
                            };
                            console.log('Calling saveUser with payload:', userPayload);
                                    const saveUserResult = await saveUser(userPayload);
                                    console.log('saveUser result:', saveUserResult);
                                    // Try to extract created UserID and save it for later use
                                    const createdUserId = saveUserResult?.UserID ?? saveUserResult?.UserId ?? saveUserResult?.data?.UserID ?? saveUserResult?.data?.UserId ?? null;
                                    if (createdUserId) {
                                        console.log('Captured createdUserId from saveUser:', createdUserId);
                                        // attach to authForm/store later when persisting demo credentials
                                        // We'll merge into userCredentials below
                                        // keep local reference for debugging
                                        window.__createdUserId = createdUserId;
                                    }
                        } catch (err) {
                            console.error('saveUser failed:', err);
                        }

                        try {
                            const nowIso = new Date().toISOString();

                            // Build contact payload but only include fields that were actually provided
                            // Avoid sending placeholder/default values (no "Primary emergency contact", no default BusinessID=90, no AddBy/ModBy 'Admin')
                            const contactPayload = {
                                PatientID: Number(createdPatientId),
                                OperationType: 0,
                                AddDate: nowIso,
                                ModDate: nowIso
                            };

                            if (authForm.contactFirstName) contactPayload.FirstName = authForm.contactFirstName;
                            if (authForm.contactLastName) contactPayload.LastName = authForm.contactLastName;
                            if (authForm.contactEmail) contactPayload.Email = authForm.contactEmail;
                            if (authForm.contactMobilePhone) contactPayload.MobilePhone = authForm.contactMobilePhone;
                            if (authForm.contactAddress1) contactPayload.Address1 = authForm.contactAddress1;
                            if (authForm.contactCity) contactPayload.City = authForm.contactCity;
                            if (authForm.contactState) contactPayload.State = authForm.contactState;
                            if (authForm.contactPostalCode) contactPayload.PostalCode = authForm.contactPostalCode;
                            if (authForm.contactRelationshipId) contactPayload.PatientContactRelationshipID = Number(authForm.contactRelationshipId);
                            if (authForm.contactNote) contactPayload.Note = authForm.contactNote;
                            // Only include BusinessID when a valid businessId is present
                            if (businessId) contactPayload.BusinessID = Number(businessId);

                            console.log('Calling savePatientContact with payload (no dummy/defaults):', contactPayload);
                            const saveContactResult = await savePatientContact(contactPayload);
                            console.log('savePatientContact result:', saveContactResult);
                        } catch (err) {
                            console.error('savePatientContact failed:', err);
                        }

                        toast.success(`Registration successful! Patient created.`);
                    } else {
                        console.warn('Could not determine created PatientID from addPatient result:', addPatientResult);
                        toast.error('Registration not successful.');
                    }

                } catch (err) {
                    console.error('Error during addPatient flow:', err);
                    toast.error('Registration failed while creating patient. Please try again.');
                    return;
                }

                // For demo/backwards compatibility: Store credentials in localStorage to simulate account creation
                // Persist demo credentials including created patientId and optionally created user id
                const storedUserId = window.__createdUserId || null;
                const userCredentials = {
                    email: authForm.email,
                    password: authForm.password,
                    firstName: authForm.firstName,
                    lastName: authForm.lastName,
                    dateOfBirth: authForm.dateOfBirth,
                    // Only persist a patientId if the backend returned one. Avoid fabricating IDs locally.
                    patientId: (typeof createdPatientId !== 'undefined' && createdPatientId) ? createdPatientId : null,
                    userId: storedUserId // may be null if saveUser did not return one
                };
                localStorage.setItem('demoUserCredentials', JSON.stringify(userCredentials));

                // If we created a patient on the backend and there are guest plans in memory,
                // migrate those guest plans and their lines to the server under the newly created patient.
                if (createdPatientId) {
                    (async () => {
                        try {
                            const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                            const createdBy = stored?.userId || window.__createdUserId || null;

                            // Find guest plans that were created in-memory (isGuestPlan flag)
                            const guestPlans = (spendingPlans || []).filter(p => p.isGuestPlan);
                            if (guestPlans.length > 0) {
                                toast.success('Migrating your saved guest plans to your new account...');
                            }

                            // helper to map section key back to plan_type
                            const planTypeFromSection = (sectionKey) => {
                                const map = {
                                    'livingarrangements': 'Living Arrangements',
                                    'employmentandcommunityparticipation': 'Employment and Community Participation',
                                    'healthandsafety': 'Health and Safety'
                                };
                                return map[sectionKey] || 'Other';
                            };

                            for (const guestPlan of guestPlans) {
                                try {
                                    const planPayload = {
                                        PatientId: createdPatientId,
                                        Name: guestPlan.Name || guestPlan.planName || '',
                                        Year: new Date(guestPlan.start_date).getFullYear() || new Date().getFullYear(),
                                        IsActive: guestPlan.IsActive ?? (sdpEntry ? 0 : 1),
                                        ...(guestPlan.CertifiedBudget !== undefined ? { CertifiedBudget: guestPlan.CertifiedBudget } : {}),
                                        StartDate: formatDateForApi(guestPlan.start_date || guestPlan.StartDate) || new Date().toISOString().split('T')[0],
                                        EndDate: formatDateForApi(guestPlan.end_date || guestPlan.EndDate) || new Date().toISOString().split('T')[0],
                                        BusinessId: businessId,
                                        businessId: businessId
                                    };
                                    if (createdBy) planPayload.CreatedByUserId = createdBy;

                                    const addedPlanResult = await addSpendingPlan(planPayload);
                                    // Try to extract the new plan id from common fields
                                    const newPlanId = addedPlanResult?.PlanId || addedPlanResult?.PlanID || addedPlanResult?.data?.PlanId || addedPlanResult?.data?.PlanID || null;
                                    if (!newPlanId) {
                                        console.warn('Could not determine new PlanId from addSpendingPlan result:', addedPlanResult);
                                        continue; // skip migrating lines for this plan
                                    }

                                    // Migrate lines for this guest plan from apiSections state
                                    const planSections = apiSections?.[guestPlan.PlanId] || {};
                                    for (const [sectionKey, rows] of Object.entries(planSections)) {
                                        for (const row of (rows || [])) {
                                            try {
                                                const linePayload = {
                                                    PlanId: newPlanId,
                                                    ServiceCode: row.serviceCode || '',
                                                    Provider: row.provider || '',
                                                    LiveIn: row.liveIn || false,
                                                    ServiceDescription: row.description || row.serviceCode || '',
                                                    IPPGoal: row.ippGoal || '',
                                                    BasePay: Number(row.basePay) || 0,
                                                    PricingUnit: Number(row.pricingUnit) || 0,
                                                    AnnualFrequency: Number(row.frequency) || 0,
                                                    TotalAnnualCost: Number(row.annualCost) || (Number(row.pricingUnit) * Number(row.frequency)) || 0,
                                                    AllocatedBudget: row.allocatedBudget !== null && row.allocatedBudget !== undefined ? Number(row.allocatedBudget) : null,
                                                    plan_type: planTypeFromSection(sectionKey),
                                                    PaymentModel: row.paymentModel || 'Bill Payer',
                                                    CustomServiceId: row.customServiceId || null,
                                                    BusinessId: businessId,
                                                    //businessId: businessId
                                                };
                                                if (createdBy) linePayload.CreatedByUserId = createdBy;
                                                await addSpendingPlanLine(linePayload);
                                            } catch (err) {
                                                console.error('Failed to migrate plan line:', err, row);
                                            }
                                        }
                                    }
                                } catch (err) {
                                    console.error('Failed to migrate guest plan:', err, guestPlan);
                                }
                            }

                            // Refresh plans for the new patient so the UI shows the migrated plans
                            try {
                                const plansData = await getSpendingPlans(createdPatientId);
                                const updatedPlans = plansData?.data || [];
                                setSpendingPlans(updatedPlans);
                                // refresh lines
                                await refreshPlanData();
                                toast.success('Guest plans migrated successfully.');
                            } catch (err) {
                                console.error('Failed to refresh plans after migration:', err);
                            }
                        } catch (err) {
                            console.error('Error during guest plan migration:', err);
                        }
                    })();
                }

                setAuthMode('login');
                // Reset the form after successful registration, keep email for convenience
                setAuthForm({
                    email: authForm.email, // Keep email for convenience
                    password: '',
                    firstName: '',
                    lastName: '',
                    confirmPassword: '',
                    dateOfBirth: ''
                });
                // DOB cleared via authForm reset above
            } else {
                // Login mode for prospects
                console.log('Logging in prospect user:', authForm.email);

                // Try API login first
                try {
                    const loginResult = await login({ username: authForm.email, password: authForm.password });
                    console.log('login result:', loginResult);

                    // Determine success/token
                    const token = loginResult?.token || loginResult?.accessToken || loginResult?.Token || loginResult?.tokenString || null;
                    // Some login endpoints return UserID/MemberID instead of a token — treat those as success as well
                    const hasId = Boolean(loginResult?.UserID || loginResult?.UserId || loginResult?.MemberID || loginResult?.MemberId || loginResult?.PatientID || loginResult?.PatientId);
                    const isSuccess = Boolean(token) || Boolean(loginResult?.success) || Boolean(loginResult?.isAuthenticated) || Boolean(loginResult?.User) || hasId;

                    if (isSuccess) {
                        if (token) localStorage.setItem('authToken', token);
                        toast.success(`Login successful! Welcome back.`);
                        setShowAuthModal(false);

                        setAuthForm({
                            email: '',
                            password: '',
                            firstName: '',
                            lastName: '',
                            confirmPassword: '',
                            dateOfBirth: ''
                        });

                        // If the API returned a patient/member id or user id, redirect so App picks it up and loads user data
                        try {
                            const returnedPatientId = loginResult?.MemberID || loginResult?.MemberId || loginResult?.PatientID || loginResult?.PatientId || null;
                            const returnedUserId = loginResult?.UserID || loginResult?.UserId || null;
                            if (returnedPatientId) {
                                try {
                                    const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                                    const merged = {
                                        ...stored,
                                        patientId: returnedPatientId,
                                        userId: returnedUserId || stored.userId || null
                                    };
                                    localStorage.setItem('demoUserCredentials', JSON.stringify(merged));
                                    // Reload so App.js picks up the stored credentials
                                    window.location.reload();
                                } catch (e) {
                                    console.error('Failed to persist credentials after login:', e);
                                }
                            }
                        } catch (e) {
                            // ignore redirect errors
                        }
                        // DOB cleared via authForm reset above
                    } else {
                        // If API returned but did not indicate success, fallback to demo credentials
                        const storedCredentials = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                            if (storedCredentials.email === authForm.email && storedCredentials.password === authForm.password) {
                            toast.success(`Login successful! Welcome back, ${storedCredentials.firstName}! Your plan has been saved permanently and you can now edit it.`);
                            setShowAuthModal(false);
                            setAuthForm({ email: '', password: '', firstName: '', lastName: '', confirmPassword: '', dateOfBirth: '' });
                            // If we have a stored demo patientId, redirect so App picks it up and switches out of guest mode
                            if (storedCredentials.patientId) {
                                try {
                                    const merged = {
                                        ...storedCredentials,
                                        patientId: storedCredentials.patientId,
                                        userId: storedCredentials.userId || null
                                    };
                                    localStorage.setItem('demoUserCredentials', JSON.stringify(merged));
                                    window.location.reload();
                                } catch (e) {
                                    console.error('Failed to persist demo credentials during login fallback:', e);
                                }
                            }
                            // DOB cleared via authForm reset above
                        } else {
                            toast.error('Invalid email or password. Please check your credentials or register first.');
                            return;
                        }
                    }
                } catch (err) {
                    console.error('Login API failed, falling back to demo credentials:', err);
                    // Fallback to stored demo credentials
                    const storedCredentials = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                    if (storedCredentials.email === authForm.email && storedCredentials.password === authForm.password) {
                        toast.success(`Login successful! Welcome back, ${storedCredentials.firstName}! Your plan has been saved permanently and you can now edit it.`);
                        setShowAuthModal(false);
                        setAuthForm({ email: '', password: '', firstName: '', lastName: '', confirmPassword: '', dateOfBirth: '' });
                        if (storedCredentials.patientId) {
                            try {
                                const merged = {
                                    ...storedCredentials,
                                    patientId: storedCredentials.patientId,
                                    userId: storedCredentials.userId || null
                                };
                                localStorage.setItem('demoUserCredentials', JSON.stringify(merged));
                                window.location.reload();
                            } catch (e) {
                                console.error('Failed to persist demo credentials during login fallback (catch):', e);
                            }
                        }
                        // DOB cleared via authForm reset above
                    } else {
                        toast.error('Invalid email or password. Please check your credentials or register first.');
                        return;
                    }
                }
            }
        } catch (error) {
            console.error('Authentication error:', error);
            toast.error('Authentication failed. Please try again.');
        } finally {
            setIsAuthenticating(false);
        }
    };
    
    // const handleAuthFormChange = (field, value) => {
    //     setAuthForm(prev => ({
    //         ...prev,
    //         [field]: value
    //     }));
    // };


    // Toggle plan accordion
    // Ensure plan lines are loaded when a plan is expanded
    const ensurePlanLinesLoaded = async (planId) => {
        try {
            if (apiSections[planId] && Object.keys(apiSections[planId]).length > 0) {
                return;
            }
            setPlanLinesLoading(prev => ({ ...prev, [planId]: true }));
            const linesData = await getSpendingPlanLines(planId);
            const lines = linesData?.data || [];

            const groupedByPlanType = lines.reduce((acc, line) => {
                const planType = line.plan_type || 'Other';
                const sectionKey = planType.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');
                if (!acc[sectionKey]) acc[sectionKey] = [];

                const basePay = parseFloat(line.BasePay) || 0;
                const paymentModel = line.PaymentModel || 'Bill Payer';
                // Vendor (SDP) lines have no base pay/burden; their invoice amount is the pricing unit
                const lineProviderType = line.ProviderType || getRememberedLineProviderType(line.LineId) || getLocalProviderType(line.Provider || line.ProviderName);
                    const correctPricingUnit = lineProviderType === PROVIDER_TYPE_VENDOR
                    ? (parseFloat(line.PricingUnit) || 0)
                    : basePay * (paymentModelMultipliers[paymentModel] || 1);
                const allocatedBudgetValue = (line.AllocatedBudget !== null && line.AllocatedBudget !== undefined) ? parseFloat(line.AllocatedBudget) : null;

                acc[sectionKey].push({
                    id: line.LineId,
                    serviceCode: line.ServiceCode || '',
                    provider: line.Provider || line.ProviderName || '',
                    workerId: line.WorkerId || null,
                    providerType: lineProviderType,
                    sdpEntered: isSdpEnteredLine(line),
                    serviceId: line.ServiceId || null,
                    liveIn: line.LiveIn || false,
                    description: line.ServiceDescription || line.Description || '',
                    ippGoal: line.IPPGoal || '',
                    basePay: basePay,
                    paymentModel: paymentModel,
                    pricingUnit: correctPricingUnit,
                    frequency: parseFloat(line.AnnualFrequency) || 0,
                    annualCost: parseFloat(line.TotalAnnualCost) || 0,
                    allocatedBudget: allocatedBudgetValue,
                    customServiceId: line.CustomServiceId || null,
                    isApi: true,
                    planId: planId,
                    originalLine: line
                });
                return acc;
            }, {});

            setApiSections(prev => ({ ...prev, [planId]: groupedByPlanType }));
        } catch (error) {
            console.error('Error loading plan lines for planId', planId, error);
        } finally {
            setPlanLinesLoading(prev => ({ ...prev, [planId]: false }));
        }
    };

    // Pay/bill rates + actual billed cost from payroll, keyed by normalized worker name + service
    // (a worker can be tied to more than one service, each with its own rate/cost) — fetched once
    // per plan, from the plan's start date through the end of the current month (not the full
    // plan/auth range, which can run a year out and made this call very slow) — capped at the
    // plan's real end date for plans that already ended. Workers have a real PayRate; vendors have
    // PayRate 0 but still have a real BillRate (used for Pricing Unit). ChargeAmount doesn't
    // reliably reduce to BillRate * hours for either role (billing codes/special rates affect it),
    // so rather than trying to recompute it, we sum the real ChargeAmount across all matching
    // visits and use that directly as Total Annual Cost for both workers and vendors.
    const ensurePayrollRatesLoaded = async (plan) => {
        if (!plan || !patientId || payrollRatesLoadedForPlan === plan.PlanId) return;
        try {
            const now = new Date();
            const endOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            const planEndDate = plan.end_date ? new Date(plan.end_date) : null;
            const effectiveEndDate = planEndDate && planEndDate < endOfCurrentMonth ? planEndDate : endOfCurrentMonth;
            const data = await getPayrollTransactionsByServiceOrder(patientId, plan.start_date, formatDateForApi(effectiveEndDate));
            // Localhost only: ?debugPayroll=1 prints what payroll returned, per provider + service, to compare with POS Spent
            if (isLocalhost && new URLSearchParams(window.location.search).has('debugPayroll')) {
                console.log('[debugPayroll] range', plan.start_date, '→', formatDateForApi(effectiveEndDate));
                console.table((data?.serviceOrders || []).map(so => {
                    const soRows = so.rows || [];
                    return {
                        worker: so.WorkerName,
                        service: so.ServiceName,
                        matchKey: `${normalizeNameForMatch(so.WorkerName)}|${getPayrollServiceKey(so.ServiceName)}`,
                        visits: soRows.length,
                        chargeTotal: soRows.reduce((s, r) => s + (Number(r.ChargeAmount) || 0), 0),
                        hours: soRows.reduce((s, r) => s + (Number(r.BillableTime) || 0), 0) / 60,
                        payRate: soRows[0]?.PayRate,
                        billRate: soRows[0]?.BillRate,
                    };
                }));
                if (data?.serviceOrders?.[0]?.rows?.[0]) console.log('[debugPayroll] fields on a visit row:', Object.keys(data.serviceOrders[0].rows[0]));
            }
            const rates = {};
            // Service orders carry no worker id; the visits list ties each one to its worker
            const workerIdByServiceOrder = {};
            (data?.visits || []).forEach(v => { if (v.ServiceOrderID && v.WorkerId) workerIdByServiceOrder[v.ServiceOrderID] = v.WorkerId; });
            (data?.serviceOrders || []).forEach(so => {
                const nameKey = normalizeNameForMatch(so.WorkerName);
                const serviceKey = getPayrollServiceKey(so.ServiceName);
                if (!nameKey || !serviceKey) return;
                const key = `${nameKey}|${serviceKey}`;
                if (!rates[key]) rates[key] = { payRate: undefined, billRate: undefined, totalChargeAmount: 0, totalMinutes: 0, workerCost: 0, workerId: null };
                if (!rates[key].workerId && workerIdByServiceOrder[so.ServiceOrderID]) rates[key].workerId = workerIdByServiceOrder[so.ServiceOrderID];
                (so.rows || []).forEach(r => {
                    if (rates[key].payRate === undefined && r.PayRate !== undefined && r.PayRate !== null) {
                        rates[key].payRate = Number(r.PayRate) || 0;
                        rates[key].billRate = Number(r.BillRate) || 0;
                    }
                    rates[key].totalChargeAmount += Number(r.ChargeAmount) || 0;
                    rates[key].totalMinutes += Number(r.BillableTime) || 0;
                    // Worker cost: each visit's own rate x its own hours
                    rates[key].workerCost += (Number(r.BillRate) || 0) * ((Number(r.BillableTime) || 0) / 60);
                });
            });
            setPayrollRates(rates);
            setPayrollRatesLoadedForPlan(plan.PlanId);
        } catch (error) {
            console.error('Error loading payroll rates for planId', plan.PlanId, error);
        }
    };

    // Payroll figures for a row's provider + service code (keyed by code, so the row's own code works
    // when there is no POS authorization to take the service name from)
    const getRowPayrollEntry = (row, svc) => {
        const serviceName = svc?.ServiceTypeName || row.serviceCode;
        return serviceName ? payrollRates[getPayrollRateKey(row.provider, serviceName)] : undefined;
    };

    // Worker/Vendor from each provider's profile (the company list behind the provider dropdown),
    // by worker id, then by name
    const providerTypeLookup = (() => {
        const byId = new Map();
        const byName = new Map();
        (companyProviders || []).forEach(p => {
            if (p.workerId) byId.set(String(p.workerId), p.providerType);
            const nameKey = normalizeNameForMatch(p.name);
            if (nameKey) byName.set(nameKey, p.providerType);
        });
        return { byId, byName };
    })();

    const getProfileProviderType = (workerId, providerName) => {
        if (workerId && providerTypeLookup.byId.has(String(workerId))) return providerTypeLookup.byId.get(String(workerId));
        return providerTypeLookup.byName.get(normalizeNameForMatch(providerName)) || null;
    };

    // A row's Worker/Vendor: picked in the provider dropdown, else the provider's profile, else (only
    // when the provider isn't in the company list) a guess from payroll
    const getRowProviderType = (row, payrollEntry) => {
        if (row.providerType && row.providerTypeSource !== 'payroll') return row.providerType;
        return getProfileProviderType(row.workerId || payrollEntry?.workerId, row.provider)
            || inferProviderTypeFromPayroll(payrollEntry);
    };

    // Cost from payroll: vendor = summed ChargeAmount; worker = each visit's rate x its hours.
    // Unknown type = ChargeAmount, the amount actually charged.
    const getPayrollCost = (payrollEntry, providerType) => {
        if (!payrollEntry) return 0;
        if (providerType === PROVIDER_TYPE_DSW) return Number(payrollEntry.workerCost) || 0;
        return Number(payrollEntry.totalChargeAmount) || 0;
    };

    // Total Annual Cost as shown on a row: Pricing Unit x Frequency, falling back to payroll when no rate is saved
    const getRowDisplayCost = (row, svc) => {
        const frequency = Number(row.frequency || 1);
        if (row.pricingUnit) return Number(row.pricingUnit) * frequency;
        const payrollEntry = getRowPayrollEntry(row, svc);
        return getPayrollCost(payrollEntry, getRowProviderType(row, payrollEntry)) * frequency;
    };

    // Hours worked from payroll (BillableTime) for a worker row; null for a vendor (not paid by the
    // hour) or a provider with no payroll yet
    const getRowPayrollHours = (row, svc) => {
        const payrollEntry = getRowPayrollEntry(row, svc);
        if (!payrollEntry || getRowProviderType(row, payrollEntry) !== PROVIDER_TYPE_DSW) return null;
        return (Number(payrollEntry.totalMinutes) || 0) / 60;
    };

    const renderPayrollHours = (row, svc) => {
        const hours = getRowPayrollHours(row, svc);
        return hours === null
            ? <span className="text-gray-400" title="Suppliers are not paid by the hour">—</span>
            : <span title="Hours worked, from payroll">{hours.toFixed(2)}</span>;
    };

    const sumPayrollHours = (rows, svc) => (rows || []).reduce((sum, r) => sum + (getRowPayrollHours(r, svc) || 0), 0);

    const sumDisplayRows = (rows) => (rows || []).reduce((sum, r) => sum + getRowDisplayCost(r), 0);

    // SDP: once the burden rates load, a worker line's Rate is Base Pay x (1 + its burden %), so
    // lines saved with another multiplier show the company's rate (saved on the next SAVE)
    useEffect(() => {
        if (!sdpEntry || burdenRates.length === 0) return;
        setApiSections(prev => {
            let changed = false;
            const next = {};
            Object.entries(prev || {}).forEach(([planId, sections]) => {
                next[planId] = {};
                Object.entries(sections || {}).forEach(([sectionKey, rows]) => {
                    next[planId][sectionKey] = (rows || []).map(r => {
                        if (isVendorRow(r) || !(Number(r.basePay) > 0)) return r;
                        const pricingUnit = roundCents(Number(r.basePay) * getModelMultiplier(r.paymentModel || 'Bill Payer'));
                        if (pricingUnit === Number(r.pricingUnit)) return r;
                        changed = true;
                        return { ...r, pricingUnit, annualCost: pricingUnit * (Number(r.frequency) || 0) };
                    });
                });
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdpEntry, burdenRates, apiSections]);

    // The company's provider list (each with its profile's Worker/Vendor type), once business login has
    // stored this company's token — without it the list would come back for another business
    useEffect(() => {
        if (!isBusinessLoginComplete || isGuestMode || companyProviders) return;
        let hasBusinessToken = false;
        try { hasBusinessToken = !!localStorage.getItem('businessAuthToken'); } catch { /* ignore */ }
        if (!hasBusinessToken) return;
        let cancelled = false;
        loadAssignableWorkers().then(list => { if (!cancelled && list.length > 0) setCompanyProviders(list); });
        return () => { cancelled = true; };
    }, [isBusinessLoginComplete, isGuestMode, companyProviders]);

    // SDP: existing lines saved without a provider type get Worker/Vendor from the provider's profile
    // (or, if the provider isn't in the company list, from payroll — marked so the tag can say so);
    // picking a provider replaces it
    useEffect(() => {
        if (!sdpEntry || !payrollRatesLoadedForPlan) return;
        const planId = payrollRatesLoadedForPlan;
        setApiSections(prev => {
            const sections = prev?.[planId];
            if (!sections) return prev;
            let changed = false;
            const patched = {};
            Object.entries(sections).forEach(([sectionKey, rows]) => {
                patched[sectionKey] = (rows || []).map(r => {
                    if (r.providerType && r.providerTypeSource !== 'payroll') return r;
                    const payrollEntry = getRowPayrollEntry(r);
                    const fromProfile = getProfileProviderType(r.workerId || payrollEntry?.workerId, r.provider);
                    const providerType = fromProfile || (r.providerType ? null : inferProviderTypeFromPayroll(payrollEntry));
                    const providerTypeSource = fromProfile ? 'profile' : 'payroll';
                    if (!providerType || (providerType === r.providerType && providerTypeSource === r.providerTypeSource)) return r;
                    changed = true;
                    return { ...r, providerType, providerTypeSource };
                });
            });
            return changed ? { ...prev, [planId]: patched } : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdpEntry, payrollRates, payrollRatesLoadedForPlan, apiSections, companyProviders]);

    const togglePlanExpansion = async (index, plan) => {
        const planId = plan.PlanId;
        setExpandedPlans(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(index)) {
                newExpanded.delete(index);
            } else {
                newExpanded.add(index);
            }
            return newExpanded;
        });
        // If expanding, trigger lazy load in background
        if (!expandedPlans.has(index)) {
            ensurePlanLinesLoaded(planId);
            ensurePayrollRatesLoaded(plan);
        }
    };

    const handlePlanFormChange = (field, value) => {
        setNewPlanForm(prev => {
            const updated = { ...prev, [field]: value };
            // SDP plans run a year: default the end date (still editable for mid-year switches)
            if (sdpEntry && field === 'startDate' && value) updated.endDate = getDefaultPlanEndDate(value);
            return updated;
        });
    };

    // Filter rows based on search query
    const filterRowsBySearch = (rows) => {
        if (!searchQuery || searchQuery.trim() === '') {
            return rows;
        }

        const query = searchQuery.toLowerCase().trim();
        
        return rows.filter(row => {
            // Get the service description from service code options
            const serviceCodeOption = serviceCodeOptions.find(opt => 
                (opt.ServiceTypeID === row.serviceCode || opt.Name === row.serviceCode)
            );
            const serviceDescription = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || row.serviceCode || '';
            
            // Search across multiple fields
            const searchableFields = [
                String(row.serviceCode || ''),
                String(row.provider || ''),
                String(row.description || ''),
                String(serviceDescription),
                String(row.ippGoal || '')
            ];
            
            return searchableFields.some(field => 
                field.toLowerCase().includes(query)
            );
        });
    };

    // Render plan lines data with interactive functionality (same as original tables)
    const renderPlanLinesData = (tabType) => {
        let filteredPlans = [];
        
        switch(tabType) {
            case 'previous':
                filteredPlans = getPreviousPlans();
                break;
            case 'current':
                filteredPlans = getCurrentPlans();
                break;
            case 'future':
                filteredPlans = getFuturePlans();
                break;
            default:
                return null;
        }

        if (filteredPlans.length === 0) {
            // Show empty state when no plans exist (guest mode or authenticated user with no plans)
            if (spendingPlans.length === 0) {
                return (
                    <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Your First Spending Plan</h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                            {isGuestMode 
                                ? "Start by creating a spending plan to organize your services and budget. You can fill it out, print it, and save it for future editing."
                                : "You don't have any spending plans yet. Create your first plan to start organizing your services and budget."
                            }
                        </p>
                        <button
                            onClick={() => setShowAddPlanModal(true)}
                            className="bg-primary-dark text-white px-6 py-3 rounded-lg shadow hover:shadow-lg transition-all duration-200 font-medium"
                        >
                            Create Your First Plan
                        </button>
                    </div>
                );
            }
            return null;
        }

        // Map plan_type to section titles with service code ranges
        const sectionTitles = {
            'livingarrangements': 'Living Arrangements (310–330)',
            'employmentandcommunityparticipation': 'Employment and Community Participation (331–355)', 
            'healthandsafety': 'Health and Safety (356–399)'
        };

        // The assigned workers shown as suggestion rows under an authorization: active, matched by
        // service type (or code), one per worker (the backend can return several assignment rows for
        // the same person/service), and not already saved as a line for that service. Saved lines are
        // searched across the whole section and matched on service AND worker, so a worker saved under
        // one service still gets a suggestion under another. Shared by the rows and the Section Total.
        const getShownAuthWorkers = (svc, rows) => {
            // SDP: providers are picked on the plan rows, so no assigned-worker suggestion rows
            if (sdpEntry) return [];
            const svcCode = getCodeFromServiceType(svc.ServiceTypeName);
            const seenWorkerKeys = new Set();
            return assignedWorkers
                .filter(w => {
                    if (!w.Active) return false;
                    if (w.ServiceTypeId === svc.ServiceTypeId) return true;
                    const wCode = getCodeFromServiceType(w.ServiceTypeName);
                    return svcCode > 0 && wCode === svcCode;
                })
                .filter(w => {
                    const key = w.WorkerId ? `id:${w.WorkerId}` : `name:${(w.WorkerName || '').trim().toLowerCase()}`;
                    if (seenWorkerKeys.has(key)) return false;
                    seenWorkerKeys.add(key);
                    return true;
                })
                .filter(w => {
                    const workerNameLower = (w.WorkerName || '').trim().toLowerCase();
                    return !rows.some(r => {
                        // Lines entered from the SDP picker carry the business service's id, not the POS
                        // ServiceTypeId, so the code number (314, 312...) also counts as the same service
                        const rowCode = getCodeFromServiceType(r.serviceCode || r.description || '');
                        const sameService = (r.serviceId && String(r.serviceId) === String(svc.ServiceTypeId))
                            || (svcCode > 0 && rowCode === svcCode)
                            || (r.serviceCode || '').trim().toLowerCase() === (svc.ServiceTypeName || '').trim().toLowerCase();
                        if (!sameService) return false;
                        if (w.WorkerId && r.workerId && String(r.workerId) === String(w.WorkerId)) return true;
                        return (r.provider || '').trim().toLowerCase() === workerNameLower;
                    });
                });
        };

        // A section's totals as its "Section Total" row shows them: its lines plus the assigned-worker
        // suggestion rows actually shown under its authorizations (not ones already saved as lines)
        const getSectionTotals = (rows, sectionAuths) => {
            const authWorkerRows = sectionAuths.flatMap(svc => getShownAuthWorkers(svc, rows)
                .map(w => ({
                    row: { ...getAuthRowObj(`auth-worker-${svc.ServiceTypeId}-${w.Id}`), provider: w.WorkerName, workerId: w.WorkerId || null, serviceCode: svc.ServiceTypeName },
                    svc,
                })));
            const totalFreq = rows.reduce((sum, r) => sum + Number(r.frequency || 0), 0)
                + authWorkerRows.reduce((sum, { row }) => sum + Number(row.frequency || 0), 0);
            // Each line and suggestion row counts what its Total Annual Cost cell shows (payroll-filled when no rate is saved)
            const totalCost = rows.reduce((sum, r) => sum + getRowDisplayCost(r), 0)
                + authWorkerRows.reduce((sum, { row, svc }) => sum + getRowDisplayCost(row, svc), 0);
            return { totalFreq, totalCost };
        };

        return (
            <div>
                {filteredPlans.map((plan, index) => {
                    const planApiSections = apiSections[plan.PlanId] || {};
                    const isExpanded = expandedPlans.has(index);
                    const hasPendingChanges = Object.values(planApiSections).flat().some(row => 
                        modifiedRows.has(`${plan.PlanId}-livingarrangements-${row.id}`) ||
                        modifiedRows.has(`${plan.PlanId}-employmentandcommunityparticipation-${row.id}`) ||
                        modifiedRows.has(`${plan.PlanId}-healthandsafety-${row.id}`)
                    );
                    
                    return (
                        <div key={plan.PlanId} className="mb-3">
                            {/* Plan Header - single merged line */}
                            {(() => {
                                // Spent = sum of TotalDollars (actual dollars billed against the authorization) per section
                                const laSpent  = authorizedServices.filter(svc => { const c = getCodeFromServiceType(svc.ServiceTypeName); return c >= 310 && c <= 330; }).reduce((sum, svc) => sum + (Number(svc.TotalDollars || 0)), 0);
                                const empSpent = authorizedServices.filter(svc => { const c = getCodeFromServiceType(svc.ServiceTypeName); return c >= 331 && c <= 355; }).reduce((sum, svc) => sum + (Number(svc.TotalDollars || 0)), 0);
                                const hsSpent  = authorizedServices.filter(svc => { const c = getCodeFromServiceType(svc.ServiceTypeName); return c >= 356 && c <= 399; }).reduce((sum, svc) => sum + (Number(svc.TotalDollars || 0)), 0);
                                const totalSpent = laSpent + empSpent + hsSpent;
                                // Total = sum of AuthorizedUnits per section from authorized services
                                const laAuth  = authorizedServices.filter(svc => { const c = getCodeFromServiceType(svc.ServiceTypeName); return c >= 310 && c <= 330; }).reduce((sum, svc) => sum + (Number(svc.AuthorizedUnits || 0)), 0);
                                const empAuth = authorizedServices.filter(svc => { const c = getCodeFromServiceType(svc.ServiceTypeName); return c >= 331 && c <= 355; }).reduce((sum, svc) => sum + (Number(svc.AuthorizedUnits || 0)), 0);
                                const hsAuth  = authorizedServices.filter(svc => { const c = getCodeFromServiceType(svc.ServiceTypeName); return c >= 356 && c <= 399; }).reduce((sum, svc) => sum + (Number(svc.AuthorizedUnits || 0)), 0);
                                const totalAuth = laAuth + empAuth + hsAuth;
                                const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                                return (
                                    <div className={`bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mt-2 mb-2 transition-all duration-200 ${isExpanded ? 'shadow-md' : ''}`}>
                                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                            {/* Search */}
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="border border-gray-300 rounded px-2 py-0.5 text-sm w-32 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white flex-shrink-0"
                                            />
                                            <span className="text-gray-300 select-none flex-shrink-0">|</span>

                                            {/* Plan Name */}
                                            {!isGuestMode ? (
                                                editingPlanName === plan.PlanId ? (
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <input type="text" className="border rounded px-1.5 py-0.5 text-xs font-semibold w-28" value={planNameForms[plan.PlanId]?.planName || plan.Name || ''} placeholder="Plan name" onChange={(e) => setPlanNameForms(prev => ({ ...prev, [plan.PlanId]: { planName: e.target.value } }))} />
                                                        <button onClick={() => handleSavePlanName(plan.PlanId)} disabled={savingPlanName === plan.PlanId} className="bg-primary-dark text-white px-1.5 py-0.5 rounded text-xs disabled:opacity-50">{savingPlanName === plan.PlanId ? 'Saving...' : 'Save'}</button>
                                                        <button onClick={() => { setEditingPlanName(null); setPlanNameForms(prev => { const n = {...prev}; delete n[plan.PlanId]; return n; }); }} className="bg-gray-300 text-primary-dark px-1.5 py-0.5 rounded text-xs">Cancel</button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <span className="text-base 2xl:text-xl font-semibold text-gray-800 whitespace-nowrap">{plan.Name || 'Unnamed Plan'}</span>
                                                        <button onClick={() => { setEditingPlanName(plan.PlanId); setPlanNameForms(prev => ({ ...prev, [plan.PlanId]: { planName: plan.Name || '' } })); }} className="text-gray-400 hover:text-blue-600" title="Edit plan name">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                        </button>
                                                    </div>
                                                )
                                            ) : (
                                                <span className="text-base 2xl:text-xl font-semibold text-gray-800 whitespace-nowrap flex-shrink-0">{plan.Name || 'Unnamed Plan'}</span>
                                            )}

                                            <span className="text-gray-300 select-none flex-shrink-0">|</span>

                                            {/* Dates */}
                                            {!isGuestMode ? (
                                                editingPlanDates === plan.PlanId ? (
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <DatePicker selected={parseDate(planDateForms[plan.PlanId]?.startDate || plan.start_date)} onChange={(date) => setPlanDateForms(prev => ({ ...prev, [plan.PlanId]: { ...prev[plan.PlanId], startDate: date, ...(sdpEntry && date ? { endDate: getDefaultPlanEndDate(date) } : {}) } }))} dateFormat="MM/dd/yyyy" placeholderText="MM/DD/YYYY" className="border rounded px-1 py-0.5 text-xs w-24" />
                                                        <span className="text-xs">–</span>
                                                        <DatePicker selected={parseDate(planDateForms[plan.PlanId]?.endDate || plan.end_date)} onChange={(date) => setPlanDateForms(prev => ({ ...prev, [plan.PlanId]: { ...prev[plan.PlanId], endDate: date } }))} dateFormat="MM/dd/yyyy" placeholderText="MM/DD/YYYY" className="border rounded px-1 py-0.5 text-xs w-24" />
                                                        <button onClick={() => handleSavePlanDates(plan.PlanId)} disabled={savingPlanDates === plan.PlanId} className="bg-primary-dark text-white px-1.5 py-0.5 rounded text-xs disabled:opacity-50">{savingPlanDates === plan.PlanId ? 'Saving...' : 'Save'}</button>
                                                        <button onClick={() => { setEditingPlanDates(null); setPlanDateForms(prev => { const n = {...prev}; delete n[plan.PlanId]; return n; }); }} className="bg-gray-300 text-primary-dark px-1.5 py-0.5 rounded text-xs">Cancel</button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <span className="text-sm 2xl:text-lg text-gray-600 whitespace-nowrap">{formatDateUS(plan.start_date)} – {formatDateUS(plan.end_date)}</span>
                                                        <button onClick={() => { setEditingPlanDates(plan.PlanId); setPlanDateForms(prev => ({ ...prev, [plan.PlanId]: { startDate: parseDate(plan.start_date), endDate: parseDate(plan.end_date) } })); }} className="text-gray-400 hover:text-blue-600" title="Edit dates">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                        </button>
                                                    </div>
                                                )
                                            ) : (
                                                <span className="text-sm 2xl:text-lg text-gray-600 whitespace-nowrap flex-shrink-0">{formatDateUS(plan.start_date)} – {formatDateUS(plan.end_date)}</span>
                                            )}

                                            <span className="text-gray-300 select-none flex-shrink-0">|</span>

                                            {sdpEntry ? (() => {
                                                // Build-up budgeting: certified budget vs the live sum of every line
                                                const certified = plan.CertifiedBudget;
                                                const hasCertified = certified !== null && certified !== undefined && certified !== '';
                                                const planTotal = sumDisplayRows(Object.values(planApiSections).flat());
                                                const unallocated = hasCertified ? Number(certified) - planTotal : null;
                                                return (
                                                    <div className="flex items-center gap-2 text-sm whitespace-nowrap flex-shrink-0">
                                                        {editingCertifiedBudget === plan.PlanId ? (
                                                            <span className="flex items-center gap-1">
                                                                <span className="text-gray-600">Certified $</span>
                                                                <input autoFocus type="number" min="0" step="0.01" className="border rounded px-1 py-0.5 text-xs w-24"
                                                                    value={certifiedBudgetDraft}
                                                                    onChange={e => setCertifiedBudgetDraft(e.target.value)}
                                                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveCertifiedBudget(plan); if (e.key === 'Escape') setEditingCertifiedBudget(null); }} />
                                                                <button onClick={() => handleSaveCertifiedBudget(plan)} disabled={savingCertifiedBudget === plan.PlanId} className="bg-primary-dark text-white px-1.5 py-0.5 rounded text-xs disabled:opacity-50">{savingCertifiedBudget === plan.PlanId ? 'Saving...' : 'Save'}</button>
                                                                <button onClick={() => setEditingCertifiedBudget(null)} className="bg-gray-300 text-primary-dark px-1.5 py-0.5 rounded text-xs">Cancel</button>
                                                            </span>
                                                        ) : (
                                                            <span className="flex items-center gap-1">
                                                                <span className="text-gray-600">Certified</span>
                                                                <span className="font-semibold text-gray-800">{hasCertified ? usdAuth(Number(certified)) : <span className="italic text-gray-400 font-normal">not set</span>}</span>
                                                                <button onClick={() => { setEditingCertifiedBudget(plan.PlanId); setCertifiedBudgetDraft(hasCertified ? String(certified) : ''); }} className="text-gray-400 hover:text-blue-600" title="Edit certified budget">
                                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                                </button>
                                                            </span>
                                                        )}
                                                        <span className="text-gray-300 select-none">|</span>
                                                        <span className="text-gray-600">Planned <span className="font-semibold text-gray-800">{usdAuth(planTotal)}</span></span>
                                                        <span className="text-gray-300 select-none">|</span>
                                                        <span className="text-gray-600">Unallocated <span className={`font-semibold ${unallocated !== null && unallocated < 0 ? 'text-red-600' : 'text-green-700'}`}>{unallocated !== null ? usdSigned(unallocated) : '—'}</span></span>
                                                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${plan.IsActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>{plan.IsActive ? 'Active' : 'Draft'}</span>
                                                        {!isGuestMode && (
                                                            <button
                                                                onClick={() => handleActivatePlan(plan)}
                                                                disabled={activatingPlan === plan.PlanId}
                                                                className="bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded text-xs font-medium shadow disabled:opacity-50"
                                                                title="Write the POS authorizations and worker/supplier assignments from this plan"
                                                            >
                                                                {activatingPlan === plan.PlanId ? 'Activating...' : (plan.IsActive ? 'Activate Changes' : 'Activate')}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })() : (
                                            /* Budget: spent/total per section (larger, to match the scale of the table below) */
                                            <span className="text-lg 2xl:text-2xl font-bold text-blue-700 whitespace-nowrap flex-shrink-0" title="Spent / authorized: plan total (Living Arrangements, Employment & Community Participation, Health & Safety)">
                                                ${fmt(totalSpent)}/${fmt(totalAuth)} (${fmt(laSpent)}/${fmt(laAuth)}, ${fmt(empSpent)}/${fmt(empAuth)}, ${fmt(hsSpent)}/${fmt(hsAuth)})
                                            </span>
                                            )}

                                            {hasPendingChanges && <span className="px-1 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">Unsaved</span>}

                                            {/* Spacer */}
                                            <div className="flex-1" />

                                            {/* Tab Nav */}
                                            <div className="flex items-center gap-1 text-sm flex-shrink-0">
                                                <button onClick={() => setActiveTab('previous')} className={`px-0.5 py-0.5 text-sm rounded ${activeTab === 'previous' ? 'font-bold text-blue-700' : 'text-gray-500 hover:text-blue-600'}`}>Previous</button>
                                                <span className="text-gray-400">–</span>
                                                <button onClick={() => setActiveTab('current')} className={`px-0.5 py-0.5 text-sm rounded ${activeTab === 'current' ? 'font-bold text-blue-700' : 'text-gray-500 hover:text-blue-600'}`}>Current</button>
                                                <span className="text-gray-400">–</span>
                                                <button onClick={() => setActiveTab('future')} className={`px-0.5 py-0.5 text-sm rounded ${activeTab === 'future' ? 'font-bold text-blue-700' : 'text-gray-500 hover:text-blue-600'}`}>Future</button>
                                            </div>

                                            <span className="text-gray-300 select-none flex-shrink-0">|</span>

                                            {!isGuestMode && (
                                                <button onClick={() => setShowAddPlanModal(true)} className="text-sm font-medium text-blue-700 hover:text-blue-900 whitespace-nowrap flex-shrink-0">
                                                    + New
                                                </button>
                                            )}

                                            {/* Export */}
                                            <div className="relative flex-shrink-0">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setDropdownOpen(dropdownOpen === `export-${plan.PlanId}` ? false : `export-${plan.PlanId}`); }}
                                                    className="bg-primary-dark text-white px-2 py-0.5 rounded text-xs shadow hover:bg-primary-hover flex items-center gap-1"
                                                >
                                                    <img src="/print.svg" alt="Print" className="w-3 h-3" />
                                                    Export
                                                </button>
                                                {dropdownOpen === `export-${plan.PlanId}` && (
                                                    <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-50">
                                                        <button onClick={(e) => { e.stopPropagation(); handleExportToExcel(plan, planApiSections); setDropdownOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            Export as Excel
                                                        </button>
                                                        <button onClick={(e) => { e.stopPropagation(); handleExportToPDF(plan, planApiSections); setDropdownOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm">
                                                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            Export as PDF
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {isGuestMode && (
                                                <button onClick={() => { setAuthMode('register'); setShowAuthModal(true); }} className="bg-primary-dark text-white px-2 py-0.5 rounded text-xs shadow font-medium flex-shrink-0">
                                                    Save My Plan
                                                </button>
                                            )}

                                            {/* Toggle */}
                                            <svg className={`w-4 h-4 text-blue-600 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} cursor-pointer flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24" onClick={(e) => { e.stopPropagation(); togglePlanExpansion(index, plan); }}>
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Plan Content - Conditionally Rendered */}
                            {isExpanded && (
                                <div className="plan-content">
                                    {/* Show loader while lines are being fetched for this plan */}
                                    {(!apiSections[plan.PlanId] || Object.keys(apiSections[plan.PlanId]).length === 0) && (
                                        planLinesLoading[plan.PlanId] ? (
                                            <div className="text-center py-10">
                                                <div className="inline-flex items-center justify-center w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                                <p className="mt-3 text-gray-500">Loading services for this plan…</p>
                                            </div>
                                        ) : null
                                    )}
                                    {/* Plan Data Tables - Always show all 3 sections */}
                            {Object.entries(sectionTitles).map(([sectionKey, sectionTitle]) => {
                                const allRows = planApiSections[sectionKey] || [];
                                const rows = filterRowsBySearch(allRows);
                                const sectionAuths = getAuthsForSection(sectionKey);

                                // In patient mode, hide sections with no authorizations entirely — except for SDP,
                                // where the plan is entered first and creates the authorizations on Activate
                                if (!isGuestMode && !sdpEntry && sectionAuths.length === 0) return null;

                                return (
                                    <section key={`${plan.PlanId}-${sectionKey}`} className="mb-4 rounded-xl shadow p-2">
                                        <div className="flex justify-between items-center mb-1">
                                            <h2 className="text-sm font-bold">{sectionTitle}</h2>
                                            {sdpEntry && rows.length > 0 && (
                                                <button
                                                    onClick={() => addSdpRow(plan.PlanId, sectionKey)}
                                                    className="text-xs font-medium text-blue-700 hover:text-blue-900"
                                                    title="Add a provider line"
                                                >
                                                    + Add line
                                                </button>
                                            )}
                                        </div>

                                        {rows.length === 0 && (sdpEntry || sectionAuths.length === 0) ? (
                                            /* Empty state - reached in guest mode, and in SDP mode for a section with no lines */
                                            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                                                <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                                                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        {searchQuery ? (
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        ) : (
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                        )}
                                                    </svg>
                                                </div>
                                                <h3 className="text-sm font-medium text-gray-900 mb-1">
                                                    {searchQuery ? `No results found for "${searchQuery}"` : 'No services in this section'}
                                                </h3>
                                                <p className="text-xs text-gray-500 mb-3">
                                                    {searchQuery ? 'Try adjusting your search terms' : 'Get started by adding your first service line item'}
                                                </p>
                                                {!searchQuery && (
                                                    <Button
                                                        className="text-sm bg-primary-dark hover:bg-primary-dark text-white rounded-lg shadow"
                                                    onClick={() => sdpEntry ? addSdpRow(plan.PlanId, sectionKey) : handleApiAddRow(plan.PlanId, sectionKey)}
                                                    title="Add First Service"
                                                >
                                                    Add First Service
                                                </Button>
                                                )}
                                            </div>
                                        ) : (
                                            /* Existing table structure for sections with data */
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[1220px] border-separate border-spacing-0 rounded-xl overflow-hidden 2xl:text-lg 2xl:my-12 bg-white">
                                        <thead>
                                            {sdpEntry ? (
                                            <tr className="bg-[#7da7d9] text-left text-sm 2xl:text-xl 2xl:h-20 font-semibold text-white">
                                                <th className="p-3 pt-4 w-14"></th>
                                                <th className="p-3">Service Code</th>
                                                <th className="p-3">Provider</th>
                                                <th className="p-3 text-center">EVV</th>
                                                <th className="p-3">Service Description</th>
                                                <th className="p-3">IPP Goal</th>
                                                <th className="p-3">Base Pay</th>
                                                <th className="p-3">Payment Model (Burden)</th>
                                                <th className="p-3">Rate / Amount</th>
                                                <th className="p-3 text-center">Hours / Count</th>
                                                <th className="p-3 text-center">Total Annual Cost</th>
                                                <th className="p-3 text-center">Actual Budget</th>
                                                <th className="p-3">Actions</th>
                                            </tr>
                                            ) : (
                                            <tr className="bg-[#7da7d9] text-left text-sm 2xl:text-xl 2xl:h-20 font-semibold text-white">
                                                <th className="p-3 pt-4 w-14"></th>
                                                <th className="p-3">Service Code</th>
                                                <th className="p-3">Provider</th>
                                                <th className="p-3 text-center">Not EVV</th>
                                                <th className="p-3">Service Description</th>
                                                <th className="p-3">IPP Goal</th>
                                                <th className="p-3">Base Pay</th>
                                                <th className="p-3">Payment Model</th>
                                                <th className="p-3">Pricing Unit</th>
                                                <th className="p-3 text-center">Annual Frequency</th>
                                                <th className="p-3 text-center">Hours Worked</th>
                                                <th className="p-3 text-center">Total Annual Cost</th>
                                                <th className="p-3 text-center">Allocated Budget</th>
                                                <th className="p-3 text-center">Actual Budget</th>
                                                <th className="p-3">Actions</th>
                                            </tr>
                                            )}
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                // Group regular rows by service code: by numeric code, so a line saved as
                                                // "310" lands under the "310 - Respite ..." authorization instead of forming its own group
                                                const authNameByCode = new Map(sectionAuths.map(s => [getCodeFromServiceType(s.ServiceTypeName), s.ServiceTypeName]));
                                                const groupedRows = rows.reduce((acc, row) => {
                                                    const rowCode = getCodeFromServiceType(row.serviceCode);
                                                    const serviceCode = (rowCode !== null && authNameByCode.has(rowCode))
                                                        ? authNameByCode.get(rowCode)
                                                        : (row.serviceCode || 'No Service Code');
                                                    if (!acc[serviceCode]) acc[serviceCode] = [];
                                                    acc[serviceCode].push(row);
                                                    return acc;
                                                }, {});

                                                const authServiceNames = new Set(sectionAuths.map(s => s.ServiceTypeName));

                                                // Auth services first (in sectionAuths order), then non-auth regular rows
                                                const authEntries = sectionAuths.map(svc => ({
                                                    serviceCode: svc.ServiceTypeName,
                                                    serviceRows: groupedRows[svc.ServiceTypeName] || [],
                                                    svc,
                                                }));
                                                const nonAuthEntries = Object.entries(groupedRows)
                                                    .filter(([sc]) => !authServiceNames.has(sc))
                                                    .map(([sc, sr]) => ({ serviceCode: sc, serviceRows: sr, svc: null }));

                                                // Every service, authorized or not, in numeric code order (310 before 320, etc.);
                                                // each one's authorization header sits directly above its own workers and lines
                                                const combinedEntries = [...authEntries, ...nonAuthEntries].sort((a, b) => {
                                                    const codeA = getCodeFromServiceType(a.serviceCode);
                                                    const codeB = getCodeFromServiceType(b.serviceCode);
                                                    if (codeA === null && codeB === null) return 0;
                                                    if (codeA === null) return 1;
                                                    if (codeB === null) return -1;
                                                    return codeA - codeB;
                                                });

                                                const renderGroup = ({ serviceCode, serviceRows, svc }, mode) => {
                                                    const authSectionJsx = (mode === 'header' && svc) ? (() => {
                                                        // Suggestion rows: assigned workers not yet saved as a line (see getShownAuthWorkers)
                                                        const svcWorkers = getShownAuthWorkers(svc, rows);
                                                        // Only count committed (saved) rows — updates only on ADD/SAVE click
                                                        const allocatedAnnual = serviceRows.reduce((sum, row) => {
                                                            return sum + Number(row.allocatedBudget || 0);
                                                        }, 0);
                                                        const authBudget = svc.AuthorizedUnits || 0;
                                                        const remaining = authBudget - allocatedAnnual;
                                                        return (
                                                            <React.Fragment key={`auth-${svc.ServiceTypeId}`}>
                                                                <tr className="bg-[#d3e3f8] border-b border-[#a9c6ea] text-sm 2xl:text-xl 2xl:h-16 transition">
                                                                    <td className="px-2 py-4"></td>
                                                                    <td className="p-2 font-bold text-gray-900">
                                                                        <span className="inline-flex items-center gap-1.5">
                                                                            <svg className="w-3.5 h-3.5 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                                            {svc.ServiceTypeName}
                                                                        </span>
                                                                    </td>
                                                                    <td className="p-2"></td>
                                                                    <td className="p-2"></td>
                                                                    <td className="p-2 font-medium"></td>
                                                                    <td className="p-2"></td>
                                                                    <td className="p-2"></td>
                                                                    <td className="p-2"></td>
                                                                    <td className="p-2"></td>
                                                                    {!sdpEntry && <td className="p-2"></td>}
                                                                    <td className="p-2 text-center"></td>
                                                                    <td className="p-2 text-center"></td>
                                                                    {/* Allocated Budget (not in SDP: build-up budgeting has no allocate-down) */}
                                                                    {!sdpEntry && (
                                                                    <td className="p-2 text-center">
                                                                        <div className="font-bold">{usdAuth(authBudget)}</div>
                                                                        <div className="text-xs text-gray-600 mt-0.5">Allocated: {usdAuth(allocatedAnnual)}</div>
                                                                        <div className={`text-xs font-bold mt-0.5 px-1.5 py-0.5 rounded ${remaining < 0 ? 'text-red-800 bg-red-200' : 'text-green-800 bg-green-200'}`}>Rem: {usdAuth(remaining)}</div>
                                                                    </td>
                                                                    )}
                                                                    <td className="p-2 text-center">
                                                                        <div className="font-bold">{usdAuth(authBudget)}</div>
                                                                        <div className="text-xs text-gray-600 mt-0.5">Spent: {usdAuth(Number(svc.TotalDollars || 0))}</div>
                                                                        <div className={`text-xs font-bold mt-0.5 px-1.5 py-0.5 rounded ${(authBudget - Number(svc.TotalDollars || 0)) < 0 ? 'text-red-800 bg-red-200' : 'text-blue-800 bg-blue-100'}`}>Rem: {usdAuth(authBudget - Number(svc.TotalDollars || 0))}</div>
                                                                    </td>
                                                                    <td className="p-2 text-center">
                                                                        {!sdpEntry && !isEmbedded && !hasUrlParams && (
                                                                            <Button
                                                                                className="text-xs bg-primary-dark hover:bg-primary-dark text-white rounded-lg shadow whitespace-nowrap"
                                                                                onClick={() => handleApiAddAuthRow(plan.PlanId, sectionKey, svc.ServiceTypeName)}
                                                                                title="Add New Worker"
                                                                            >
                                                                                Add New Worker
                                                                            </Button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                                {svcWorkers
                                                                    .map(w => {
                                                                        const rowKey = `auth-worker-${svc.ServiceTypeId}-${w.Id}`;
                                                                        const workerKey = `${svc.ServiceTypeId}-${w.Id}`;
                                                                        const provider = w.WorkerName;
                                                                        const workerEntry = { assignmentId: w.Id, entry: getAuthWorkerEntry(svc.ServiceTypeId, w.Id) };
                                                                        const aRow = { ...getAuthRowObj(rowKey), id: rowKey, serviceCode: svc.ServiceTypeName, provider, workerId: w.WorkerId || null, description: svc.ServiceTypeName };
                                                                        const aSectionKey = `auth-${svc.ServiceTypeId}`;
                                                                        const isEditingACell = (field) => editingCell.sectionKey === aSectionKey && editingCell.rowId === rowKey && editingCell.field === field;
                                                                        const openACell = (field) => setEditingCell({ sectionKey: aSectionKey, rowId: rowKey, field });
                                                                        const closeACell = () => setEditingCell({ sectionKey: null, rowId: null, field: null });
                                                                        const onAChange = (field, value) => handleAuthRowChange(rowKey, field, value);
                                                                        const entry = workerEntry?.entry || { amount: 0, mode: 'year' };
                                                                        return (
                                                                            <tr key={rowKey} className="bg-[#f6fbfa] hover:bg-[#f3f6fa] border-b border-[#e0e7ef] text-sm 2xl:text-xl 2xl:h-16 transition">
                                                                                <td className="px-2 py-4 flex gap-1">
                                                                                    <button className="text-gray-400 hover:text-gray-600"
                                                                                        onClick={() => handleApiCopyRow(plan.PlanId, sectionKey, { ...aRow, id: `copy-${rowKey}-${Date.now()}`, isNew: true })}
                                                                                        title="Copy Row">
                                                                                        <img src="/copy-icon.png" alt="Copy" className="w-4 h-4" />
                                                                                    </button>
                                                                                    <button className="text-red-400 hover:text-red-600 ml-1"
                                                                                        onClick={() => setAuthWorkerEntry(svc.ServiceTypeId, workerEntry?.assignmentId, { amount: 0, mode: 'year' })}
                                                                                        title="Clear Budget">
                                                                                        <img src="/group_1.svg" alt="Delete" className="w-4 h-4" />
                                                                                    </button>
                                                                                </td>
                                                                                <td className="p-2 font-medium"></td>
                                                                                <td className="p-2 text-gray-600">{provider}</td>
                                                                                <td className="p-2 text-center">
                                                                                    <input type="checkbox" checked={aRow.liveIn || false}
                                                                                        onChange={e => onAChange('liveIn', e.target.checked)}
                                                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2" />
                                                                                </td>
                                                                                <td className="p-2">
                                                                                    <div className="flex items-center gap-1">
                                                                                        <div className="flex-1">{svc.ServiceTypeName}</div>
                                                                                        {!isGuestMode && (
                                                                                            <button onClick={e => { e.stopPropagation(); handleAddServiceType(plan.PlanId, sectionKey, rowKey, svc.ServiceTypeName, svc.ServiceTypeName); }}
                                                                                                className="text-gray-400 hover:text-blue-600 p-1" title="Add custom service type name">
                                                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 9V4a1 1 0 011-1z" />
                                                                                                </svg>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="p-2" onClick={() => openACell('ippGoal')}>
                                                                                    {isEditingACell('ippGoal') ? (
                                                                                        <input autoFocus type="text" className="w-full border rounded px-1"
                                                                                            value={aRow.ippGoal} placeholder="Enter IPP goal"
                                                                                            onChange={e => onAChange('ippGoal', e.target.value)}
                                                                                            onBlur={closeACell}
                                                                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') closeACell(); }} />
                                                                                    ) : (aRow.ippGoal || <span className="text-gray-400 italic">Enter IPP goal</span>)}
                                                                                </td>
                                                                                <td className="p-2" onClick={() => openACell('basePay')}>
                                                                                    {(() => {
                                                                                        // Vendors have no hourly pay: payroll's PayRate on their visits is a placeholder
                                                                                        const aPayroll = payrollRates[getPayrollRateKey(provider, svc.ServiceTypeName)];
                                                                                        const payrollRate = !aRow.basePay && getRowProviderType(aRow, aPayroll) === PROVIDER_TYPE_DSW ? aPayroll?.payRate : undefined;
                                                                                        if (isEditingACell('basePay')) {
                                                                                            const defaultValue = aRow.basePay || (payrollRate !== undefined ? payrollRate : 0);
                                                                                            return (
                                                                                                <input autoFocus type="number" step="1" min="0" className="w-full border rounded px-1"
                                                                                                    value={defaultValue === 0 ? '' : defaultValue} placeholder="Enter amount"
                                                                                                    onChange={e => onAChange('basePay', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                                                                                    onBlur={closeACell}
                                                                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') closeACell(); }} />
                                                                                            );
                                                                                        }
                                                                                        if (aRow.basePay) return `$${Number(aRow.basePay).toFixed(2)}`;
                                                                                        return <span className="text-black" title="From payroll — click to override">${Number(payrollRate || 0).toFixed(2)}</span>;
                                                                                    })()}
                                                                                </td>
                                                                                <td className="p-2" onClick={() => openACell('paymentModel')}>
                                                                                    {isEditingACell('paymentModel') ? (
                                                                                        <select autoFocus className="w-full border rounded px-1"
                                                                                            value={aRow.paymentModel}
                                                                                            onChange={e => onAChange('paymentModel', e.target.value)}
                                                                                            onBlur={closeACell}>
                                                                                            <option value="">-- Select --</option>
                                                                                            {PAYMENT_MODEL_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                                                        </select>
                                                                                    ) : (aRow.paymentModel || <span className="text-gray-400 italic">Select</span>)}
                                                                                </td>
                                                                                <td className="p-2">
                                                                                    {(() => {
                                                                                        if (aRow.pricingUnit) return `$${Number(aRow.pricingUnit).toFixed(2)}`;
                                                                                        const aPayroll = svc ? payrollRates[getPayrollRateKey(provider, svc.ServiceTypeName)] : undefined;
                                                                                        // Vendor: the total charged (Pricing Unit x Frequency = Total Annual Cost)
                                                                                        if (getRowProviderType(aRow, aPayroll) === PROVIDER_TYPE_VENDOR) return <span title="Total charged, from payroll">${Number(aPayroll?.totalChargeAmount || 0).toFixed(2)}</span>;
                                                                                        return `$${Number(aPayroll?.billRate || 0).toFixed(2)}`;
                                                                                    })()}
                                                                                </td>
                                                                                <td className="p-2" onClick={() => openACell('frequency')}>
                                                                                    <div className="flex items-center justify-between">
                                                                                        <div className="flex-1 flex justify-between mr-2">
                                                                                            <CalculatorModal
                                                                                                row={aRow}
                                                                                                sectionKey={aSectionKey}
                                                                                                field="frequency"
                                                                                                onUpdateRow={updatedRow => onAChange('frequency', updatedRow.frequency)}
                                                                                            />
                                                                                        </div>
                                                                                        <div className="flex-1 text-center">
                                                                                            {isEditingACell('frequency') ? (
                                                                                                <input autoFocus type="number" step="1" min="1" className="w-20 border rounded px-1 text-left"
                                                                                                    value={aRow.frequency === '' ? '' : aRow.frequency}
                                                                                                    onChange={e => onAChange('frequency', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                                                                    onBlur={closeACell}
                                                                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') closeACell(); }} />
                                                                                            ) : (aRow.frequency || 0)}
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="p-2 text-center">{renderPayrollHours(aRow, svc)}</td>
                                                                                <td className="p-2 text-center">
                                                                                    {(() => {
                                                                                        const displayCost = getRowDisplayCost(aRow, svc);
                                                                                        return `$${displayCost.toFixed(2)}`;
                                                                                    })()}
                                                                                </td>
                                                                                <td className="p-2 text-center">
                                                                                    <div className="flex items-center justify-center gap-1">
                                                                                        <span className="text-gray-400 text-xs">$</span>
                                                                                        <input type="number" min="0"
                                                                                            value={editingAuthCell?.serviceTypeId === svc.ServiceTypeId && editingAuthCell?.assignmentId === workerEntry?.assignmentId ? editingAuthDraft : (entry.amount > 0 ? entry.amount : '')}
                                                                                            placeholder="0"
                                                                                            onFocus={() => { setEditingAuthCell({ serviceTypeId: svc.ServiceTypeId, assignmentId: workerEntry.assignmentId }); setEditingAuthDraft(entry.amount > 0 ? String(entry.amount) : ''); }}
                                                                                            onChange={e => setEditingAuthDraft(e.target.value)}
                                                                                            onBlur={() => { const n = parseFloat(String(editingAuthDraft).replace(/[^0-9.]/g, '')); setAuthWorkerEntry(svc.ServiceTypeId, workerEntry.assignmentId, { amount: isNaN(n) ? 0 : n }); setEditingAuthCell(null); }}
                                                                                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingAuthCell(null); }}
                                                                                            className="w-24 border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                                                                                        />
                                                                                    </div>
                                                                                </td>
                                                                                <td className="p-2"></td>
                                                                                <td className="p-2 text-center">
                                                                                    <button
                                                                                        disabled={savingAuthWorkers.has(workerKey)}
                                                                                        onClick={async () => {
                                                                                                    if (savingAuthWorkers.has(workerKey)) return; // guard against double-click creating two rows
                                                                                                    setSavingAuthWorkers(prev => new Set([...prev, workerKey]));
                                                                                                    try {
                                                                                                    const n = parseFloat(String(editingAuthDraft).replace(/[^0-9.]/g, ''));
                                                                                                    const allocAmt = isNaN(n) ? entry.amount : n;
                                                                                                    setAuthWorkerEntry(svc.ServiceTypeId, workerEntry.assignmentId, { amount: allocAmt });
                                                                                                    setEditingAuthCell(null);
                                                                                                    const rowData = getAuthRowObj(rowKey);
                                                                                                    const newRowId = Date.now();
                                                                                                    const newRow = {
                                                                                                        id: newRowId,
                                                                                                        serviceCode: svc.ServiceTypeName,
                                                                                                        provider: provider,
                                                                                                        workerId: w.WorkerId || null,
                                                                                                        serviceId: svc.ServiceTypeId || null,
                                                                                                        liveIn: rowData.liveIn || false,
                                                                                                        description: svc.ServiceTypeName,
                                                                                                        ippGoal: rowData.ippGoal || '',
                                                                                                        basePay: rowData.basePay || 0,
                                                                                                        paymentModel: rowData.paymentModel || 'Bill Payer',
                                                                                                        pricingUnit: rowData.pricingUnit || 0,
                                                                                                        frequency: rowData.frequency || 0,
                                                                                                        annualCost: rowData.annualCost || 0,
                                                                                                        allocatedBudget: allocAmt || null,
                                                                                                        isNew: true,
                                                                                                    };
                                                                                                    setApiSections(prev => ({
                                                                                                        ...prev,
                                                                                                        [plan.PlanId]: {
                                                                                                            ...(prev[plan.PlanId] || {}),
                                                                                                            [sectionKey]: [...(prev[plan.PlanId]?.[sectionKey] || []), newRow]
                                                                                                        }
                                                                                                    }));
                                                                                                    const saved = await handleApiSave(plan.PlanId, sectionKey, newRow);
                                                                                                    if (!saved) {
                                                                                                        // Roll back the optimistic row so the worker is suggested again instead of
                                                                                                        // silently vanishing while the backend never actually got the save
                                                                                                        setApiSections(prev => ({
                                                                                                            ...prev,
                                                                                                            [plan.PlanId]: {
                                                                                                                ...(prev[plan.PlanId] || {}),
                                                                                                                [sectionKey]: (prev[plan.PlanId]?.[sectionKey] || []).filter(r => r.id !== newRowId)
                                                                                                            }
                                                                                                        }));
                                                                                                        return;
                                                                                                    }
                                                                                                    } finally {
                                                                                                        setSavingAuthWorkers(prev => { const s = new Set(prev); s.delete(workerKey); return s; });
                                                                                                    }
                                                                                                }}
                                                                                                className="px-3 py-1 rounded text-xs font-semibold transition-colors min-w-[50px] bg-green-500 hover:bg-green-600 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                            >
                                                                                                ADD
                                                                                            </button>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                            </React.Fragment>
                                                        );
                                                    })() : null;

                                                    return (
                                                        <React.Fragment key={mode === 'header' ? `hdr-${svc.ServiceTypeId}` : `grp-${svc ? svc.ServiceTypeId : serviceCode}`}>
                                                            {authSectionJsx}
                                                            {mode === 'rows' && serviceRows.map((row) => {
                                                            // Saved lines (fetched) keep the plain row colour; a line added here and not
                                                            // saved yet is amber, and a saved line with unsaved edits is marked blue
                                                            // SDP: lines from the old flow keep the old row colour; lines added on this
                                                            // screen are green, before and after saving
                                                            const isAddedLine = sdpEntry && (row.isNew || row.sdpEntered);
                                                            return (
                                                            <tr key={row.id} className={`${isAddedLine ? 'bg-emerald-100 hover:bg-emerald-200' : 'bg-[#f6fbfa] hover:bg-[#f3f6fa]'} border-b border-[#e0e7ef] text-sm 2xl:text-xl 2xl:h-16 transition`}>
                                                                <td className="px-2 py-4 flex gap-1">
                                                                    <button 
                                                                            className="text-gray-400 hover:text-gray-600"
                                                                            onClick={() => handleApiCopyRow(plan.PlanId, sectionKey, row)}
                                                                            title="Copy Row"
                                                                        >
                                                                            <img src="/copy-icon.png" alt="Copy" className="w-4 h-4" />
                                                                        </button>
                                                                        <button 
                                                                            className="text-red-400 hover:text-red-600 ml-1"
                                                                            onClick={() => {
                                                                                console.log('Delete button clicked for row:', row);
                                                                                handleApiDeleteRow(plan.PlanId, sectionKey, row);
                                                                            }}
                                                                            title="Delete Row"
                                                                        >
                                                                            <img src="/group_1.svg" alt="Delete" className="w-4 h-4" />
                                                                        </button>
                                                                </td>
                                                    {/* Service Code - Dropdown */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "serviceCode" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "serviceCode" ? (
                                                            <div>
                                                                <div className="text-xs text-gray-500 mb-1 font-semibold">
                                                                    {getServiceCodeLabel(sectionKey)}
                                                                </div>
                                                                <select
                                                                    autoFocus
                                                                    className="w-full border rounded px-1"
                                                                    value={row.serviceCode}
                                                                    onChange={(e) => {
                                                                        const selectedServiceCode = e.target.value;
                                                                        
                                                                        console.log('Service code dropdown onChange - selected:', selectedServiceCode);
                                                                        console.log('Current row.serviceCode before update:', row.serviceCode);
                                                                        
                                                                        // Find the selected service code object from the same filtered list used in dropdown
                                                                        const filteredOptions = getFilteredServiceCodes(sectionKey);
                                                                        const selectedOption = filteredOptions.find(opt => (opt.ServiceTypeID || opt.Name) === selectedServiceCode);
                                                                        
                                                                        console.log('Selected option found:', selectedOption);
                                                                        
                                                                        // Update multiple fields at once to avoid state conflicts
                                                                        const provider = selectedOption?.Provider || selectedOption?.DefaultProvider || '';
                                                                        const description = selectedOption?.Description || selectedOption?.ServiceDescription || selectedOption?.Name || '';
                                                                        
                                                                        // Update the sections state directly with all changes
                                                                        const updatedSections = {
                                                                            ...apiSections,
                                                                            [plan.PlanId]: {
                                                                                ...apiSections[plan.PlanId],
                                                                                [sectionKey]: apiSections[plan.PlanId][sectionKey].map(r => {
                                                                                    if (r.id === row.id) {
                                                                                        const updatedRow = { 
                                                                                            ...r, 
                                                                                            serviceCode: selectedServiceCode,
                                                                                            description: description
                                                                                        };
                                                                                        
                                                                                        // Also update provider if available (not in SDP, where the provider
                                                                                        // is a picked record rather than a default name)
                                                                                        if (provider && !sdpEntry) {
                                                                                            updatedRow.provider = provider;
                                                                                        }
                                                                                        
                                                                                        console.log('Updated row:', updatedRow);
                                                                                        return updatedRow;
                                                                                    }
                                                                                    return r;
                                                                                })
                                                                            }
                                                                        };

                                                                        setApiSections(updatedSections);
                                                                        
                                                                        // Mark row as modified
                                                                        const rowKey = `${plan.PlanId}-${sectionKey}-${row.id}`;
                                                                        setModifiedRows(prev => new Set([...prev, rowKey]));
                                                                        
                                                                        // Save to localStorage in guest mode
                                                                        if (isGuestMode) {
                                                                            saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                                                                        }
                                                                        
                                                                        // Force close the dropdown after selection. SDP keyboard entry keeps it
                                                                        // open (arrowing through options fires change) and moves on with Tab.
                                                                        if (!sdpEntry) {
                                                                            setTimeout(() => {
                                                                                setEditingCell({ sectionKey: null, rowId: null, field: null });
                                                                            }, 100);
                                                                        }
                                                                    }}
                                                                    onBlur={() => closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'serviceCode')}
                                                                    onKeyDown={sdpEntry ? sdpInputKeyDown(plan.PlanId, sectionKey, row, 'serviceCode') : undefined}
                                                                >
                                                                    <option value="">-- Select Service Description --</option>
                                                                    {(() => {
                                                                        const options = getFilteredServiceCodes(sectionKey);
                                                                        console.log('Dropdown options for', sectionKey, ':', options);
                                                                        return options.map((opt) => (
                                                                            <option key={opt.ServiceTypeID || opt.Name} value={opt.ServiceTypeID || opt.Name}>
                                                                                {opt.Description || opt.ServiceDescription || opt.Name || opt.ServiceTypeID}
                                                                            </option>
                                                                        ));
                                                                    })()}
                                                                </select>
                                                            </div>
                                                        ) : (() => {
                                                            // Display service description instead of code; if the row has no
                                                            // serviceCode (e.g. an auth-matched row), fall back to the Service
                                                            // Description field instead of leaving the cell blank.
                                                            const serviceCodeOption = serviceCodeOptions.find(opt =>
                                                                (opt.ServiceTypeID === row.serviceCode || opt.Name === row.serviceCode)
                                                            );
                                                            const displayText = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || row.serviceCode || row.description;
                                                            return displayText || <span className="text-gray-400 italic">Select service description</span>;
                                                        })()}
                                                    </td>
                                                    {/* Provider - SDP: picker that resolves to a worker/vendor record; otherwise free text */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "provider" })}>
                                                        {sdpEntry ? (
                                                            editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "provider" ? (
                                                                <ProviderPicker
                                                                    value={row.provider}
                                                                    providerType={row.providerType}
                                                                    localProviders={sdpLocalProviders}
                                                                    enableSearch={!isGuestMode}
                                                                    onSelect={(selection) => handleSdpProviderSelect(plan.PlanId, sectionKey, row.id, selection)}
                                                                    onNavigate={(key, selection) => handleSdpCellKey(plan.PlanId, sectionKey, selection ? applyProviderToRow(row, selection) : row, 'provider', key)}
                                                                    onClose={() => closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'provider')}
                                                                />
                                                            ) : (
                                                                <div className="flex flex-col items-start gap-0.5">
                                                                    <span>{row.provider || <span className="text-gray-400 italic">Select provider</span>}</span>
                                                                    {row.provider && (
                                                                        <span className="flex gap-1">
                                                                            <span className="text-[10px] px-1 rounded bg-gray-100 text-gray-600" title={row.providerTypeSource === 'payroll' ? 'From payroll — pick the provider to confirm' : undefined}>{isVendorRow(row) ? 'Supplier' : 'Worker'}{row.providerTypeSource === 'payroll' ? '*' : ''}</span>
                                                                            {!isRowLinked(row) && (
                                                                                <span className="text-[10px] px-1 rounded bg-yellow-100 text-yellow-800" title="Link this line to a worker or supplier record before activating">Not linked</span>
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )
                                                        ) : editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "provider" ? (
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                className="w-full border rounded px-1"
                                                                value={row.provider}
                                                                placeholder="Enter provider"
                                                                onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "provider", e.target.value)}
                                                                onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                            />
                                                        ) : (row.provider || <span className="text-gray-400 italic">Enter provider</span>)}
                                                    </td>
                                                    {/* SDP: EVV derived from code + provider type + live-in; otherwise the "Not EVV" checkbox */}
                                                    {sdpEntry ? (
                                                        <td className="p-2 text-center">
                                                            <div className="flex flex-col items-center gap-1">
                                                                <span className={`text-xs px-1.5 py-0.5 rounded ${isRowEvv(row) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                                                                    {isRowEvv(row) ? 'EVV' : 'Non-EVV'}
                                                                </span>
                                                                {canRowBeLiveIn(row) && (
                                                                    <label className="flex items-center gap-1 text-xs text-gray-600" title="A live-in worker uses the non-EVV variant of the service">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isLiveIn(row.liveIn)}
                                                                            onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "liveIn", e.target.checked)}
                                                                            className="w-3 h-3"
                                                                        />
                                                                        Live-in
                                                                    </label>
                                                                )}
                                                            </div>
                                                        </td>
                                                    ) : (
                                                    <td className="p-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={row.liveIn || false}
                                                            onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "liveIn", e.target.checked)}
                                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                                        />
                                                    </td>
                                                    )}
                                                    {/* Description - Non-editable, only override via add/edit */}
                                                    <td className="p-2">
                                                        <div className="flex items-center gap-1">
                                                            <div className="flex-1">
                                                                {row.description || <span className="text-gray-400 italic">Select service code</span>}
                                                            </div>
                                                            {/* Show add/duplicate first until a ServiceId exists; then allow edit. Not available in guest mode */}
                                                            {!isGuestMode && (!row.isNew && row.serviceCode) && (
                                                                <div className="flex gap-1">
                                                                    {(() => {
                                                                        const customKey = `${plan.PlanId}-${sectionKey}-${row.id}`;
                                                                        // Allow Edit if we have a ServiceId
                                                                        const hasServiceId = Boolean(row.customServiceId) || Boolean(customServiceTypes[customKey]?.serviceId);
                                                                        const serviceCodeOption = serviceCodeOptions.find(opt =>
                                                                            (opt.ServiceTypeID === row.serviceCode || opt.Name === row.serviceCode)
                                                                        );
                                                                        // Canonical: prefer Description/ServiceDescription; otherwise use Name as-is (no extra prefix)
                                                                        const canonicalFromCodes = (
                                                                            serviceCodeOption?.Description ??
                                                                            serviceCodeOption?.ServiceDescription ??
                                                                            serviceCodeOption?.Name ??
                                                                            ''
                                                                        );
                                                                        const normalize = (s) => (s || '').toString().trim();
                                                                        // Show edit icon ONLY if description matches canonical (not overridden) AND we have a ServiceId
                                                                        const isDescriptionMatchingCanonical = normalize(row.description) === normalize(canonicalFromCodes);
                                                                        const showEditIcon = hasServiceId && !isDescriptionMatchingCanonical;
                                                                        
                                                                        return (
                                                                            <>
                                                                                {!showEditIcon ? (
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleAddServiceType(plan.PlanId, sectionKey, row.id, row.serviceCode, row.description);
                                                                                        }}
                                                                                        className="text-green-600 hover:text-green-800 p-1"
                                                                                        title="Add/Duplicate custom service type name"
                                                                                    >
                                                                                        <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                                                                                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                                                            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                                                                                        </svg>
                                                                                    </button>
                                                                                ) : (
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleEditServiceType(plan.PlanId, sectionKey, row.id, row.serviceCode, row.description);
                                                                                        }}
                                                                                        className="text-blue-600 hover:text-blue-800 p-1"
                                                                                        title="Edit custom service type name"
                                                                                    >
                                                                                        <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                                                                                            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708L14.5 5.207l-3-3L12.146.146zm-.793 1.207L2.5 10.207V13.5h3.293l8.853-8.853-3-3zM1 13.207V16h2.793L15.293 4.5l-3-3L1 13.207z"/>
                                                                                        </svg>
                                                                                    </button>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    {/* IPP Goal - Editable */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "ippGoal" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "ippGoal" ? (
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                className="w-full border rounded px-1"
                                                                value={row.ippGoal}
                                                                placeholder="Enter IPP goal"
                                                                onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "ippGoal", e.target.value)}
                                                                onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                            />
                                                        ) : (row.ippGoal || <span className="text-gray-400 italic">Enter IPP goal</span>)}
                                                    </td>
                                                    {/* Base Pay - Editable, pre-filled from payroll for saved worker rows when not yet manually set */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "basePay" })}>
                                                        {(() => {
                                                            // SDP vendors are paid a single invoice amount: no base pay
                                                            if (sdpEntry && isVendorRow(row)) return <span className="text-gray-400">—</span>;
                                                            // SDP rows are entered here, so no payroll-derived default
                                                            // Vendors have no hourly pay: payroll's PayRate on their visits is a placeholder
                                                            const rowPayroll = getRowPayrollEntry(row, svc);
                                                            const payrollRate = !row.basePay && getRowProviderType(row, rowPayroll) === PROVIDER_TYPE_DSW ? rowPayroll?.payRate : undefined;
                                                            const isEditingThis = editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "basePay";
                                                            if (isEditingThis) {
                                                                const defaultValue = row.basePay || (payrollRate !== undefined ? payrollRate : 0);
                                                                return (
                                                                    <input
                                                                        autoFocus
                                                                        type="number"
                                                                        step="1"
                                                                        min="0"
                                                                        className="w-full border rounded px-1"
                                                                        value={defaultValue === 0 ? '' : defaultValue}
                                                                        placeholder="Enter amount"
                                                                        onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "basePay", e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                                                        onBlur={() => closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'basePay')}
                                                                        onKeyDown={sdpInputKeyDown(plan.PlanId, sectionKey, row, 'basePay')}
                                                                    />
                                                                );
                                                            }
                                                            if (row.basePay) return `$${Number(row.basePay).toFixed(2)}`;
                                                            if ((svc && !sdpEntry) || (sdpEntry && payrollRate !== undefined)) {
                                                                return <span className="text-black" title="From payroll — click to override">${Number(payrollRate || 0).toFixed(2)}</span>;
                                                            }
                                                            return <span className="text-gray-400 italic">Enter base pay</span>;
                                                        })()}
                                                    </td>
                                                    {/* Payment Model - Dropdown */}
                                                    <td className="p-2" onClick={() => { if (!(sdpEntry && isVendorRow(row))) setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "paymentModel" }); }}>
                                                        {sdpEntry && isVendorRow(row) ? (
                                                            // Vendors are paid the invoice amount as billed: always Bill Payer, no burden
                                                            <span title="Suppliers have no burden">Bill Payer</span>
                                                        ) : sdpEntry && editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "customBurden" ? (
                                                            // SDP worker: custom burden %, applied as Base Pay x (1 + %)
                                                            (() => {
                                                                const commit = () => {
                                                                    const pct = parseFloat(customBurdenDraft);
                                                                    if (!isNaN(pct) && pct >= 0) handleApiChange(plan.PlanId, sectionKey, row.id, "paymentModel", formatCustomBurden(pct));
                                                                    closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'customBurden');
                                                                };
                                                                return (
                                                                    <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                                        <input
                                                                            autoFocus
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            className="w-20 border rounded px-1"
                                                                            value={customBurdenDraft}
                                                                            placeholder="e.g. 12.5"
                                                                            onChange={(e) => setCustomBurdenDraft(e.target.value)}
                                                                            onBlur={commit}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                                                                                if (e.key === 'Escape') { e.preventDefault(); closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'customBurden'); }
                                                                            }}
                                                                        />
                                                                        <span className="text-gray-500">%</span>
                                                                    </span>
                                                                );
                                                            })()
                                                        ) : editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "paymentModel" ? (
                                                            <select
                                                                autoFocus
                                                                className="w-full border rounded px-1"
                                                                value={sdpEntry && burdenRates.length > 0
                                                                    ? (parseCustomBurdenPercent(row.paymentModel) !== null ? '__custom__' : (isWorkerBurdenChosen(row.paymentModel) ? row.paymentModel : ''))
                                                                    : row.paymentModel}
                                                                onChange={(e) => {
                                                                    if (e.target.value === '__custom__') {
                                                                        // Custom %: switch the cell to a percent box, prefilled with the current custom value
                                                                        const current = parseCustomBurdenPercent(row.paymentModel);
                                                                        setCustomBurdenDraft(current !== null ? String(current) : '');
                                                                        setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "customBurden" });
                                                                        return;
                                                                    }
                                                                    handleApiChange(plan.PlanId, sectionKey, row.id, "paymentModel", e.target.value);
                                                                }}
                                                                onBlur={() => closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'paymentModel')}
                                                                onKeyDown={sdpEntry ? sdpInputKeyDown(plan.PlanId, sectionKey, row, 'paymentModel') : undefined}
                                                            >
                                                                {sdpEntry && burdenRates.length > 0 ? (
                                                                    // Worker burdens only: Bill Payer is for suppliers, so it's not offered here
                                                                    <>
                                                                        <option value="">-- Select burden --</option>
                                                                        {burdenRates.filter(b => String(b.Name || '').trim().toLowerCase() !== 'bill payer').map(b => (
                                                                            <option key={b.BurdenRateId} value={b.Name}>{b.Name} ({Number(b.Percent)}%)</option>
                                                                        ))}
                                                                        <option value="__custom__">
                                                                            {parseCustomBurdenPercent(row.paymentModel) !== null ? `Custom (${parseCustomBurdenPercent(row.paymentModel)}%)…` : 'Custom %…'}
                                                                        </option>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <option value="">-- Select Payment Model --</option>
                                                                        {PAYMENT_MODEL_OPTIONS.filter(opt => !(sdpEntry && opt === 'Bill Payer')).map((opt) => (
                                                                            <option key={opt} value={opt}>{opt}</option>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </select>
                                                        ) : sdpEntry ? (
                                                            burdenRates.length > 0 && !isWorkerBurdenChosen(row.paymentModel) ? (
                                                                <span className="text-gray-400 italic">Select burden</span>
                                                            ) : (
                                                                <span>
                                                                    {parseCustomBurdenPercent(row.paymentModel) !== null ? 'Custom' : row.paymentModel}
                                                                    {getModelMultiplier(row.paymentModel) !== 1 && (
                                                                        <span className="text-xs text-gray-500"> (+{roundCents((getModelMultiplier(row.paymentModel) - 1) * 100)}%)</span>
                                                                    )}
                                                                </span>
                                                            )
                                                        ) : row.paymentModel}
                                                    </td>
                                                    {/* Pricing Unit - Calculated, falls back to payroll's Bill Rate for saved worker/vendor rows.
                                                        SDP: a DSW's pay + burden; a vendor's invoice amount, entered here. */}
                                                    <td className="p-2" onClick={() => { if (sdpEntry && isVendorRow(row)) setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "pricingUnit" }); }}>
                                                        {(() => {
                                                            if (sdpEntry && isVendorRow(row)) {
                                                                if (editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "pricingUnit") {
                                                                    return (
                                                                        <input
                                                                            autoFocus
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            className="w-full border rounded px-1"
                                                                            value={row.pricingUnit === 0 ? '' : row.pricingUnit}
                                                                            placeholder="Amount"
                                                                            onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "pricingUnit", e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                                                            onBlur={() => closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'pricingUnit')}
                                                                            onKeyDown={sdpInputKeyDown(plan.PlanId, sectionKey, row, 'pricingUnit')}
                                                                        />
                                                                    );
                                                                }
                                                                return row.pricingUnit ? `$${Number(row.pricingUnit).toFixed(2)}` : <span className="text-gray-400 italic">Enter amount</span>;
                                                            }
                                                            if (row.pricingUnit) return `$${Number(row.pricingUnit).toFixed(2)}`;
                                                            const rowPayroll = (svc || sdpEntry) ? getRowPayrollEntry(row, svc) : undefined;
                                                            // Vendor: the total charged (Pricing Unit x Frequency = Total Annual Cost)
                                                            if (getRowProviderType(row, rowPayroll) === PROVIDER_TYPE_VENDOR) return <span title="Total charged, from payroll">${Number(rowPayroll?.totalChargeAmount || 0).toFixed(2)}</span>;
                                                            return `$${Number(rowPayroll?.billRate || 0).toFixed(2)}`;
                                                        })()}
                                                    </td>
                                                    {/* Frequency - Editable + Calculator */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "frequency" })}>
                                                        <div className="flex items-center justify-between">
                                                             {/* Hours calculator is for DSW hours; a vendor's count (e.g. 12 monthly payments) is typed */}
                                                             {!(sdpEntry && isVendorRow(row)) && (
                                                             <div className="flex-1 flex justify-between mr-2">
                                                                <CalculatorModal
                                                                    row={row}
                                                                    sectionKey={`${plan.PlanId}-${sectionKey}`}
                                                                    field="frequency"
                                                                    onUpdateRow={(updatedRow) => {
                                                                        // Calculator will push updated frequency values back to the table
                                                                        handleApiChange(plan.PlanId, sectionKey, row.id, "frequency", updatedRow.frequency);
                                                                    }}
                                                                />
                                                            </div>
                                                             )}
                                                            <div className="flex-1 text-center">
                                                                {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "frequency" ? (
                                                                        <input
                                                                            autoFocus
                                                                            type="number"
                                                                            step="1"
                                                                            min="1"
                                                                            className="w-20 border rounded px-1 text-left"
                                                                            value={row.frequency === '' ? '' : row.frequency}
                                                                            onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "frequency", e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                                            onBlur={() => closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'frequency')}
                                                                            onKeyDown={sdpInputKeyDown(plan.PlanId, sectionKey, row, 'frequency')}
                                                                        />
                                                                ) : (
                                                                    Number(row.frequency || 1).toLocaleString(undefined, { maximumFractionDigits: 2 })
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {/* Hours Worked (old view): payroll hours for a worker, so Total Annual Cost reads as rate x hours */}
                                                    {!sdpEntry && <td className="p-2 text-center">{renderPayrollHours(row, svc)}</td>}
                                                    {/* Annual Cost - Calculated (Read-only) with budget comparison: for workers (real PayRate > 0),
                                                        Pricing Unit x hours worked (from payroll); for vendors, Pricing Unit (payroll's summed
                                                        ChargeAmount) directly — same as before, just now also x Frequency when one is set via
                                                        the calculator. Does not change what the Pricing Unit column itself displays. */}
                                                    <td
                                                        className={`p-2 text-center ${sdpEntry ? 'cursor-pointer' : ''}`}
                                                        onClick={() => {
                                                            if (!sdpEntry) return;
                                                            setTotalDraft(String(roundCents(getRowDisplayCost(row, svc)) || ''));
                                                            setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "annualCost" });
                                                        }}
                                                    >
                                                        {(() => {
                                                            const displayCost = getRowDisplayCost(row, svc);
                                                            // SDP: editable; the typed total updates the amount (supplier) or hours (worker)
                                                            if (sdpEntry && editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "annualCost") {
                                                                const commit = () => { applySdpTotal(plan.PlanId, sectionKey, row, totalDraft); closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'annualCost'); };
                                                                return (
                                                                    <input
                                                                        autoFocus
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        className="w-28 border rounded px-1 text-center"
                                                                        value={totalDraft}
                                                                        placeholder="Total"
                                                                        title={isVendorRow(row) ? 'Sets the amount (total ÷ count)' : 'Sets the hours (total ÷ rate)'}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={(e) => setTotalDraft(e.target.value)}
                                                                        onBlur={commit}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') { e.preventDefault(); commit(); }
                                                                            if (e.key === 'Escape') { e.preventDefault(); closeCellIf(`${plan.PlanId}-${sectionKey}`, row.id, 'annualCost'); }
                                                                        }}
                                                                    />
                                                                );
                                                            }
                                                            return (
                                                                <div className="flex flex-col items-center">
                                                                    <div>${displayCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                                    {!sdpEntry && Number(row.allocatedBudget) > 0 && (
                                                                        <div className={`text-xs mt-1 px-2 py-1 rounded ${
                                                                            displayCost > Number(row.allocatedBudget) ? 'bg-red-100 text-red-600' :
                                                                            displayCost < Number(row.allocatedBudget) ? 'bg-green-100 text-green-600' :
                                                                            'bg-yellow-100 text-yellow-600'
                                                                        }`}>
                                                                            {displayCost > Number(row.allocatedBudget) ? 'Over Budget' :
                                                                             displayCost < Number(row.allocatedBudget) ? 'Under Budget' :
                                                                             'On Budget'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    {/* Allocated Budget - Editable (not in SDP: build-up budgeting has no allocate-down) */}
                                                    {!sdpEntry && (
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "allocatedBudget" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "allocatedBudget" ? (
                                                            <input
                                                                autoFocus
                                                                type="number"
                                                                step="0.01"
                                                                className="w-full border rounded px-1"
                                                                value={row.allocatedBudget || ''}
                                                                placeholder="Enter budget"
                                                                onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "allocatedBudget", e.target.value)}
                                                                onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                            />
                                                        ) : (
                                                            <div className="text-center">
                                                                {(row.allocatedBudget !== null && row.allocatedBudget !== undefined && row.allocatedBudget !== '' && !isNaN(row.allocatedBudget)) ? 
                                                                    `$${Number(row.allocatedBudget).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                                                                    <span className="text-gray-400 italic">Enter budget</span>
                                                                }
                                                            </div>
                                                        )}
                                                    </td>
                                                    )}
                                                    {/* Actual Budget - not applicable per line item */}
                                                    <td className="p-2"></td>
                                                    {/* Actions */}
                                                    <td className="p-2">
                                                        <div className="flex gap-1">
                                                            {/* Save Button - Always visible, changes style based on state */}
                                                                <button 
                                                                    className={`px-3 py-1 rounded text-xs font-semibold transition-colors min-w-[50px] ${
                                                                        (modifiedRows.has(`${plan.PlanId}-${sectionKey}-${row.id}`) || row.isNew) 
                                                                            ? "bg-green-500 text-white hover:bg-green-600 shadow-md" 
                                                                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                                                    }`}
                                                                    onClick={() => handleApiSave(plan.PlanId, sectionKey, row)}
                                                                    disabled={savingRows.has(`${plan.PlanId}-${sectionKey}-${row.id}`)}
                                                                    title={`${row.isNew ? 'Add New' : 'Save Changes'} Row`}
                                                                >
                                                                    {savingRows.has(`${plan.PlanId}-${sectionKey}-${row.id}`) ? (
                                                                        <svg className="w-3 h-3 animate-spin mx-auto" fill="currentColor" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                    ) : (
                                                                        row.isNew ? 'ADD' : 'SAVE'
                                                                    )}
                                                                </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                            })}
                                            
                                            {/* SDP service code subtotal: dollars only (never sums hours with payment counts);
                                                the code's POS authorization and spend are on its header row */}
                                            {sdpEntry && mode === 'rows' && serviceRows.length > 0 && (
                                                <tr className="bg-[#e8f4f8] text-gray-700 border-b border-[#c2d9e0] font-semibold">
                                                    <td colSpan={10} className="p-2 text-center text-sm">
                                                        {serviceCode} Subtotal ({serviceRows.length} provider{serviceRows.length === 1 ? '' : 's'})
                                                    </td>
                                                    <td className="p-2 text-center text-sm">
                                                        {usdAuth(sumDisplayRows(serviceRows))}
                                                    </td>
                                                    <td className="p-2"></td>
                                                    <td className="p-2 text-center">
                                                        <button
                                                            onClick={() => addSdpRow(plan.PlanId, sectionKey, serviceRows[0]?.serviceCode || '', serviceRows[0]?.description || '')}
                                                            className="text-xs font-medium text-blue-700 hover:text-blue-900 whitespace-nowrap"
                                                            title={`Add another provider under ${serviceCode}`}
                                                        >
                                                            + Provider
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}

                                            {/* Service Code Subtotal - always shown so every service-code group has a visible boundary */}
                                            {!sdpEntry && mode === 'rows' && serviceRows.length > 0 && (
                                                <tr className="bg-[#e8f4f8] text-gray-700 border-b border-[#c2d9e0] font-semibold">
                                                    <td colSpan={9} className="p-2 text-center text-sm">
                                                        {serviceCode} Subtotal ({serviceRows.length} record{serviceRows.length === 1 ? '' : 's'})
                                                    </td>
                                                    <td className="p-2 ps-14 text-center text-sm">
                                                        {serviceRows.reduce((sum, r) => sum + Number(r.frequency || 0), 0).toFixed(2)}
                                                    </td>
                                                    <td className="p-2 text-center text-sm">
                                                        {sumPayrollHours(serviceRows, svc).toFixed(2)}
                                                    </td>
                                                    <td className="p-2 text-center text-sm">
                                                        ${serviceRows.reduce((sum, r) => sum + getRowDisplayCost(r, svc), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="p-2 text-center text-sm">
                                                        ${serviceRows.reduce((sum, r) => sum + (Number(r.allocatedBudget || 0)), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="p-2"></td>
                                                    <td className="p-2"></td>
                                                </tr>
                                            )}

                                                        </React.Fragment>
                                                    );
                                                };

                                                // SDP: a code with no POS authorization yet still gets a header row; its
                                                // authorization is created from these lines on Activate
                                                const renderSdpPlannedHeader = ({ serviceCode, serviceRows }) => {
                                                    return (
                                                        <tr key={`sdp-hdr-${serviceCode}`} className="bg-[#d3e3f8] border-b border-[#a9c6ea] text-sm 2xl:text-xl 2xl:h-16 transition">
                                                            <td className="px-2 py-4"></td>
                                                            <td className="p-2 font-bold text-gray-900">
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" strokeWidth={2} strokeDasharray="3 3" /></svg>
                                                                    {serviceRows[0]?.description || serviceCode}
                                                                </span>
                                                            </td>
                                                            <td className="p-2" colSpan={8}></td>
                                                            <td className="p-2 text-center">
                                                                <div className="text-xs text-gray-600">Planned</div>
                                                                <div className="font-bold">{usdAuth(sumDisplayRows(serviceRows))}</div>
                                                            </td>
                                                            <td className="p-2 text-center">
                                                                <div className="text-xs italic text-gray-500">Not authorized yet</div>
                                                                <div className="text-xs text-gray-500">Created on Activate</div>
                                                            </td>
                                                            <td className="p-2"></td>
                                                        </tr>
                                                    );
                                                };

                                                // Each code's header (with its assigned-worker rows) sits directly above its own lines.
                                                // SDP also gives not-yet-authorized codes a "Planned" header.
                                                return (
                                                    <React.Fragment>
                                                        {combinedEntries.map(entry => (
                                                            <React.Fragment key={`svc-${entry.svc ? entry.svc.ServiceTypeId : entry.serviceCode}`}>
                                                                {entry.svc ? renderGroup(entry, 'header') : (sdpEntry ? renderSdpPlannedHeader(entry) : null)}
                                                                {renderGroup(entry, 'rows')}
                                                            </React.Fragment>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })()}
                                            {/* Section total */}
                                            {sdpEntry ? (
                                                <tr className="bg-[#d1e7f5] text-gray-800 border-b border-[#b5d6c2] font-bold 2xl:text-2xl">
                                                    <td colSpan={10} className="p-2 text-center">Section Total</td>
                                                    <td className="p-2 text-center">{usdAuth(sumDisplayRows(rows))}</td>
                                                    <td className="p-2" colSpan={2}></td>
                                                </tr>
                                            ) : (() => {
                                                const { totalFreq, totalCost } = getSectionTotals(rows, sectionAuths);
                                                // Same figures as the subtotals (Allocated) and the authorization headers (Actual), added up
                                                const totalAllocated = rows.reduce((sum, r) => sum + Number(r.allocatedBudget || 0), 0);
                                                const totalAuthorized = sectionAuths.reduce((sum, svc) => sum + Number(svc.AuthorizedUnits || 0), 0);
                                                const totalSpent = sectionAuths.reduce((sum, svc) => sum + Number(svc.TotalDollars || 0), 0);
                                                return (
                                                    <tr className="bg-[#d1e7f5] text-gray-800 border-b border-[#b5d6c2] font-bold 2xl:text-2xl">
                                                        <td colSpan={9} className="p-2 text-center">Section Total</td>
                                                        <td className="p-2 text-center">{totalFreq.toFixed(0)}</td>
                                                        <td className="p-2 text-center">{sumPayrollHours(rows).toFixed(2)}</td>
                                                        <td className="p-2 text-center">${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                        <td className="p-2 text-center">
                                                            {sectionAuths.length > 0 ? (
                                                                <>
                                                                    <div>{usdAuth(totalAuthorized)}</div>
                                                                    <div className="text-xs font-bold mt-0.5 px-1.5 py-0.5 rounded text-green-800 bg-green-200">Allocated: {usdAuth(totalAllocated)}</div>
                                                                </>
                                                            ) : usdAuth(totalAllocated)}
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            {sectionAuths.length > 0 && (
                                                                <>
                                                                    <div>{usdAuth(totalAuthorized)}</div>
                                                                    <div className="text-xs font-bold mt-0.5 px-1.5 py-0.5 rounded text-blue-800 bg-blue-100">Spent: {usdAuth(totalSpent)}</div>
                                                                </>
                                                            )}
                                                        </td>
                                                        <td className="p-2"></td>
                                                    </tr>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}

                            {/* Actual total: the Actual Budget column (authorized / spent / remaining from POS)
                                added up per section and for the whole plan */}
                            {(() => {
                                const sectionTotals = Object.entries(sectionTitles)
                                    .map(([sectionKey, sectionTitle]) => {
                                        const sectionAuths = getAuthsForSection(sectionKey);
                                        // Actual figures come from POS, so only sections with authorizations count
                                        // (in SDP too, where a section's lines can come before its authorization)
                                        if (sectionAuths.length === 0) return null;
                                        const authorized = sectionAuths.reduce((sum, svc) => sum + Number(svc.AuthorizedUnits || 0), 0);
                                        const spent = sectionAuths.reduce((sum, svc) => sum + Number(svc.TotalDollars || 0), 0);
                                        return { sectionKey, title: sectionTitle.replace(/\s*\(.*\)\s*$/, ''), authorized, spent, remaining: authorized - spent };
                                    })
                                    .filter(Boolean);
                                if (sectionTotals.length === 0) return null;
                                const total = sectionTotals.reduce((acc, s) => ({
                                    authorized: acc.authorized + s.authorized,
                                    spent: acc.spent + s.spent,
                                    remaining: acc.remaining + s.remaining,
                                }), { authorized: 0, spent: 0, remaining: 0 });
                                const usd = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                const remClass = (n) => (n < 0 ? 'text-red-700' : 'text-blue-800');
                                return (
                                    <section className="mb-4 rounded-xl shadow p-2">
                                        <div className="bg-[#b9d6ee] text-gray-900 rounded-xl px-4 py-3">
                                            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
                                                <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm 2xl:text-lg text-gray-700">
                                                    {sectionTotals.map(s => (
                                                        <div key={s.sectionKey} className="whitespace-nowrap">
                                                            <div className="font-semibold text-gray-900">{s.title}</div>
                                                            <div>
                                                                {usd(s.authorized)} · Spent {usd(s.spent)} · Rem <span className={`font-semibold ${remClass(s.remaining)}`}>{usd(s.remaining)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="text-right whitespace-nowrap">
                                                    <div className="font-bold text-lg 2xl:text-2xl">Actual Total (All Sections): {usd(total.authorized)}</div>
                                                    <div className="text-sm 2xl:text-lg text-gray-700">
                                                        Spent {usd(total.spent)} · Rem <span className={`font-semibold ${remClass(total.remaining)}`}>{usd(total.remaining)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                );
                            })()}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Individual Plan Export Functions
    // Export wrapper functions that handle plan locking for guest users
    const handleExportToExcel = (plan, planApiSections) => {
        exportPlanToExcel(plan, planApiSections);
        
        // Plan lock removed — do not lock after export
    };
    
    const handleExportToPDF = (plan, planApiSections) => {
        exportPlanToPDF(plan, planApiSections);
        
        // Plan lock removed — do not lock after export
    };

    const exportPlanToExcel = async (plan, planApiSections) => {
        const workbook = new ExcelJS.Workbook();
        const planDisplayName = plan.Name || plan.planName || `Plan ${plan.PlanId}`;
        const sheet = workbook.addWorksheet(planDisplayName);

        // Plan header info
        sheet.addRow([planDisplayName]);
        sheet.mergeCells(`A1:K1`);
    sheet.addRow([`Period: ${formatDateUS(plan.start_date)} - ${formatDateUS(plan.end_date)}`]);
        sheet.mergeCells(`A2:K2`);
        sheet.addRow([sdpEntry
            ? `Status: ${plan.IsActive ? 'Active' : 'Draft'} | ${getSdpBudgetSummary(plan, planApiSections)}`
            : `Status: ${plan.IsActive ? 'Active' : 'Inactive'} | Year: ${plan.Year}`]);
        sheet.mergeCells(`A3:K3`);
        sheet.addRow([]);

        // const headerColumns = [
        //     "Service Code",
        //     "Provider", 
        //     "Service Description",
        //     "IPP Goal",
        //     "Base Pay",
        //     "Payment Model",
        //     "Pricing Unit",
        //     "Annual Frequency",
        //     "Total Annual Cost",
        // ];

        // Map plan_type to section titles with service code ranges
        const sectionTitles = {
            'livingarrangements': 'Living Arrangements (310–330)',
            'employmentandcommunityparticipation': 'Employment and Community Participation (331–355)', 
            'healthandsafety': 'Health and Safety (356–399)'
        };

        let grandTotal = 0;
        const sectionSubtotalRows = [];
        const sectionTitleRows = [];

        Object.entries(sectionTitles).forEach(([sectionKey, sectionTitle]) => {
            const sectionData = planApiSections[sectionKey] || [];
            
            // Section title
            sheet.addRow([sectionTitle]);
            const titleRowNum = sheet.lastRow.number;
            sectionTitleRows.push(titleRowNum);
            sheet.mergeCells(`A${titleRowNum}:K${titleRowNum}`);
            
            // Section headers
            const headerColumns = sdpEntry ? [
                "Service Code",
                "Provider",
                "EVV",
                "Service Description",
                "IPP Goal",
                "Base Pay",
                "Payment Model",
                "Rate / Amount",
                "Hours / Count",
                "Total Annual Cost",
                "Provider Type",
            ] : [
                "Service Code",
                "Provider",
                "Not EVV",
                "Service Description",
                "IPP Goal",
                "Base Pay",
                "Payment Model",
                "Pricing Unit",
                "Annual Frequency",
                "Total Annual Cost",
                "Allocated Budget",
            ];
            // Add header row for this section so Excel has column headers
            const headerRow = sheet.addRow(headerColumns);
           // const headerRowNum = headerRow.number;
            // Apply basic header styling immediately so it appears above the data
            headerRow.height = 22;
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F0CCB6" } };
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                cell.border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" },
                };
            });

                if (sectionData.length > 0) {
                // Group by serviceCode for subtotals
                const groups = {};
                sectionData.forEach((r) => {
                    const code = r.serviceCode || r.ServiceCode || 'Unknown';
                    if (!groups[code]) groups[code] = [];
                    groups[code].push(r);
                });

                let sectionTotal = 0;

                Object.keys(groups).forEach((code) => {
                    groups[code].forEach((r) => {
                        const annualCost = Number(r.frequency || r.Frequency || 0) * Number(r.pricingUnit || r.UnitCost || 0);
                        if (sdpEntry) {
                            const vendor = isVendorRow(r);
                            sheet.addRow([
                                r.serviceCode,
                                r.provider,
                                isRowEvv(r) ? 'Yes' : 'No',
                                r.description,
                                r.ippGoal,
                                vendor ? '' : r.basePay,
                                vendor ? '' : r.paymentModel,
                                r.pricingUnit,
                                r.frequency,
                                annualCost,
                                `${vendor ? 'Supplier' : 'Worker'}${isRowLinked(r) ? '' : ' (not linked)'}`
                            ]);
                            sectionTotal += annualCost;
                            return;
                        }
                        sheet.addRow([
                            r.serviceCode || r.ServiceCode,
                            r.provider || r.Provider,
                            r.liveIn || r.LiveIn ? 'Yes' : 'No',
                            r.description || r.Description,
                            r.ippGoal || r.IppGoal,
                            r.basePay || r.BasePay,
                            r.paymentModel || r.PaymentModel,
                            r.pricingUnit || r.UnitCost,
                            r.frequency || r.Frequency,
                            annualCost,
                            r.allocatedBudget || r.AllocatedBudget || ''
                        ]);
                        sectionTotal += annualCost;
                    });

                    // Add subtotal for each service code group (always show like main export)
                    const subtotal = groups[code].reduce((sum, r) => 
                        sum + (Number(r.frequency || r.Frequency || 0) * Number(r.pricingUnit || r.UnitCost || 0)), 0
                    );
                    const allocatedSubtotal = groups[code].reduce((sum, r) => 
                        sum + (Number(r.allocatedBudget || r.AllocatedBudget || 0)), 0
                    );
                    sheet.addRow([
                        "", "", "", "", "", "", "", "", `Subtotal ${code}`, subtotal, ""
                    ]);
                    // SDP has no allocate-down or separately approved per-code budget to compare against
                    if (sdpEntry) return;

                    // Add budget comparison row for this service code
                    const budgetKey = `${plan.PlanId}-${sectionKey}-${code}`;
                    const approvedBudget = Number(approvedBudgets[budgetKey] || 0);
                    const actualDiff = subtotal - approvedBudget;
                    const allocatedDiff = allocatedSubtotal - approvedBudget;
                    
                    const budgetRow = sheet.addRow([
                        "", "", "", `Approved Budget for ${code}:`, "", "",
                        `Actual: $${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        `Approved: ${approvedBudget > 0 ? '$' + approvedBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'Not Set'}`,
                        `Allocated: $${allocatedSubtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        approvedBudget > 0 ? `Variance: ${actualDiff >= 0 ? '+' : ''}${actualDiff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
                        approvedBudget > 0 ? `Alloc Var: ${allocatedDiff >= 0 ? '+' : ''}${allocatedDiff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''
                    ]);
                    budgetRow.height = 20;
                    budgetRow.eachCell((cell) => {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "F0F8FF" }
                        };
                        cell.font = { italic: true };
                        cell.alignment = { horizontal: "left", vertical: "middle" };
                    });
                });

                // Add section subtotal
                const sectionSubtotalRow = sheet.addRow([
                    "", "", "", "", "", "", "", "", `Subtotal (${sectionTitle.split(' (')[0]})`, sectionTotal, ""
                ]);
                sectionSubtotalRows.push(sectionSubtotalRow.number);
                grandTotal += sectionTotal;
            } else {
                sheet.addRow(["No data available for this section"]);
                sheet.mergeCells(`A${sheet.lastRow.number}:K${sheet.lastRow.number}`);
            }

            sheet.addRow([]);
        });

        // Grand total
        const grandTotalRow = sheet.addRow([
            "Grand Total", "", "", "", "", "", "", "", "", grandTotal, ""
        ]);
        sheet.mergeCells(`A${grandTotalRow.number}:I${grandTotalRow.number}`);

        /** ---- Styling (Same as main export) ---- **/
        
        // Plan header styling
        [1, 2, 3].forEach((rowNum) => {
            const row = sheet.getRow(rowNum);
            row.height = 25;
            row.eachCell((cell) => {
                cell.font = { bold: true, size: rowNum === 1 ? 16 : 12 };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "E6F3FF" },
                };
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            });
        });

        // Section titles
        sectionTitleRows.forEach((rowNum) => {
            const row = sheet.getRow(rowNum);
            row.height = 30;
            row.eachCell((cell) => {
                cell.font = { bold: true, size: 14, color: { argb: "000000" } };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "c8e0e8" },
                };
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            });
        });

        // Column headers (find all header rows)
        sheet.eachRow((row, rowNum) => {
            if (row.getCell(1).value === "Service Code") {
                row.height = 25;
                row.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "f0ccb6" },
                    };
                    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                    cell.border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" },
                    };
                });
            }
        });

        // Borders for all data rows
        sheet.eachRow((row, rowNum) => {
            if (rowNum > 4) { // Skip header rows
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" },
                    };
                    cell.alignment = { wrapText: true, vertical: "middle" };
                });
            }
        });

        // Section subtotals (grey)
        sectionSubtotalRows.forEach((rowNum) => {
            const row = sheet.getRow(rowNum);
            row.height = 25;
            row.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "D9D9D9" },
                };
                cell.alignment = { horizontal: "right", vertical: "middle" };
            });
        });

        // Grand total (dark fill, white text)
        grandTotalRow.height = 28;
        grandTotalRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "444444" },
            };
            cell.alignment = { horizontal: "right", vertical: "middle" };
        });

        // Currency formatting
        ["F", "H", "J", "K"].forEach((col) => {
            sheet.getColumn(col).numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00';
        });

        // Set custom widths for each column (all 11 columns)
        sheet.columns = [
            { key: "serviceCode", width: 15 },      // A
            { key: "provider", width: 20 },         // B
            { key: "liveIn", width: 10 },           // C
            { key: "description", width: 35 },      // D
            { key: "ippGoal", width: 30 },          // E
            { key: "basePay", width: 15 },          // F
            { key: "paymentModel", width: 18 },     // G
            { key: "pricingUnit", width: 15 },      // H
            { key: "frequency", width: 20 },        // I
            { key: "annualCost", width: 18 },       // J
            { key: "allocatedBudget", width: 18 },  // K
        ];
        
        // Save the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer]);
        saveAs(blob, `spending-plan-${plan.PlanId}.xlsx`);
    };

    const exportPlanToPDF = (plan, planApiSections) => {
        const doc = new jsPDF();
        
        // Plan header
        const planDisplayName = plan.Name || plan.planName || `Plan #${plan.PlanId}`;
        doc.setFontSize(16);
        doc.text(planDisplayName, 20, 20);
        
        doc.setFontSize(12);
    doc.text(`Period: ${formatDateUS(plan.start_date)} - ${formatDateUS(plan.end_date)}`, 20, 35);
        doc.text(sdpEntry
            ? `Status: ${plan.IsActive ? 'Active' : 'Draft'} | ${getSdpBudgetSummary(plan, planApiSections)}`
            : `Status: ${plan.IsActive ? 'Active' : 'Inactive'} | Year: ${plan.Year}`, 20, 45);

        let startY = 60;
        let grandTotal = 0;

        const sectionTitles = {
            'livingarrangements': 'Living Arrangements (310–330)',
            'employmentandcommunityparticipation': 'Employment and Community Participation (331–355)', 
            'healthandsafety': 'Health and Safety (356–399)'
        };

        Object.entries(sectionTitles).forEach(([sectionKey, sectionTitle]) => {
            const sectionData = planApiSections[sectionKey] || [];
            
            // Add section title
            doc.setFontSize(14);
            doc.text(sectionTitle, 20, startY);
            startY += 15;

            if (sectionData.length > 0) {
                // Group by service code
                const groups = {};
                sectionData.forEach((r) => {
                    const code = r.serviceCode || r.ServiceCode || 'Unknown';
                    if (!groups[code]) groups[code] = [];
                    groups[code].push(r);
                });

                const body = [];
                let serviceCodeTotals = {}; // Track totals per service code
                let serviceCodeAllocated = {}; // Track allocated per service code
                
                Object.keys(groups).forEach((code) => {
                    let codeTotal = 0;
                    let codeAllocated = 0;
                    
                    groups[code].forEach((r) => {
                        const annualCost = Number(r.frequency || r.Frequency || 0) * Number(r.pricingUnit || r.UnitCost || 0);
                        const allocated = Number(r.allocatedBudget || r.AllocatedBudget || 0);
                        if (sdpEntry) {
                            const vendor = isVendorRow(r);
                            body.push([
                                r.serviceCode,
                                r.provider,
                                isRowEvv(r) ? 'Yes' : 'No',
                                r.description,
                                r.ippGoal,
                                vendor ? '' : r.paymentModel,
                                `$${Number(r.pricingUnit || 0).toLocaleString()}`,
                                r.frequency,
                                `$${annualCost.toLocaleString()}`,
                                `${vendor ? 'Supplier' : 'Worker'}${isRowLinked(r) ? '' : ' (not linked)'}`
                            ]);
                            grandTotal += annualCost;
                            codeTotal += annualCost;
                            return;
                        }
                        body.push([
                            r.serviceCode || r.ServiceCode,
                            r.provider || r.Provider,
                            r.liveIn || r.LiveIn ? 'Yes' : 'No',
                            r.description || r.Description,
                            r.ippGoal || r.IppGoal,
                            r.paymentModel || r.PaymentModel,
                            `$${(r.pricingUnit || r.UnitCost || 0).toLocaleString()}`,
                            r.frequency || r.Frequency,
                            `$${annualCost.toLocaleString()}`,
                            allocated ? `$${allocated.toLocaleString()}` : ''
                        ]);
                        grandTotal += annualCost;
                        codeTotal += annualCost;
                        codeAllocated += allocated;
                    });
                    
                    serviceCodeTotals[code] = codeTotal;
                    serviceCodeAllocated[code] = codeAllocated;

                    if (groups[code].length > 1 || sdpEntry) {
                        body.push([{
                            content: `Subtotal ${code}: $${codeTotal.toLocaleString()}`,
                            colSpan: 10,
                            styles: { halign: "right", fontStyle: "bold", fillColor: [230, 230, 230] }
                        }]);
                    }
                    // SDP has no allocate-down or separately approved per-code budget to compare against
                    if (sdpEntry) return;

                    // Add budget comparison row
                    const budgetKey = `${plan.PlanId}-${sectionKey}-${code}`;
                    const approvedBudget = Number(approvedBudgets[budgetKey] || 0);
                    if (approvedBudget > 0) {
                        const actualDiff = codeTotal - approvedBudget;
                        const allocatedDiff = codeAllocated - approvedBudget;
                        body.push([{
                            content: `Budget for ${code} | Actual: $${codeTotal.toLocaleString()} | Approved: $${approvedBudget.toLocaleString()} | Allocated: $${codeAllocated.toLocaleString()} | Actual Var: ${actualDiff >= 0 ? '+' : ''}$${Math.abs(actualDiff).toLocaleString()} | Alloc Var: ${allocatedDiff >= 0 ? '+' : ''}$${Math.abs(allocatedDiff).toLocaleString()}`,
                            colSpan: 10,
                            styles: { halign: "left", fontStyle: "italic", fillColor: [240, 248, 255], fontSize: 7 }
                        }]);
                    }
                });

                if (body.length > 0) {
                    autoTable(doc, {
                        startY: startY,
                        head: [sdpEntry
                            ? ["Service Code", "Provider", "EVV", "Description", "IPP Goal", "Payment Model", "Rate / Amount", "Hours / Count", "Annual Cost", "Provider Type"]
                            : ["Service Code", "Provider", "Not EVV", "Description", "IPP Goal", "Payment Model", "Unit Cost", "Frequency", "Annual Cost", "Allocated Budget"]],
                        body: body,
                        theme: 'grid',
                        headStyles: { fillColor: [41, 128, 185] },
                        styles: { fontSize: 8 }
                    });
                    startY = doc.lastAutoTable.finalY + 10;
                }
            } else {
                doc.setFontSize(10);
                doc.text("No data available for this section", 20, startY);
                startY += 15;
            }

            startY += 5;
        });

        // Grand total
        doc.setFontSize(14);
        doc.text(`Grand Total: $${grandTotal.toLocaleString()}`, 20, startY);

        doc.save(`spending-plan-${plan.PlanId}.pdf`);
    };


    // Helper to get section label for dropdown
    const getServiceCodeLabel = (sectionKey) => {
        if (sectionKey === "livingarrangements" || sectionKey === "livingArrangements") return "Living arrangements: (310-330)";
        if (sectionKey === "employmentandcommunityparticipation" || sectionKey === "employmentCommunityParticipation") return "Employment and Community Participation (331-355)";
        if (sectionKey === "healthandsafety" || sectionKey === "healthAndSafety") return "Health and Safety (356 - 399)";
        return "";
    };

    // Helper to filter service codes by section
    const getFilteredServiceCodes = (sectionKey) => {
        console.log('Getting filtered service codes for section:', sectionKey);
        console.log('Available service code options:', serviceCodeOptions);
        
        if (!Array.isArray(serviceCodeOptions)) {
            console.log('Service code options is not an array');
            return [];
        }
        
        // Since the API returns ServiceTypeID and Name, use them directly
        console.log('Using service codes with ServiceTypeID and Name format');
        
        // Match the actual section keys from API data (all lowercase)
        if (sectionKey === "livingarrangements" || sectionKey === "livingArrangements") {
            const filtered = serviceCodeOptions.filter(opt => {
                if (!opt.Name) return false;
                const code = parseInt(opt.Name.split(" ")[0]);
                return !isNaN(code) && code >= 310 && code <= 330;
            });
            // Always include "320 - Community Living Supports" if not present
            if (!filtered.some(opt => opt.Name && opt.Name.includes("320"))) {
                filtered.push({
                    ServiceTypeID: "320",
                    Name: "320 - Community Living Supports"
                });
            }
            console.log('Filtered living arrangements:', filtered);
            return filtered;
        }
        
        if (sectionKey === "employmentandcommunityparticipation" || sectionKey === "employmentCommunityParticipation") {
            const filtered = serviceCodeOptions.filter(opt => {
                if (!opt.Name) return false;
                const code = parseInt(opt.Name.split(" ")[0]);
                return !isNaN(code) && code >= 331 && code <= 355;
            });
            console.log('Filtered employment options:', filtered);
            return filtered;
        }
        
        if (sectionKey === "healthandsafety" || sectionKey === "healthAndSafety") {
            const filtered = serviceCodeOptions.filter(opt => {
                if (!opt.Name) return false;
                const code = parseInt(opt.Name.split(" ")[0]);
                return !isNaN(code) && code >= 356 && code <= 399;
            });
            console.log('Filtered health and safety options:', filtered);
            return filtered;
        }
        
        return [];
    };

    return (
        <div className="px-4 pt-3 max-w-full overflow-hidden">
            {/* Toast Notifications */}
            <Toaster 
                position="bottom-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#4ade80',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
            {/* Loading State */}
            {isLoadingData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center ">
                        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Spending Plans</h3>
                        <p className="text-gray-600">
                            {isGuestMode 
                                ? "Setting up your guest workspace..." 
                                : "Fetching your spending plans and data..."
                            }
                        </p>
                        <div className="mt-4 text-sm text-gray-500">
                            Please wait while we prepare your dashboard
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content - only show when not loading */}
            {!isLoadingData && (
                <>

            {/* Tab Content */}
                {activeTab === "previous" && (
                <div>
                    {/* API Data Tables - Lines from previous plans grouped by plan_type */}
                    {renderPlanLinesData('previous') || (
                        <div className="text-center py-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Previous Plans</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">Previous spending plans will appear here once they are archived or completed.</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === "current" && (
                <div>
                    {/* Show loading state while data is being fetched */}
                    {isLoadingData ? (
                        <div className="text-center py-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full mb-4">
                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Current Plans</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">Fetching your active spending plans...</p>
                        </div>
                    ) : (
                        /* API Data Tables with Interactive Functionality */
                        renderPlanLinesData('current') || (
                            <div className="text-center py-16">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-blue-100 rounded-full mb-4">
                                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No Current Plans</h3>
                                <p className="text-gray-500 max-w-sm mx-auto">No active spending plans found for the current date range. Create a new plan to get started.</p>
                            </div>
                        )
                    )}
                </div>
            )}

            {activeTab === "future" && (
                <div>
                    {/* API Data Tables - Lines from future plans grouped by plan_type */}
                    {renderPlanLinesData('future') || (
                        <div className="text-center py-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full mb-4">
                                <svg className="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Future Plans</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">Future spending plans and budget projections will appear here when scheduled.</p>
                        </div>
                    )}
                </div>
            )}
                </>
            )}

            {/* Add Plan Modal */}
            {showAddPlanModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Add New Spending Plan</h3>
                            <button
                                onClick={() => setShowAddPlanModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <form onSubmit={(e) => { e.preventDefault(); handleAddPlan(); }} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                                <input
                                    type="text"
                                    value={newPlanForm.planName}
                                    onChange={(e) => handlePlanFormChange('planName', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                    placeholder="Enter plan name"
                                    required
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    {/* <input
                                        type="date"
                                        value={newPlanForm.startDate || ''}
                                        onChange={(e) => handlePlanFormChange('startDate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                    /> */}
                                    <DatePicker
                                        selected={newPlanForm.startDate ? new Date(newPlanForm.startDate) : null}
                                        onChange={(date) => handlePlanFormChange('startDate', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                        />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    {/* <input
                                        type="date"
                                        value={newPlanForm.endDate || ''}
                                        onChange={(e) => handlePlanFormChange('endDate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                    /> */}
                                     <DatePicker
                                        selected={newPlanForm.endDate ? new Date(newPlanForm.endDate) : null}
                                        onChange={(date) => handlePlanFormChange('endDate', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                        />
                                </div>
                            </div>

                            {/* Certified Budget field hidden for now; it can still be set from the plan header */}
                            {SHOW_CERTIFIED_BUDGET_ON_NEW_PLAN && sdpEntry && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Certified Budget</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={newPlanForm.totalBudget}
                                        onChange={(e) => handlePlanFormChange('totalBudget', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        placeholder="Total individual budget certified by the regional center"
                                    />
                                </div>
                            )}

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAddPlanModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isAddingPlan}
                                    className={`flex-1 px-4 py-2 bg-primary-dark text-white rounded-lg transition-all duration-200 ${
                                        isAddingPlan 
                                            ? 'opacity-50 cursor-not-allowed' 
                                            : 'hover:shadow-lg'
                                    }`}
                                >
                                    {isAddingPlan ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Creating...
                                        </div>
                                    ) : (
                                        'Create Plan'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Authentication Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 h-[80%] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold m-auto text-gray-900 py-3">
                                {authMode === 'login' ? 'Login to Save Plan' : 'Register to Save Plan'}
                            </h3>
                            <button
                                onClick={() => setShowAuthModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <form onSubmit={handleAuth} className="space-y-4">
                            <fieldset disabled={isAuthenticating} aria-busy={isAuthenticating}>
                            {authMode === 'register' && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                            <input
                                                type="text"
                                                value={authForm.firstName}
                                                onChange={(e) => setAuthForm({...authForm, firstName: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                            <input
                                                type="text"
                                                value={authForm.lastName}
                                                onChange={(e) => setAuthForm({...authForm, lastName: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                                required
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                                        {/* <input
                                            type="date"
                                            value={authForm.dateOfBirth || ''}
                                            onChange={(e) => setAuthForm({...authForm, dateOfBirth: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                            required
                                        /> */}
                                        <DatePicker
                                        selected={authForm.dateOfBirth ? new Date(authForm.dateOfBirth) : null}
                                        onChange={(date) => setAuthForm({ ...authForm, dateOfBirth: date })}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        required
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        />
                                    </div>
                                </>
                            )}
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={authForm.email}
                                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                <input
                                    type="password"
                                    value={authForm.password}
                                    onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                    required
                                />
                            </div>
                            
                            {authMode === 'register' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                                    <input
                                        type="password"
                                        value={authForm.confirmPassword}
                                        onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                    />
                                </div>
                            )}
                            {authMode === 'register' && (
                                <div className="pt-2">
                                    {/* <h4 className="text-sm font-medium text-gray-800 mb-2">Contact Details</h4> */}
                                    {/* Contact first/last name fields removed — patient first/last already captured in the top of the form */}

                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                        {/* <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Contact Email</label>
                                            <input
                                                type="email"
                                                value={authForm.contactEmail}
                                                onChange={(e) => setAuthForm({...authForm, contactEmail: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div> */}
                                        {/* <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Mobile Phone</label>
                                            <input
                                                type="tel"
                                                value={authForm.contactMobilePhone}
                                                onChange={(e) => setAuthForm({...authForm, contactMobilePhone: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div> */}
                                    </div>

                                    {/* <div className="mt-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                                        <input
                                            type="text"
                                            value={authForm.contactAddress1}
                                            onChange={(e) => setAuthForm({...authForm, contactAddress1: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                        />
                                    </div> */}

                                    {/* <div className="grid grid-cols-3 gap-4 mt-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                                            <input
                                                type="text"
                                                value={authForm.contactCity}
                                                onChange={(e) => setAuthForm({...authForm, contactCity: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                                            <input
                                                type="text"
                                                value={authForm.contactState}
                                                onChange={(e) => setAuthForm({...authForm, contactState: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Postal Code</label>
                                            <input
                                                type="text"
                                                value={authForm.contactPostalCode}
                                                onChange={(e) => setAuthForm({...authForm, contactPostalCode: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div>
                                    </div> */}

                                    {/* <div className="mt-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Relationship (ID)</label>
                                        <input
                                            type="number"
                                            value={authForm.contactRelationshipId}
                                            onChange={(e) => setAuthForm({...authForm, contactRelationshipId: Number(e.target.value)})}
                                            className="w-32 border border-gray-300 rounded-lg px-2 py-1"
                                        />
                                    </div> */}
                                </div>
                            )}
                            
                            <div className="flex gap-3 justify-center items-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAuthModal(false)}
                                    className={`px-6 py-2 border border-gray-300 text-gray-700 rounded-lg ${(isAuthenticating || !isBusinessLoginComplete) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                    disabled={isAuthenticating || !isBusinessLoginComplete}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isAuthenticating || !isBusinessLoginComplete}
                                    className={`px-6 py-2 bg-primary-dark text-white rounded-lg transition-all duration-200 ${(isAuthenticating || !isBusinessLoginComplete) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}`}
                                >
                                    {!isBusinessLoginComplete ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Initializing...
                                        </div>
                                    ) : isAuthenticating ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            {authMode === 'login' ? 'Logging in...' : 'Registering...'}
                                        </div>
                                    ) : (
                                        (authMode === 'login' ? 'Login' : 'Register')
                                    )}
                                </button>
                            </div>
                            </fieldset>
                            
                            <div className="text-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                                    className="text-sm text-[#2A3F54] hover:underline"
                                >
                                    {authMode === 'login' 
                                        ? "Don't have an account? Register" 
                                        : "Already have an account? Login"
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Service Type Name Modal */}
            {showServiceTypeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-[350px] mx-4 shadow-xl">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                            {serviceTypeForm.operation === 'edit' ? 'Edit Service Type Name' : 'Add/Duplicate Custom Service Type Name'}
                        </h3>
                        <div className="space-y-4">
                            {serviceTypeForm.operation === 'edit' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Original Service Description
                                    </label>
                                    <input
                                        type="text"
                                        value={serviceTypeForm.originalName}
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {serviceTypeForm.operation === 'edit' ? 'Updated Service Type Name' : 'Service Type Name'}
                                </label>
                                <input
                                    type="text"
                                    value={serviceTypeForm.newName}
                                    onChange={(e) => setServiceTypeForm(prev => ({ ...prev, newName: e.target.value }))}
                                    placeholder={serviceTypeForm.operation === 'edit' ? 'Enter updated service type name' : 'Enter service type name'}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowServiceTypeModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                disabled={isSavingServiceType}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveServiceType}
                                disabled={isSavingServiceType || !serviceTypeForm.newName.trim()}
                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition-all duration-200 flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {isSavingServiceType ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {serviceTypeForm.operation === 'edit' ? 'Update' : 'Add'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`bg-white rounded-lg p-6 mx-4 shadow-xl ${confirmModalData.content ? 'max-w-lg w-full' : 'max-w-md'}`}>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">{confirmModalData.title}</h3>
                        {/* content: formatted JSX (e.g. the Activate preview); message: plain text */}
                        {confirmModalData.content
                            ? <div className="mb-6 max-h-[60vh] overflow-y-auto">{confirmModalData.content}</div>
                            : <p className="text-gray-600 mb-6 whitespace-pre-line">{confirmModalData.message}</p>}
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {confirmModalData.cancelText}
                            </button>
                            <button
                                onClick={confirmModalData.onConfirm}
                                className={`flex-1 text-white px-4 py-2 rounded-lg shadow transition-all duration-200 flex items-center justify-center gap-2 ${confirmModalData.variant === 'primary' ? 'bg-green-600 hover:bg-green-700' : 'bg-danger-dark hover:from-red-700 hover:to-red-800'}`}
                            >
                                {confirmModalData.variant === 'primary' ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                )}
                                {confirmModalData.confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


















